import { calculateDraggedImagePosition, ResponsivePage } from "./page-render";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import React from "react";
import { ProductionPanel } from "./production";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ChangeEvent,
  type Dispatch,
} from "react";

import {
  ComicApiError,
  previewGeneration,
  refreshAsset,
  downloadAsset,
  uploadPanelRevision,
  type GenerationPreview,
  analyzePanel,
  applyStoryboard,
  archiveCharacter,
  changePageLayout,
  createCharacter,
  createPanel,
  createStoryboard,
  deleteAsset,
  deletePanel,
  deleteScene,
  exportPage,
  exportProject,
  generatePanel,
  generateCharacterSheet,
  loadAssets,
  loadBootstrap,
  loadComicPage,
  loadPanelCandidates,
  retryJob,
  saveEditorState,
  savePanel,
  selectPanelAsset,
  setCharacterReference,
  updateCharacter,
  updateAssetFavorite,
  updateProjectData,
  waitForJob,
  loadStoryboardDraft,
  saveStoryboardDraft,
  changeProduction,
  loadProduction,
} from "./api";
import {
  editorReducer,
  panelWithBubbleText,
  syncPanelBubbles,
  placePanelDialogue,
  initialEditorModel,
  newBubble,
  updateBubble,
  updatePanelPlacement,
  updatePage,
  type EditorAction,
  type EditorModel,
} from "./reducer";
import type {
  Asset,
  Bubble,
  Character,
  ComicJob,
  ComicJobSummary,
  ComicPage,
  EditorState,
  Panel,
  PanelPlacement,
  PanelAnalysisResult,
  PanelContent,
  ProjectData,
  Scene,
  Selection,
  StoryboardResult,
  StoryboardGenerationMetadata,
  StoryboardProgress,
  StudioBootstrap,
  LayoutOption,
  ProductionRun,
  StoryboardDraft,
} from "./types";

const OUTPUT_LANGUAGE_OPTIONS = [
  ["ja", "日本語"],
  ["en", "英語"],
  ["de", "ドイツ語"],
  ["fr", "フランス語"],
  ["ko", "韓国語"],
  ["zh-Hant", "中国語（繁体字）"],
  ["it", "イタリア語"],
  ["es", "スペイン語"],
  ["pt-BR", "ポルトガル語（ブラジル）"],
] as const;

export const STORYBOARD_PAGE_PRESETS = [
  { pages: 1, label: "1ページ", description: "一つの場面" },
  { pages: 4, label: "4ページ", description: "短編 / 四コマ風" },
  { pages: 8, label: "8ページ", description: "完結する短編（おすすめ）", recommended: true },
  { pages: 16, label: "16ページ", description: "長めの章" },
] as const;

const CONTINUITY_KEEP_OPTIONS = [
  ["identity", "キャラクター"],
  ["face", "顔"],
  ["hair", "髪型"],
  ["outfit", "衣装"],
  ["palette", "配色"],
  ["style", "画風"],
  ["scene", "シーン"],
  ["position", "位置"],
  ["action", "動作"],
  ["pose", "姿勢"],
  ["camera", "カメラ"],
  ["composition", "構図"],
  ["time", "時間"],
] as const;

const CONTINUITY_CHANGE_OPTIONS = [
  ["expression", "表情"],
  ["small_action", "小さな動作"],
  ["action", "動作"],
  ["pose", "姿勢"],
  ["camera", "カメラ"],
  ["camera_distance", "カメラ距離"],
  ["outfit", "衣装"],
  ["palette", "配色"],
  ["accessories", "アクセサリー"],
  ["scene", "シーン"],
  ["background", "背景"],
  ["light", "光"],
  ["composition", "構図"],
  ["position", "位置"],
  ["time", "時間"],
] as const;

const ANALYSIS_CATEGORY_LABELS: Record<
  PanelAnalysisResult["issues"][number]["category"],
  string
> = {
  character_identity: "キャラクターの同一性",
  character_count: "人数",
  outfit: "衣装",
  expression: "表情",
  action: "動作",
  pose: "ポーズ",
  scene: "シーン",
  camera: "カメラ",
  composition: "構図",
  continuity: "連続性",
  readable_text: "画像内の文字",
};

const ASSET_TYPE_LABELS: Record<Asset["asset_type"], string> = {
  upload: "アップロード画像",
  character_reference: "キャラクター参考",
  scene_reference: "シーン参考",
  panel_candidate: "コマ候補",
  page_export: "ページ書き出し",
  project_export: "作品書き出し",
};

function assetTypeLabel(value: string): string {
  return ASSET_TYPE_LABELS[value as Asset["asset_type"]] ?? "素材";
}

function outputLanguageLabel(value: string): string {
  return OUTPUT_LANGUAGE_OPTIONS.find(([code]) => code === value)?.[1] ?? value;
}

function readBootstrap(): StudioBootstrap | null {
  const element = document.getElementById("comic-studio-bootstrap");
  if (!element?.textContent) {
    return null;
  }
  try {
    return JSON.parse(element.textContent) as StudioBootstrap;
  } catch {
    return null;
  }
}

function notifySaveState(state: string): void {
  window.dispatchEvent(new CustomEvent("comic:save-state-changed", { detail: { state } }));
}

export function jobContextLabel(studio: StudioBootstrap, job: ComicJob): string {
  const chapter = studio.chapters.find((item) => item.id === job.resource.chapter_id);
  const page = studio.chapters.flatMap((item) => item.pages)
    .find((item) => item.id === job.resource.page_id);
  const activePage = studio.active_page;
  const panel = activePage && job.resource.page_id === activePage.id
    ? activePage.panels.find((item) => item.id === job.resource.panel_id)
    : undefined;
  const parts: string[] = [];
  if (chapter) parts.push(`第${chapter.order}章「${chapter.title}」`);
  else if (job.resource.chapter_id) parts.push("削除済みの章");
  if (page) parts.push(`ページ${page.order}`);
  else if (job.resource.page_id) parts.push("削除済みのページ");
  if (panel) parts.push(`コマ${panel.order}`);
  else if (job.resource.panel_id) {
    const displayPanel = typeof job.display.panel_label === "string"
      ? job.display.panel_label.trim()
      : "";
    parts.push(displayPanel || "対象のコマ");
  }
  if (!parts.length) parts.push("作品全体");
  const createdAt = new Date(job.created_at);
  if (!Number.isNaN(createdAt.valueOf())) {
    parts.push(new Intl.DateTimeFormat("ja-JP", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(createdAt));
  }
  return parts.join(" / ");
}

function jobSummary(job: ComicJob, studio: StudioBootstrap): ComicJobSummary {
  return {
    id: job.id,
    job_type: job.job_type,
    status: job.status,
    phase: job.phase,
    title: typeof job.display.title === "string" ? job.display.title : "漫画タスク",
    context_label: jobContextLabel(studio, job),
    estimated_credits: job.estimated_credits,
    actual_cost_credits: job.actual_cost_credits,
    error_message: job.error_message,
    progress: job.progress,
    created_at: job.created_at,
  };
}

function notifyJobStatus(job: ComicJob, studio: StudioBootstrap): void {
  window.dispatchEvent(
    new CustomEvent("comic:job-status-changed", { detail: { job: jobSummary(job, studio) } }),
  );
}

function waitForTrackedJob(
  studio: StudioBootstrap,
  jobId: string,
  signal?: AbortSignal,
): Promise<ComicJob> {
  return waitForJob(studio, jobId, {
    signal,
    onUpdate: (job) => notifyJobStatus(job, studio),
  });
}

function usePanelSaveQueue(
  studio: StudioBootstrap,
  dispatch: Dispatch<EditorAction>,
): {
  queue: (panel: Panel) => void;
  flush: (panelId?: string) => Promise<void>;
  isPending: () => boolean;
} {
  const studioRef = useRef(studio);
  const pending = useRef(new Map<string, Panel>());
  const inFlight = useRef(new Map<string, Promise<void>>());
  const timers = useRef(new Map<string, number>());
  const drainRef = useRef<(panelId: string) => Promise<void>>(async () => undefined);
  studioRef.current = studio;

  const drain = useCallback(async (panelId: string): Promise<void> => {
    const active = inFlight.current.get(panelId);
    if (active) {
      await active;
      if (pending.current.has(panelId)) {
        await drainRef.current(panelId);
      }
      return;
    }
    const panel = pending.current.get(panelId);
    if (!panel) {
      return;
    }
    pending.current.delete(panelId);
    let saveFailed = false;
    const work = (async () => {
      let lastError: unknown = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const saved = await savePanel(studioRef.current, panel);
          if (!pending.current.has(panelId)) {
            dispatch({ type: "panel-content-saved", panel: saved });
          }
          return;
        } catch (error) {
          lastError = error;
          if (attempt < 2) {
            await new Promise((resolve) => window.setTimeout(resolve, 500 * (2 ** attempt)));
          }
        }
      }
      dispatch({
        type: "save-failure",
        conflict: false,
        message:
          lastError instanceof Error
            ? `コマを保存できませんでした: ${lastError.message}`
            : "コマを保存できませんでした。",
      });
      saveFailed = true;
      if (!pending.current.has(panelId)) {
        pending.current.set(panelId, panel);
      }
    })();
    inFlight.current.set(panelId, work);
    try {
      await work;
    } finally {
      inFlight.current.delete(panelId);
      const nextPanel = pending.current.get(panelId);
      if (nextPanel && (!saveFailed || nextPanel !== panel)) {
        await drainRef.current(panelId);
      }
    }
  }, [dispatch]);
  drainRef.current = drain;

  const schedule = useCallback((panelId: string, delay: number) => {
    const current = timers.current.get(panelId);
    if (current !== undefined) {
      window.clearTimeout(current);
    }
    const timer = window.setTimeout(() => {
      timers.current.delete(panelId);
      void drainRef.current(panelId);
    }, delay);
    timers.current.set(panelId, timer);
  }, []);

  const queue = useCallback((panel: Panel) => {
    dispatch({ type: "replace-panel", panel });
    pending.current.set(panel.id, panel);
    schedule(panel.id, 500);
  }, [dispatch, schedule]);

  const flush = useCallback(async (panelId?: string) => {
    const ids = panelId
      ? [panelId]
      : [...new Set([...pending.current.keys(), ...inFlight.current.keys()])];
    for (const id of ids) {
      const timer = timers.current.get(id);
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timers.current.delete(id);
      }
    }
    await Promise.all(ids.map((id) => drainRef.current(id)));
    if (ids.some((id) => pending.current.has(id))) {
      throw new ComicApiError("コマの保存に失敗しました。編集内容は保持されています。保存後にもう一度お試しください。");
    }
  }, []);

  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (pending.current.size > 0 || inFlight.current.size > 0) {
        event.preventDefault();
      }
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", warnBeforeUnload);
      for (const timer of timers.current.values()) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  return { queue, flush, isPending: () => pending.current.size > 0 || inFlight.current.size > 0 };
}

function selectedPanel(model: EditorModel): Panel | null {
  if (model.selection.kind !== "panel") {
    return null;
  }
  const selectedId = model.selection.id;
  const panel = model.page.panels.find((panel) => panel.id === selectedId);
  return panel ? panelWithBubbleText(panel, model.state) : null;
}

function selectedBubble(model: EditorModel): Bubble | null {
  if (model.selection.kind !== "bubble") {
    return null;
  }
  const selectedId = model.selection.id;
  return model.state.bubbles.find((bubble) => bubble.id === selectedId) ?? null;
}

function startBubbleGesture(
  event: React.PointerEvent<HTMLElement>,
  bubble: Bubble,
  mode: "drag" | "resize",
  commit: (state: EditorState) => void,
  state: EditorState,
): void {
  if (event.button !== 0) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  const handle = event.currentTarget;
  const bubbleElement = handle.closest<HTMLElement>(".comic-canvas-bubble");
  const surface = handle.closest<HTMLElement>(".comic-page-canvas__surface");
  if (!bubbleElement || !surface) {
    return;
  }
  const bounds = surface.getBoundingClientRect();
  const startX = event.clientX;
  const startY = event.clientY;
  let nextX = bubble.x;
  let nextY = bubble.y;
  let nextWidth = bubble.width;
  let nextHeight = bubble.height;

  const move = (pointerEvent: PointerEvent) => {
    const dx = (pointerEvent.clientX - startX) / bounds.width;
    const dy = (pointerEvent.clientY - startY) / bounds.height;
    if (mode === "drag") {
      nextX = Math.max(0, Math.min(1 - bubble.width, bubble.x + dx));
      nextY = Math.max(0, Math.min(1 - bubble.height, bubble.y + dy));
      bubbleElement.style.left = `${nextX * 100}%`;
      bubbleElement.style.top = `${nextY * 100}%`;
      return;
    }
    nextWidth = Math.max(0.05, Math.min(1 - bubble.x, bubble.width + dx));
    nextHeight = Math.max(0.05, Math.min(1 - bubble.y, bubble.height + dy));
    bubbleElement.style.width = `${nextWidth * 100}%`;
    bubbleElement.style.height = `${nextHeight * 100}%`;
  };
  const finish = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", finish);
    window.removeEventListener("pointercancel", finish);
    commit(
      updateBubble(state, bubble.id, {
        x: nextX,
        y: nextY,
        width: nextWidth,
        height: nextHeight,
      }),
    );
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", finish, { once: true });
  window.addEventListener("pointercancel", finish, { once: true });
}

function startPanelImageGesture(
  event: React.PointerEvent<HTMLElement>,
  placement: PanelPlacement,
  commit: (state: EditorState) => void,
  state: EditorState,
): void {
  if (event.button !== 0) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  const panelElement = event.currentTarget;
  const imageElement = panelElement.querySelector<HTMLImageElement>("img");
  if (!imageElement || !imageElement.naturalWidth || !imageElement.naturalHeight) {
    return;
  }
  const bounds = imageElement.getBoundingClientRect();
  const startX = event.clientX;
  const startY = event.clientY;
  let nextPosition = {
    image_position_x: placement.image_position_x,
    image_position_y: placement.image_position_y,
  };
  let moved = false;

  const move = (pointerEvent: PointerEvent) => {
    const deltaX = pointerEvent.clientX - startX;
    const deltaY = pointerEvent.clientY - startY;
    moved ||= Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2;
    nextPosition = calculateDraggedImagePosition({
      placement,
      naturalWidth: imageElement.naturalWidth,
      naturalHeight: imageElement.naturalHeight,
      frameWidth: bounds.width,
      frameHeight: bounds.height,
      deltaX,
      deltaY,
    });
    imageElement.style.objectPosition = `${nextPosition.image_position_x * 100}% ${nextPosition.image_position_y * 100}%`;
    panelElement.dataset.imagePositionX = String(nextPosition.image_position_x);
    panelElement.dataset.imagePositionY = String(nextPosition.image_position_y);
  };
  const cleanup = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", finish);
    window.removeEventListener("pointercancel", cancel);
  };
  const finish = () => {
    cleanup();
    if (moved) {
      commit(updatePanelPlacement(state, placement.panel_id, nextPosition));
    }
  };
  const cancel = () => {
    cleanup();
    imageElement.style.objectPosition = `${placement.image_position_x * 100}% ${placement.image_position_y * 100}%`;
    panelElement.dataset.imagePositionX = String(placement.image_position_x);
    panelElement.dataset.imagePositionY = String(placement.image_position_y);
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", finish, { once: true });
  window.addEventListener("pointercancel", cancel, { once: true });
}

function elapsedLabel(createdAt: string, now: number): string {
  const created = Date.parse(createdAt);
  const elapsedSeconds = Number.isFinite(created)
    ? Math.max(0, Math.floor((now - created) / 1000))
    : 0;
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function storyboardProgressCopy(progress?: StoryboardProgress): string {
  if (!progress || (progress.stage !== "planning" && progress.stage !== "generating")) {
    return "";
  }
  const message = typeof progress.message === "string" ? progress.message.trim() : "";
  const stage = progress.stage === "planning" ? "全体のページ構成を計画中" : "下書きの詳細を生成中";
  const hasPageCount = Number.isInteger(progress.completed_pages)
    && Number.isInteger(progress.requested_pages)
    && progress.requested_pages > 0
    && progress.completed_pages >= 0;
  const pageCount = hasPageCount
    ? `${progress.completed_pages}/${progress.requested_pages}ページ`
    : "";
  return [message || stage, pageCount].filter(Boolean).join(" · ");
}

export function generationStatusCopy(job: Pick<ComicJobSummary, "job_type" | "phase" | "progress">): string {
  if (job.phase === "queued") {
    return "順番を待っています";
  }
  if (job.phase !== "generating") {
    return "";
  }
  if (job.job_type === "comic_storyboard_generate") {
    const details = storyboardProgressCopy(job.progress);
    return details ? `下書きを作っています · ${details}` : "下書きを作っています";
  }
  if (job.job_type === "comic_panel_generate" || job.job_type === "comic_panel_edit") {
    return "画像を作っています";
  }
  if (job.job_type === "comic_panel_analyze") {
    return "画像を確認しています";
  }
  if (job.job_type === "comic_character_generate") {
    return "人物の見本画像を作っています";
  }
  if (job.job_type === "comic_page_export" || job.job_type === "comic_project_export") {
    return "保存用ファイルを作っています";
  }
  return "漫画の処理をしています";
}

export function GenerationStatus({
  jobs,
  now,
}: {
  jobs: ComicJobSummary[];
  now: number;
}) {
  if (!jobs.length) {
    return null;
  }
  return (
    <section className="comic-generation-status" aria-live="polite" aria-label="生成タスクの進行状況">
      {jobs.slice(0, 3).map((job) => {
        const elapsed = Number.isFinite(Date.parse(job.created_at))
          ? Math.max(0, now - Date.parse(job.created_at))
          : 0;
        const statusCopy = generationStatusCopy(job)
          || (job.status === "failed" ? "処理に失敗しました" : job.status === "success" ? "完了しました" : "処理中です");
        return (
          <article key={job.id} className="comic-generation-status__job">
            <span className="comic-generation-status__spinner" aria-hidden="true" />
            <div>
              <strong>{job.title.replace(/ネーム/g, "下書き")}</strong>
              <span>
                {statusCopy}
                {" · "}
                経過 {elapsedLabel(job.created_at, now)}
              </span>
              <small>
                {elapsed >= 120_000
                  ? "時間がかかっています。進み具合は「5 タスク」で確認できます。"
                  : "編集を続けても、画面を閉じても処理は続きます。"}
              </small>
            </div>
          </article>
        );
      })}
      {jobs.length > 3 && (
        <p className="comic-generation-status__more">ほか {jobs.length - 3} 件を処理中です。</p>
      )}
      <span className="comic-generation-status__progress" aria-hidden="true" />
    </section>
  );
}

function returnToEditorSource(): void {
  window.setTimeout(() => {
    const editorRoot = document.getElementById("comic-editor-root");
    const target = editorRoot && editorRoot.getClientRects().length > 0
      ? editorRoot
      : document.getElementById("tool-section");
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    const focusTarget = target
      ? Array.from(target.querySelectorAll<HTMLElement>(
          "button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])",
        )).find((element) => element.getClientRects().length > 0)
      : null;
    if (focusTarget && focusTarget.getClientRects().length > 0) {
      focusTarget.focus({ preventScroll: true });
    }
  }, 0);
}

function PageCanvas({
  model,
  dispatch,
  commit,
}: {
  model: EditorModel;
  dispatch: React.Dispatch<Parameters<typeof editorReducer>[1]>;
  commit: (state: EditorState) => void;
}) {
  const [zoom, setZoom] = useState(92);
  const layoutLabel = model.studio.layout_catalog.find(
    (layout) => layout.key === model.page.layout_key,
  )?.label ?? "ページレイアウト";
  return (
    <section className={`comic-react-canvas${model.preview ? " is-preview" : ""}`}>
      <header className="comic-react-canvas__toolbar">
        <div>
          <p>PAGE {model.page.order}</p>
          <strong>{layoutLabel}</strong>
        </div>
        <div className="comic-react-canvas__history">
          <label className="comic-canvas-zoom">
            <span>{zoom}%</span>
            <input
              type="range"
              min="45"
              max="120"
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
            />
          </label>
          <button
            type="button"
            onClick={() => dispatch({ type: "undo" })}
            disabled={!model.past.length || model.preview}
          >
            ↶ 元に戻す
          </button>
          <button
            type="button"
            onClick={() => dispatch({ type: "redo" })}
            disabled={!model.future.length || model.preview}
          >
            ↷ やり直す
          </button>
          {model.preview && (
            <button type="button" onClick={() => dispatch({ type: "preview", value: false })}>
              編集へ戻る
            </button>
          )}
        </div>
      </header>
      <div className="comic-page-canvas" style={{ width: `${zoom}%` }}>
        <ResponsivePage state={model.state} panels={model.page.panels}
          selection={model.preview ? undefined : model.selection} showGuides={!model.preview}
          onSelect={model.preview ? undefined : (selection) => dispatch({type:"select", selection})}
          onPanelImageGesture={model.preview ? undefined : (event, placement) => startPanelImageGesture(event, placement, commit, model.state)}
          onGesture={model.preview ? undefined : (event, bubble, mode) => startBubbleGesture(event, bubble, mode, commit, model.state)} />
      </div>
    </section>
  );
}

function ContinuousPreview({
  pages,
  onClose,
}: {
  pages: ComicPage[];
  onClose: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const heading = headingRef.current;
    const result = heading?.closest<HTMLElement>(".comic-workspace-result");
    if (!heading || !result) {
      return;
    }
    if (typeof result.scrollIntoView === "function") {
      result.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    const focusTimer = window.setTimeout(() => heading.focus({ preventScroll: true }), 0);
    return () => window.clearTimeout(focusTimer);
  }, []);
  return (
    <section
      id="comic-continuous-preview-result"
      className="comic-workspace-result comic-continuous-preview"
      aria-labelledby="comic-continuous-preview-title"
      tabIndex={-1}
    >
        <header className="comic-result-header">
          <div><p className="comic-kicker">全ページの確認</p><h2 id="comic-continuous-preview-title" ref={headingRef} tabIndex={-1}>作品全体プレビュー</h2></div>
          <div className="comic-result-header__actions">
            <button type="button" className="comic-button comic-button--quiet" onClick={onClose}>編集に戻る</button>
            <button type="button" onClick={onClose} aria-label="閉じる">×</button>
          </div>
        </header>
        <nav id="comic-continuous-page-nav" className="comic-result-page-nav" aria-label="ページへ移動">
          <span>ページへ移動</span>
          <div>
            {pages.map((page, pageIndex) => (
              <a key={page.id} href={`#comic-continuous-page-${pageIndex + 1}`}>{pageIndex + 1}ページ</a>
            ))}
          </div>
        </nav>
        <p className="comic-inspector-hint">
          全{pages.length}ページを読む順番に表示しています。「編集に戻る」で作業を再開できます。
        </p>
        <div>
          {pages.map((page, pageIndex) => (
            <article key={page.id} id={`comic-continuous-page-${pageIndex + 1}`}>
              <p>{pageIndex + 1}ページ / 全{pages.length}ページ</p>
              <ResponsivePage state={page.editor_state} panels={page.panels} />
              <a className="comic-result-page-backlink" href="#comic-continuous-page-nav">ページ一覧に戻る</a>
            </article>
          ))}
        </div>
        <footer className="comic-result-footer">
          <button type="button" className="comic-button comic-button--quiet" onClick={onClose}>編集に戻る</button>
        </footer>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  textarea = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  textarea?: boolean;
}) {
  return (
    <label className="comic-inspector-field">
      <span>{label}</span>
      {textarea ? (
        <textarea aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} />
      )}
    </label>
  );
}

function storyboardFromOutput(output: Record<string, unknown>): StoryboardResult | null {
  const value = output.storyboard;
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Partial<StoryboardResult>;
  if (
    typeof candidate.title !== "string" ||
    typeof candidate.chapter_title !== "string" ||
    !Array.isArray(candidate.pages)
  ) {
    return null;
  }
  const result = structuredClone(candidate) as StoryboardResult;
  const valid = result.pages.every((page) => Array.isArray(page.panels) && page.panels.every((panel) =>
    Array.isArray(panel.character_names)
    && Array.isArray(panel.character_actions)
    && Array.isArray(panel.dialogue_speakers)
    && Array.isArray(panel.dialogue)
    && panel.dialogue_speakers.length === panel.dialogue.length
    && Array.isArray(panel.narration)
    && Array.isArray(panel.sound_effects),
  ));
  return valid ? result : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value);
}

export function storyboardGenerationFromOutput(
  output: Record<string, unknown>,
): StoryboardGenerationMetadata | null {
  const value = output.storyboard_generation;
  if (!isRecord(value)) {
    return null;
  }
  const requestedPages = value.requested_pages;
  const completedPages = value.completed_pages;
  const newPages = value.new_pages;
  const partial = value.partial;
  const continuationOf = value.continuation_of;
  if (
    !isInteger(requestedPages)
    || requestedPages < 1
    || requestedPages > 30
    || !isInteger(completedPages)
    || completedPages < 1
    || completedPages > requestedPages
    || typeof partial !== "boolean"
    || partial !== (completedPages < requestedPages)
    || !isInteger(newPages)
    || newPages < 1
    || newPages > completedPages
    || typeof continuationOf !== "string"
  ) {
    return null;
  }
  return {
    requested_pages: requestedPages,
    completed_pages: completedPages,
    partial,
    new_pages: newPages,
    continuation_of: continuationOf,
  };
}

export function storyboardReviewFromJob(job: ComicJob): {
  value: StoryboardResult;
  metadata: StoryboardGenerationMetadata | null;
  applied?: boolean;
} | null {
  const value = storyboardFromOutput(job.output);
  if (!value) {
    return null;
  }
  const metadata = storyboardGenerationFromOutput(job.output);
  const appliedChapterId = typeof job.output.applied_chapter_id === "string"
    ? job.output.applied_chapter_id.trim()
    : "";
  return {
    value,
    metadata: metadata && metadata.completed_pages === value.pages.length ? metadata : null,
    ...(appliedChapterId ? { applied: true } : {}),
  };
}

function panelAnalysisFromOutput(
  output: Record<string, unknown>,
): PanelAnalysisResult | null {
  const value = output.analysis;
  if (!value || typeof value !== "object") {
    return null;
  }
  const analysis = value as Partial<PanelAnalysisResult>;
  if (
    typeof analysis.passed !== "boolean"
    || typeof analysis.summary !== "string"
    || !Array.isArray(analysis.issues)
    || typeof analysis.correction_prompt !== "string"
  ) {
    return null;
  }
  return analysis as PanelAnalysisResult;
}

interface ReopenedPanelAnalysis {
  jobId: string;
  panelId: string;
  assetId: string;
  result: PanelAnalysisResult;
}

interface CandidateComparisonState {
  comparison: {
    baseUrl: string;
    baseLabel: string;
    candidate: Asset;
  } | null;
  pendingBase: Asset | null;
}

interface PanelGenerationFeedback {
  panelId: string;
  status: "success" | "error";
  message: string;
}

type CharacterGenerationPhase = "idle" | "saving" | "generating" | "success" | "error";
type StoryboardAction = "apply" | "continue" | null;

export function panelAiCreditCopy(operationCredits: Record<string, number>): {
  analysisDescription: string;
  correctionButton: string;
  correctionConfirmation: string;
} {
  const analysisCredits = operationCredits.panel_analyze;
  const correctionCredits = operationCredits.apply_ai_correction;
  return {
    analysisDescription: `完成画像と設定のずれを確認します。1回 ${analysisCredits} クレジットです。`,
    correctionButton: `修正候補を生成（${correctionCredits} クレジット）`,
    correctionConfirmation:
      `修正候補を1枚生成し、${correctionCredits}クレジットを使用します。続行しますか？`,
  };
}

export function panelGenerationCreditsPerImage(
  studio: StudioBootstrap,
  page: ComicPage,
  panel: Panel,
  explicitSourceAssetId: string,
  availableAssets: Asset[],
): number {
  const sourceIds = new Set<string>();
  const characterReferenceIds = new Set<string>();
  const assetById = new Map(availableAssets.map((asset) => [asset.id, asset]));
  const addSource = (assetId: string, isCharacterReference = false) => {
    if (!assetId) {
      return;
    }
    sourceIds.add(assetId);
    if (isCharacterReference) {
      characterReferenceIds.add(assetId);
    }
  };

  if (explicitSourceAssetId) {
    const explicitSource = assetById.get(explicitSourceAssetId);
    addSource(
      explicitSourceAssetId,
      explicitSource?.asset_type === "character_reference",
    );
  } else if (panel.current_asset_id) {
    const currentSource = assetById.get(panel.current_asset_id);
    addSource(
      panel.current_asset_id,
      currentSource?.asset_type === "character_reference",
    );
  }

  panel.content.characters.forEach((panelCharacter) => {
    const character = studio.characters.find(
      (item) => item.id === panelCharacter.character_id,
    );
    if (!character) {
      return;
    }
    character.profile.additional_reference_asset_ids.forEach((assetId) => addSource(assetId, true));
    addSource(character.profile.primary_reference_asset_id, true);
    const outfit = character.profile.outfits.find(
      (item) => item.id === panelCharacter.outfit_id,
    );
    outfit?.reference_asset_ids.forEach((assetId) => addSource(assetId));
  });

  const scene = studio.project_data?.scene_library.find(
    (item) => item.id === panel.content.scene_id,
  );
  scene?.reference_asset_ids.forEach((assetId) => addSource(assetId));

  const previousPanel = page.panels.find(
    (item) => item.id === panel.content.continuity.previous_panel_id,
  );
  if (previousPanel?.current_asset_id) {
    addSource(previousPanel.current_asset_id);
  }

  const isSimpleGeneration =
    sourceIds.size === 0
    || (
      sourceIds.size === 1
      && characterReferenceIds.has([...sourceIds][0])
    );
  return isSimpleGeneration
    ? studio.operation_credits.generate
    : studio.operation_credits.generate_complex;
}

export function resolveCandidateComparison(
  currentAssetUrl: string,
  currentAssetId: string | null,
  selected: Asset,
  pendingBase: Asset | null,
): CandidateComparisonState {
  if (currentAssetUrl && currentAssetId !== selected.id) {
    return {
      comparison: {
        baseUrl: currentAssetUrl,
        baseLabel: "現在使用中",
        candidate: selected,
      },
      pendingBase: null,
    };
  }
  if (!pendingBase || pendingBase.id === selected.id) {
    return { comparison: null, pendingBase: selected };
  }
  return {
    comparison: {
      baseUrl: pendingBase.download_url,
      baseLabel: `比較候補 · ${pendingBase.id.slice(0, 8)}`,
      candidate: selected,
    },
    pendingBase: null,
  };
}

function emptyStoryboardPanel(): StoryboardResult["pages"][number]["panels"][number] {
  return {
    story_goal: "",
    character_names: [],
    character_actions: [],
    dialogue_speakers: [],
    scene_name: "",
    time: "",
    camera: "",
    composition: "",
    tone: "",
    dialogue: [],
    narration: [],
    sound_effects: [],
    continuity_preset: "dialogue_continuation",
  };
}

export function StoryboardReview({
  value,
  metadata,
  layouts,
  busy,
  action = null,
  errorMessage = "",
  applied = false,
  production = false,
  characterLabels = {},
  onChange,
  onContinue,
  onApply,
  onClose,
}: {
  value: StoryboardResult;
  metadata: StoryboardGenerationMetadata | null;
  layouts: LayoutOption[];
  busy: boolean;
  action?: "apply" | "continue" | null;
  errorMessage?: string;
  applied?: boolean;
  production?: boolean;
  characterLabels?: Record<string, string>;
  onChange: (value: StoryboardResult) => void;
  onContinue: (() => void) | null;
  onApply: () => void;
  onClose: () => void;
}) {
  const aliases = Object.keys(characterLabels);
  const characterLabel = (name: string) => {
    const label = characterLabels[name];
    if (!label) return name;
    const sameNames = aliases.filter((alias) => characterLabels[alias] === label);
    return sameNames.length > 1 ? `${label}（人物${sameNames.indexOf(name) + 1}）` : label;
  };
  const characterAlias = (label: string) => aliases.find((alias) => characterLabel(alias) === label) ?? label;
  const updatePanel = (
    pageIndex: number,
    panelIndex: number,
    updates: Partial<StoryboardResult["pages"][number]["panels"][number]>,
  ) => {
    const next = structuredClone(value);
    next.pages[pageIndex].panels[panelIndex] = {
      ...next.pages[pageIndex].panels[panelIndex],
      ...updates,
    };
    const changed = next.pages[pageIndex].panels[panelIndex];
    changed.character_actions = changed.character_names.map((name) =>
      changed.character_actions.find((item) => item.character_name === name)
      ?? { character_name: name, action: "", expression: "", pose: "", position: "" },
    );
    changed.dialogue_speakers = changed.dialogue.map((_, index) => changed.character_names.includes(changed.dialogue_speakers[index] ?? "") ? changed.dialogue_speakers[index] : "");
    onChange(next);
  };
  const updatePageLayout = (pageIndex: number, layoutKey: string) => {
    const layout = layouts.find((item) => item.key === layoutKey);
    if (!layout) {
      return;
    }
    const next = structuredClone(value);
    const removedPanels = next.pages[pageIndex].panels.slice(layout.panel_rects.length);
    if (removedPanels.some((panel) =>
      panel.story_goal.trim() || panel.character_names.length || panel.character_actions.length
      || panel.dialogue_speakers.length || panel.dialogue.length
      || panel.narration.length || panel.sound_effects.length || panel.scene_name.trim()
      || panel.time.trim() || panel.camera.trim() || panel.composition.trim() || panel.tone.trim()
      || panel.continuity_preset !== "dialogue_continuation",
    ) && !window.confirm("コマを減らすと、後ろのコマの内容・人物・セリフが削除されます。続けますか？")) {
      return;
    }
    const panels = next.pages[pageIndex].panels.slice(0, layout.panel_rects.length);
    while (panels.length < layout.panel_rects.length) {
      panels.push(emptyStoryboardPanel());
    }
    next.pages[pageIndex] = { layout_key: layout.key, panels };
    onChange(next);
  };
  const movePage = (pageIndex: number, direction: -1 | 1) => {
    const target = pageIndex + direction;
    if (target < 0 || target >= value.pages.length) {
      return;
    }
    const next = structuredClone(value);
    [next.pages[pageIndex], next.pages[target]] = [next.pages[target], next.pages[pageIndex]];
    onChange(next);
  };
  const movePanel = (pageIndex: number, panelIndex: number, direction: -1 | 1) => {
    const target = panelIndex + direction;
    const next = structuredClone(value);
    const panels = next.pages[pageIndex].panels;
    if (target < 0 || target >= panels.length) {
      return;
    }
    [panels[panelIndex], panels[target]] = [panels[target], panels[panelIndex]];
    onChange(next);
  };
  const changePanelCount = (pageIndex: number, delta: -1 | 1) => {
    const desired = value.pages[pageIndex].panels.length + delta;
    const layout = layouts.find((item) => item.panel_rects.length === desired);
    if (layout) {
      updatePageLayout(pageIndex, layout.key);
    }
  };
  const addPage = () => {
    const layout = layouts.find((item) => item.key === "four_grid") ?? layouts[0];
    if (!layout) {
      return;
    }
    onChange({
      ...value,
      pages: [
        ...value.pages,
        {
          layout_key: layout.key,
          panels: layout.panel_rects.map(() => emptyStoryboardPanel()),
        },
      ],
    });
  };
  const removePage = (pageIndex: number) => {
    if (value.pages.length <= 1) {
      return;
    }
    onChange({ ...value, pages: value.pages.filter((_, index) => index !== pageIndex) });
  };
  const lines = (text: string) =>
    text.split("\n").map((item) => item.trim()).filter(Boolean);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const heading = headingRef.current;
    const result = heading?.closest<HTMLElement>(".comic-workspace-result");
    if (!heading || !result) {
      return;
    }
    if (typeof result.scrollIntoView === "function") {
      result.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    const focusTimer = window.setTimeout(() => heading.focus({ preventScroll: true }), 0);
    return () => window.clearTimeout(focusTimer);
  }, []);
  if (applied) {
    return (
      <section
        id="comic-storyboard-result"
        className="comic-workspace-result comic-storyboard-review"
        aria-labelledby="comic-storyboard-result-title"
        tabIndex={-1}
      >
        <header className="comic-result-header">
          <div>
            <p className="comic-kicker">下書きの確認</p>
            <h2 id="comic-storyboard-result-title" ref={headingRef} tabIndex={-1}>この下書きは作品に追加済みです</h2>
          </div>
          <div className="comic-result-header__actions">
            <button type="button" className="comic-button comic-button--quiet" onClick={onClose}>編集に戻る</button>
          </div>
        </header>
        <p className="comic-inspector-hint">
          内容を直す場合は、編集画面のページ一覧から追加した章を選んでください。
        </p>
      </section>
    );
  }
  return (
    <section
      id="comic-storyboard-result"
      className="comic-workspace-result comic-storyboard-review"
      aria-labelledby="comic-storyboard-result-title"
      tabIndex={-1}
    >
        <header className="comic-result-header">
          <div>
            <p className="comic-kicker">下書きの確認</p>
            <h2 id="comic-storyboard-result-title" ref={headingRef} tabIndex={-1}>コマの内容とセリフを確認してください</h2>
          </div>
          <div className="comic-result-header__actions">
            <button type="button" className="comic-button comic-button--quiet" disabled={busy} onClick={onClose}>編集に戻る</button>
            <button type="button" disabled={busy} onClick={onClose} aria-label="閉じる">×</button>
          </div>
        </header>
        <p className="comic-inspector-hint">
          {production ? "変更は自動保存されます。確認後、画像と文字の制作を続けます。" : "まだ作品には追加されていません。内容を直して「この下書きでページを作る」を押し、追加したページでコマ画像を作ってください。"}
          {production && value.character_briefs?.map((brief) => <span className="comic-inspector-hint" key={brief.name}>{characterLabel(brief.name)}：{brief.profile.appearance.description}（AIの提案）</span>)}
        </p>
        <nav id="comic-storyboard-page-nav" className="comic-result-page-nav" aria-label="ページへ移動">
          <span>ページへ移動</span>
          <div>
            {value.pages.map((_, pageIndex) => (
              <a key={pageIndex} href={`#comic-storyboard-page-${pageIndex + 1}`}>{pageIndex + 1}ページ</a>
            ))}
          </div>
        </nav>
        <label className="comic-inspector-field">
          <span>章タイトル</span>
          <input
            value={value.chapter_title}
            onChange={(event) => onChange({ ...value, chapter_title: event.target.value })}
          />
        </label>
        {metadata?.partial && (
          <aside className="comic-inspector-hint" role="status">
            目標 {metadata.requested_pages}ページ中 {metadata.completed_pages}ページが完成しました。
            完成済みのページはそのまま利用でき、実際に生成されたページ分だけ課金されています。
          </aside>
        )}
        <div className="comic-storyboard-pages">
          {value.pages.map((page, pageIndex) => (
            <article key={`storyboard-page-${pageIndex + 1}`} id={`comic-storyboard-page-${pageIndex + 1}`}>
              <header>
                <strong>ページ {pageIndex + 1}</strong>
                <div className="comic-storyboard-page-actions">
                  <select
                    aria-label={`ページ${pageIndex + 1} レイアウト`}
                    value={page.layout_key}
                    onChange={(event) => updatePageLayout(pageIndex, event.target.value)}
                  >
                    {layouts.map((layout) => (
                      <option key={layout.key} value={layout.key}>{layout.label}</option>
                    ))}
                  </select>
                  <button type="button" disabled={pageIndex === 0} onClick={() => movePage(pageIndex, -1)}>↑</button>
                  <button type="button" disabled={pageIndex === value.pages.length - 1} onClick={() => movePage(pageIndex, 1)}>↓</button>
                  <button type="button" disabled={value.pages.length === 1} onClick={() => removePage(pageIndex)}>削除</button>
                </div>
              </header>
              <div>
                {page.panels.map((panel, panelIndex) => (
                  <section key={`storyboard-panel-${pageIndex + 1}-${panelIndex + 1}`}>
                    <header>
                      <b>コマ {panelIndex + 1}</b>
                      <span>
                        <button type="button" disabled={panelIndex === 0} onClick={() => movePanel(pageIndex, panelIndex, -1)}>←</button>
                        <button type="button" disabled={panelIndex === page.panels.length - 1} onClick={() => movePanel(pageIndex, panelIndex, 1)}>→</button>
                      </span>
                    </header>
                    <label className="comic-inspector-field">
                      <span>このコマの内容</span>
                      <textarea
                        aria-label={`ページ${pageIndex + 1} コマ${panelIndex + 1} 内容`}
                        value={panel.story_goal}
                        onChange={(event) =>
                          updatePanel(pageIndex, panelIndex, { story_goal: event.target.value })
                        }
                      />
                    </label>
                    <label className="comic-inspector-field">
                      <span>人物名</span>
                      <input
                        aria-label="登場人物"
                        value={panel.character_names.map(characterLabel).join("、")}
                        placeholder="登場人物（読点区切り）"
                        onChange={(event) =>
                          updatePanel(pageIndex, panelIndex, {
                            character_names: [
                              ...new Set(
                                event.target.value
                                  .split(/[、,]/)
                                  .map((item) => item.trim())
                                  .map(characterAlias)
                                  .filter(Boolean),
                              ),
                            ],
                          })
                        }
                      />
                    </label>
                    {panel.character_names.map((name) => {
                      const acting = panel.character_actions.find((item) => item.character_name === name)
                        ?? { character_name: name, action: "", expression: "", pose: "", position: "" };
                      return <fieldset key={name}><legend>{characterLabel(name)}の演技</legend>{(["action","expression","pose","position"] as const).map((key) => <Field key={key} label={{action:"動作",expression:"表情",pose:"姿勢",position:"位置"}[key]} value={acting[key]} onChange={(text) => updatePanel(pageIndex,panelIndex,{character_actions:[...panel.character_actions.filter((item)=>item.character_name !== name), {...acting,[key]:text}]})} />)}</fieldset>;
                    })}
                    <label className="comic-inspector-field">
                      <span>場所</span>
                      <input aria-label="シーン" value={panel.scene_name} placeholder="場所・シーン" onChange={(event) => updatePanel(pageIndex, panelIndex, { scene_name: event.target.value })} />
                    </label>
                    <label className="comic-inspector-field">
                      <span>時間</span>
                      <input aria-label="時間" value={panel.time} placeholder="時間" onChange={(event) => updatePanel(pageIndex, panelIndex, { time: event.target.value })} />
                    </label>
                    <label className="comic-inspector-field">
                      <span>見る角度</span>
                      <input
                        aria-label="カメラ"
                        value={panel.camera}
                        placeholder="カメラ"
                        onChange={(event) =>
                          updatePanel(pageIndex, panelIndex, { camera: event.target.value })
                        }
                      />
                    </label>
                    <label className="comic-inspector-field">
                      <span>配置</span>
                      <input aria-label="構図" value={panel.composition} placeholder="構図" onChange={(event) => updatePanel(pageIndex, panelIndex, { composition: event.target.value })} />
                    </label>
                    <label className="comic-inspector-field">
                      <span>雰囲気</span>
                      <input aria-label="雰囲気" value={panel.tone} placeholder="雰囲気・トーン" onChange={(event) => updatePanel(pageIndex, panelIndex, { tone: event.target.value })} />
                    </label>
                    <label className="comic-inspector-field">
                      <span>台詞</span>
                      <textarea
                        aria-label="台詞"
                        value={panel.dialogue.join("\n")}
                        placeholder="台詞（1行に1つ）"
                        onChange={(event) =>
                          updatePanel(pageIndex, panelIndex, {
                            dialogue: lines(event.target.value),
                          })
                        }
                      />
                    </label>
                    {panel.dialogue.map((text, index) => <label key={index}>台詞{index+1}の話者<select value={panel.dialogue_speakers[index]} onChange={(event)=>updatePanel(pageIndex,panelIndex,{dialogue_speakers:panel.dialogue.map((_,lineIndex)=>lineIndex===index ? event.target.value : panel.dialogue_speakers[lineIndex])})}><option value="">話者なし・画面外</option>{panel.character_names.map((name)=><option key={name} value={name}>{characterLabel(name)}</option>)}</select><small>{text}</small></label>)}
                    <label className="comic-inspector-field">
                      <span>ナレーション</span>
                      <textarea
                        aria-label="ナレーション"
                        value={panel.narration.join("\n")}
                        placeholder="ナレーション（1行に1つ）"
                        onChange={(event) => updatePanel(pageIndex, panelIndex, { narration: lines(event.target.value) })}
                      />
                    </label>
                    <label className="comic-inspector-field">
                      <span>効果音</span>
                      <textarea
                        aria-label="効果音"
                        value={panel.sound_effects.join("\n")}
                        placeholder="効果音（1行に1つ）"
                        onChange={(event) => updatePanel(pageIndex, panelIndex, { sound_effects: lines(event.target.value) })}
                      />
                    </label>
                    <label className="comic-inspector-field">
                      <span>前のコマから同じにしたいもの</span>
                      <select
                        aria-label="前のコマから引き継ぐもの"
                        value={panel.continuity_preset}
                        onChange={(event) =>
                          updatePanel(pageIndex, panelIndex, {
                            continuity_preset: event.target.value as typeof panel.continuity_preset,
                          })
                        }
                      >
                        <option value="dialogue_continuation">会話を継続</option>
                        <option value="action_continuation">動作を継続</option>
                        <option value="scene_change">場面転換</option>
                        <option value="outfit_change">衣装変更</option>
                        <option value="expression_only">表情のみ変更</option>
                        <option value="custom">カスタム</option>
                      </select>
                    </label>
                  </section>
                ))}
              </div>
              <footer className="comic-storyboard-panel-actions">
                <button
                  type="button"
                  disabled={!layouts.some((layout) => layout.panel_rects.length === page.panels.length - 1)}
                  onClick={() => changePanelCount(pageIndex, -1)}
                >− コマ</button>
                <button
                  type="button"
                  disabled={!layouts.some((layout) => layout.panel_rects.length === page.panels.length + 1)}
                  onClick={() => changePanelCount(pageIndex, 1)}
                >＋ コマ</button>
              </footer>
              <a className="comic-result-page-backlink" href="#comic-storyboard-page-nav">ページ一覧に戻る</a>
            </article>
          ))}
        </div>
        <footer>
          <p>下書きのページを新しい章に追加します。まだ編集していない最初の空白ページだけは、この下書きに置き換わります。</p>
          {errorMessage && <p className="comic-result-error" role="alert">{errorMessage}</p>}
          <button type="button" className="comic-button comic-button--quiet" onClick={addPage}>
            ＋ ページ
          </button>
          <button type="button" className="comic-button comic-button--quiet" disabled={busy} onClick={onClose}>
            編集に戻る
          </button>
          {metadata?.partial && onContinue && (
            <button
              type="button"
              className="comic-button comic-button--quiet"
              disabled={busy}
              onClick={onContinue}
            >
              {action === "continue" ? "続きを作っています…" : `残り${metadata.requested_pages - metadata.completed_pages}ページを続けて生成`}
            </button>
          )}
          <button
            type="button"
            className="comic-button comic-button--primary"
            disabled={busy}
            onClick={onApply}
          >
            {action === "apply" ? "ページを作っています…" : production ? "確認して制作を続ける" : metadata?.partial ? "完成したページを作品に追加" : "この下書きでページを作る"}
          </button>
        </footer>
    </section>
  );
}

function PageInspector({
  model,
  commit,
  changeLayout,
}: {
  model: EditorModel;
  commit: (state: EditorState) => void;
  changeLayout: (layoutKey: string) => Promise<void>;
}) {
  return (
    <>
      <p className="comic-kicker">ページ設定</p>
      <h3>ページ {model.page.order}</h3>
      <label className="comic-inspector-field">
        <span>制作状態</span>
        <select
          value={model.state.page.status}
          onChange={(event) =>
            commit(updatePage(model.state, {
              status: event.target.value as EditorState["page"]["status"],
            }))
          }
        >
          <option value="draft">下書き</option>
          <option value="active">制作中</option>
          <option value="complete">完成</option>
        </select>
      </label>
      <label className="comic-inspector-field">
        <span>レイアウト</span>
        <select
          value={model.page.layout_key}
          disabled={model.saveStatus === "dirty" || model.saveStatus === "saving"}
          onChange={(event) => void changeLayout(event.target.value)}
        >
          {model.studio.layout_catalog.map((layout) => (
            <option key={layout.key} value={layout.key}>{layout.label}</option>
          ))}
        </select>
      </label>
      <label className="comic-inspector-field">
        <span>背景色</span>
        <input
          type="color"
          value={model.state.page.background}
          onChange={(event) =>
            commit(updatePage(model.state, { background: event.target.value }))
          }
        />
      </label>
      <label className="comic-inspector-field">
        <span>ページ余白</span>
        <input
          type="range"
          min="0"
          max="160"
          value={model.state.page.margin}
          onChange={(event) =>
            commit(updatePage(model.state, { margin: Number(event.target.value) }))
          }
        />
        <small>{model.state.page.margin}px</small>
      </label>
      <label className="comic-inspector-field">
        <span>コマ間隔</span>
        <input
          type="range"
          min="0"
          max="80"
          value={model.state.page.gutter}
          onChange={(event) =>
            commit(updatePage(model.state, { gutter: Number(event.target.value) }))
          }
        />
        <small>{model.state.page.gutter}px</small>
      </label>
    </>
  );
}

function placeUnplacedPanel(model: EditorModel, panel: Panel): EditorState {
  const index = model.state.panel_placements.length;
  const layout = model.studio.layout_catalog.find(
    (item) => item.key === model.page.layout_key,
  );
  const preset = layout?.panel_rects[index];
  const fallbackOffset = (index % 4) * 0.04;
  const placement = preset ?? {
    x: 0.08 + fallbackOffset,
    y: 0.08 + fallbackOffset,
    width: 0.42,
    height: 0.32,
  };
  return {
    ...model.state,
    panel_placements: [
      ...model.state.panel_placements,
      {
        panel_id: panel.id,
        ...placement,
        z_index: Math.max(
          1,
          ...model.state.panel_placements.map((item) => item.z_index + 1),
        ),
        image_fit: "contain",
        image_position_x: 0.5,
        image_position_y: 0.5,
      },
    ],
    reading_order: [...model.state.reading_order, panel.id],
  };
}

export function UnplacedPanelsTray({
  model,
  commit,
  addPanel,
}: {
  model: EditorModel;
  commit: (state: EditorState) => void;
  addPanel: () => Promise<void>;
}) {
  const placedPanelIds = new Set(model.state.panel_placements.map((item) => item.panel_id));
  const unplacedPanels = model.page.panels.filter((panel) => !placedPanelIds.has(panel.id));
  return (
    <section className="comic-unplaced-tray" aria-label="未配置のコマ">
      <div className="comic-unplaced-tray__label">
        <strong>未配置のコマ</strong>
        <small>{unplacedPanels.length ? `${unplacedPanels.length}件` : "すべて配置済み"}</small>
      </div>
      <div className="comic-unplaced-tray__actions">
        {unplacedPanels.map((panel) => (
          <button
            key={panel.id}
            type="button"
            className="comic-button comic-button--quiet"
            onClick={() => {
              commit(placeUnplacedPanel(model, panel));
              window.dispatchEvent(
                new CustomEvent("comic:panel-select-requested", {
                  detail: { panelId: panel.id },
                }),
              );
            }}
          >
            コマ {panel.order} を配置
          </button>
        ))}
        <button
          type="button"
          className="comic-button comic-button--quiet"
          onClick={() => void addPanel()}
        >
          ＋ コマを追加
        </button>
      </div>
    </section>
  );
}

function PanelInspector({
  model,
  panel,
  commit,
  reopenedAnalysis,
  refreshCredits,
  updateContent,
  runGeneration,
  generationFeedback,
  removePanel,
  placeDialogue,
}: {
  model: EditorModel;
  panel: Panel;
  commit: (state: EditorState) => void;
  reopenedAnalysis: ReopenedPanelAnalysis | null;
  refreshCredits: () => Promise<void>;
  updateContent: (updates: Partial<PanelContent>) => void;
  runGeneration: (
    operation: string,
    requestedChange: string,
    outputCount: number,
    sourceAssetId?: string | null,
    analysisJobId?: string | null,
    expectedCredits?: number,
  ) => Promise<void>;
  generationFeedback?: PanelGenerationFeedback | null;
  removePanel: () => Promise<void>;
  placeDialogue: () => void;
}) {
  const [inspectorSection, setInspectorSection] = useState<
    "composition" | "people" | "generation" | "review"
  >("composition");
  const recentCandidates = model.studio.recent_assets.filter(
    (asset) => asset.panel_id === panel.id && ["panel_candidate", "upload"].includes(asset.asset_type),
  );
  const [candidates, setCandidates] = useState(recentCandidates);
  const [candidateOffset, setCandidateOffset] = useState(0);
  const [candidateHasMore, setCandidateHasMore] = useState(true);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [operation, setOperation] = useState("generate");
  const [assetError, setAssetError] = useState("");
  const [requestedChange, setRequestedChange] = useState("");
  const outputCount = 1;
  const [running, setRunning] = useState(false);
  const [poseReferenceId, setPoseReferenceId] = useState("");
  const [analysisAssetId, setAnalysisAssetId] = useState(panel.current_asset_id ?? "");
  const [analysisResult, setAnalysisResult] = useState<PanelAnalysisResult | null>(null);
  const [analysisJobId, setAnalysisJobId] = useState("");
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [analysisMessage, setAnalysisMessage] = useState("");
  const [referenceAssets, setReferenceAssets] = useState(model.studio.recent_assets);
  const [comparison, setComparison] = useState<{
    baseUrl: string;
    baseLabel: string;
    candidate: Asset;
  } | null>(null);
  const [comparisonBaseCandidate, setComparisonBaseCandidate] = useState<Asset | null>(null);
  const generationOption =
    model.studio.generation_options.find((option) => option.operation === operation)
    ?? model.studio.generation_options[0];
  const fallbackCreditsPerImage = operation === "generate"
    ? panelGenerationCreditsPerImage(
        model.studio,
        model.page,
        panel,
        poseReferenceId,
        referenceAssets,
      )
    : (generationOption?.credits_per_image ?? 0);
  const [generationPreview, setGenerationPreview] = useState<GenerationPreview | null>(null);
  const [previewError, setPreviewError] = useState("");
  const placement = model.state.panel_placements.find((item) => item.panel_id === panel.id);
  const contentKey = JSON.stringify(panel.content);
  useEffect(() => {
    let active = true;
    setGenerationPreview(null);
    setPreviewError("");
    const timer = window.setTimeout(() => {
      void previewGeneration(model.studio, panel.id, operation, poseReferenceId || null, panel.content)
        .then((value) => { if (active) setGenerationPreview(value); })
        .catch((error: unknown) => { if (active) setPreviewError(error instanceof Error ? error.message : "生成条件を確認できません。"); });
    }, 350);
    return () => { active = false; window.clearTimeout(timer); };
  }, [panel.id, contentKey, operation, poseReferenceId, panel.current_asset_id, model.studio.characters, model.studio.project_data]);
  const selectedCreditsPerImage = generationPreview?.credits ?? fallbackCreditsPerImage;
  const panelGenerating = panel.generation_status === "queued"
    || panel.generation_status === "generating";
  const aiCreditCopy = panelAiCreditCopy(model.studio.operation_credits);
  const updateCharacterField = (
    characterId: string,
    updates: Partial<PanelContent["characters"][number]>,
  ) => {
    updateContent({
      characters: panel.content.characters.map((item) =>
        item.character_id === characterId ? { ...item, ...updates } : item
      ),
    });
  };
  const toggleContinuity = (
    key: "keep" | "allow_change",
    value: string,
    checked: boolean,
  ) => {
    const current = panel.content.continuity[key];
    updateContent({
      continuity: {
        ...panel.content.continuity,
        preset: "custom",
        [key]: checked
          ? [...new Set([...current, value])]
          : current.filter((item) => item !== value),
      },
    });
  };
  const loadMoreCandidates = useCallback(async (reset = false) => {
    if (candidateLoading || (!reset && !candidateHasMore)) {
      return;
    }
    setCandidateLoading(true);
    try {
      const page = await loadPanelCandidates(
        model.studio,
        panel.id,
        reset ? 0 : candidateOffset,
      );
      setCandidates((current) => {
        const combined = reset ? page.assets : [...current, ...page.assets];
        return [...new Map(combined.map((asset) => [asset.id, asset])).values()];
      });
      setCandidateOffset(page.nextOffset);
      setCandidateHasMore(page.hasMore);
    } catch {
      setCandidateHasMore(false);
    } finally {
      setCandidateLoading(false);
    }
  }, [
    candidateHasMore,
    candidateLoading,
    candidateOffset,
    model.studio,
    panel.id,
  ]);
  useEffect(() => {
    setInspectorSection("composition");
    setCandidates(recentCandidates);
    setCandidateOffset(0);
    setCandidateHasMore(true);
    setComparison(null);
    setComparisonBaseCandidate(null);
    setPoseReferenceId("");
    setAnalysisAssetId(panel.current_asset_id ?? "");
    setAnalysisResult(null);
    setAnalysisJobId("");
    setAnalysisMessage("");
    void loadMoreCandidates(true);
  }, [panel.id, model.studio.recent_assets]);
  useEffect(() => {
    if (!analysisAssetId && candidates[0]) {
      setAnalysisAssetId(candidates[0].id);
    }
  }, [analysisAssetId, candidates]);
  useEffect(() => {
    setAnalysisAssetId(panel.current_asset_id ?? "");
    setAnalysisResult(null);
    setAnalysisJobId("");
    setAnalysisMessage("");
  }, [panel.current_asset_id]);
  useEffect(() => {
    if (!reopenedAnalysis || reopenedAnalysis.panelId !== panel.id) {
      return;
    }
    setAnalysisAssetId(reopenedAnalysis.assetId);
    setAnalysisResult(reopenedAnalysis.result);
    setAnalysisJobId(reopenedAnalysis.jobId);
    setAnalysisMessage("保存済みのAIチェック結果を表示しています。");
  }, [panel.id, reopenedAnalysis]);
  useEffect(() => {
    let active = true;
    void Promise.all([
      loadAssets(model.studio, { asset_type: "upload" }, 0, 100),
      loadAssets(model.studio, { asset_type: "character_reference" }, 0, 100),
      loadAssets(
        model.studio,
        { asset_type: "panel_candidate", panel_id: panel.id },
        0,
        100,
      ),
    ])
      .then((results) => {
        if (active) {
          setReferenceAssets(
            [
              ...new Map(
                results
                  .flatMap((result) => result.assets)
                  .map((asset) => [asset.id, asset]),
              ).values(),
            ],
          );
        }
      })
      .catch(() => {
        if (active) {
          setReferenceAssets(
            model.studio.recent_assets.filter(
              (asset) =>
                asset.asset_type === "upload"
                || asset.asset_type === "character_reference"
                || (
                  asset.asset_type === "panel_candidate"
                  && asset.panel_id === panel.id
                ),
            ),
          );
        }
      });
    return () => {
      active = false;
    };
  }, [model.studio.active_project?.id, panel.id]);
  const useCandidate = async (asset: Asset) => {
    setAssetError("");
    try {
      const selected = await selectPanelAsset(model.studio, panel.id, asset.id);
      window.dispatchEvent(new CustomEvent("comic:panel-updated", { detail: { panel: selected } }));
      setComparison(null);
      setComparisonBaseCandidate(null);
    } catch (error) { setAssetError(error instanceof Error ? error.message : "採用できませんでした。"); }
  };
  const compareCandidate = (asset: Asset) => {
    const next = resolveCandidateComparison(
      panel.current_asset_url,
      panel.current_asset_id,
      asset,
      comparisonBaseCandidate,
    );
    setComparison(next.comparison);
    setComparisonBaseCandidate(next.pendingBase);
  };
  return (
    <div className="comic-panel-inspector" data-panel-section={inspectorSection}>
      <p className="comic-kicker">コマ設定</p>
      <h3>コマ {panel.order}</h3>
      <p className="comic-inspector-hint">このページは{model.page.panels.filter((item) => item.current_asset_id).length}/{model.page.panels.length}コマに画像を設定済みです。画像と台詞を整え、プレビューで確認してから書き出してください。</p>
      {assetError && <p role="alert">{assetError}</p>}
      <div className="comic-inspector-section-switch" role="group" aria-label="コマ設定の項目">
        {([
          ["composition", "構図"],
          ["people", "人物・文字"],
          ["generation", "画像生成"],
          ["review", "確認"],
        ] as const).map(([value, label]) => (
          <button
            type="button"
            key={value}
            className={inspectorSection === value ? "is-active" : ""}
            aria-pressed={inspectorSection === value}
            onClick={() => setInspectorSection(value)}
          >
            {label}
          </button>
        ))}
      </div>
      <section className="comic-inspector-section" data-section="generation" aria-label="画像の準備">
      {panel.current_asset_id && <button type="button" className="comic-button comic-button--quiet" onClick={() => void downloadAsset(model.studio, panel.current_asset_id!).catch((error: Error) => setAssetError(error.message))}>原図をダウンロードして修正</button>}
      {panel.current_asset_id && placement && <fieldset className="comic-inspector-group comic-image-framing">
        <legend>画像の表示</legend>
        <div className="comic-image-framing__options">
          <label>
            <input type="radio" name={`image-fit-${panel.id}`} value="contain"
              checked={placement.image_fit === "contain"}
              onChange={() => commit(updatePanelPlacement(model.state, panel.id, {
                image_fit: "contain",
              }))} />
            <span><b>全体を表示</b><small>画像を切らずにコマ内へ収めます</small></span>
          </label>
          <label>
            <input type="radio" name={`image-fit-${panel.id}`} value="cover"
              checked={placement.image_fit === "cover"}
              onChange={() => commit(updatePanelPlacement(model.state, panel.id, { image_fit: "cover" }))} />
            <span><b>コマを埋める</b><small>余白なく表示し、はみ出す部分を調整できます</small></span>
          </label>
        </div>
        {placement.image_fit === "cover" && <>
          <p className="comic-inspector-hint">キャンバス上の画像をドラッグして、見せる位置を調整できます。</p>
          <button type="button" className="comic-button comic-button--quiet"
            disabled={placement.image_position_x === 0.5 && placement.image_position_y === 0.5}
            onClick={() => commit(updatePanelPlacement(model.state, panel.id, {
              image_position_x: 0.5,
              image_position_y: 0.5,
            }))}>
            表示位置を中央に戻す
          </button>
        </>}
      </fieldset>}
      <label className="comic-inspector-field"><span>外部で修正した画像を候補へ追加</span>
        <input type="file" accept="image/png,image/jpeg,image/webp" disabled={running} onChange={async (event) => {
          const input = event.currentTarget;
          const file = input.files?.[0];
          if (!file) return;
          setRunning(true); setAssetError("");
          try {
            const asset = await uploadPanelRevision(model.studio, panel.id, file);
            setCandidates((current) => [asset, ...current]);
            setComparison({baseUrl:panel.current_asset_url, baseLabel:"現在のコマ", candidate:asset});
            window.dispatchEvent(new CustomEvent("comic:asset-changed"));
          } catch (error) { setAssetError(error instanceof Error ? error.message : "アップロードできませんでした。"); }
          finally { setRunning(false); input.value = ""; }
        }} />
      </label>
      <button type="button" className="comic-button comic-button--quiet" onClick={() => {
        const order = model.state.reading_order;
        const following = [...order.slice(order.indexOf(panel.id) + 1), ...order.slice(0, order.indexOf(panel.id))];
        const next = following.find((id) => !model.page.panels.find((item) => item.id === id)?.current_asset_id);
        if (next) window.dispatchEvent(new CustomEvent("comic:panel-select-requested", {detail:{panelId:next}}));
        else setAssetError(panel.current_asset_id
          ? "配置済みのコマの画像は揃いました。台詞を配置してプレビュー・書き出しへ進めます。"
          : "ほかに移動できる空きコマはありません。このコマに画像を設定してください。");
      }}>次の空きコマへ</button>
      </section>

      <section className="comic-inspector-section" data-section="composition" aria-label="構図と場面">
      <Field
        label="このコマの目的"
        value={panel.content.story_goal}
        textarea
        onChange={(story_goal) => updateContent({ story_goal })}
      />
      <Field
        label="カメラ"
        value={panel.content.camera}
        onChange={(camera) => updateContent({ camera })}
      />
      <Field
        label="構図"
        value={panel.content.composition}
        textarea
        onChange={(composition) => updateContent({ composition })}
      />
      <Field
        label="時間"
        value={panel.content.time}
        onChange={(time) => updateContent({ time })}
      />
      <Field
        label="色調・雰囲気"
        value={panel.content.tone}
        onChange={(tone) => updateContent({ tone })}
      />
      <label className="comic-inspector-field">
        <span>シーン</span>
        <select
          value={panel.content.scene_id}
          onChange={(event) => {
            const scene = model.studio.project_data?.scene_library.find(
              (item) => item.id === event.target.value,
            );
            updateContent({
              scene_id: event.target.value,
              time: scene?.time || panel.content.time,
            });
          }}
        >
          <option value="">未設定</option>
          {(model.studio.project_data?.scene_library ?? []).map((scene) => (
            <option key={scene.id} value={scene.id}>{scene.name}</option>
          ))}
        </select>
      </label>
      </section>
      <section className="comic-inspector-section" data-section="people" aria-label="人物と文字">
      <fieldset className="comic-inspector-group">
        <legend>登場キャラクター</legend>
        {model.studio.characters.map((character) => {
          const current = panel.content.characters.find(
            (item) => item.character_id === character.id,
          );
          return (
            <div key={character.id} className="comic-character-setting">
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(current)}
                  onChange={(event) => {
                    const characters = event.target.checked
                      ? [
                          ...panel.content.characters,
                          {
                            character_id: character.id,
                            outfit_id: character.profile.default_outfit_id,
                            expression: "",
                            action: "",
                            pose: "",
                            position: "",
                            relationship_note: "",
                          },
                        ]
                      : panel.content.characters.filter(
                          (item) => item.character_id !== character.id,
                        );
                    updateContent({ characters });
                  }}
                />
                {character.name}
              </label>
              {current && (
                <>
                  <select
                    value={current.outfit_id}
                    aria-label={`${character.name} の衣装`}
                    onChange={(event) => updateCharacterField(character.id, { outfit_id: event.target.value })}
                  >
                    <option value="">標準</option>
                    {character.profile.outfits.map((outfit) => (
                      <option key={outfit.id} value={outfit.id}>{outfit.name}</option>
                    ))}
                  </select>
                  <input
                    value={current.expression}
                    placeholder="表情"
                    onChange={(event) => updateCharacterField(character.id, { expression: event.target.value })}
                  />
                  <input
                    value={current.action}
                    placeholder="動作"
                    onChange={(event) => updateCharacterField(character.id, { action: event.target.value })}
                  />
                  <input
                    value={current.pose}
                    placeholder="姿勢・ポーズ"
                    onChange={(event) => updateCharacterField(character.id, { pose: event.target.value })}
                  />
                  <input
                    value={current.position}
                    placeholder="画面内の位置"
                    onChange={(event) => updateCharacterField(character.id, { position: event.target.value })}
                  />
                  <input
                    value={current.relationship_note}
                    placeholder="他キャラとの距離・関係"
                    onChange={(event) => updateCharacterField(character.id, { relationship_note: event.target.value })}
                  />
                </>
              )}
            </div>
          );
        })}
      </fieldset>
      {(["dialogue", "narration", "sound_effects"] as const).map((kind) => <fieldset className="comic-inspector-group" key={kind}>
        <legend>{kind === "dialogue" ? "セリフ" : kind === "narration" ? "ナレーション" : "効果音"}</legend>
        {panel.content[kind].map((line, index) => <div key={line.id} className="comic-character-setting">
          <label>{index + 1}・話者<select value={line.character_id ?? ""} onChange={(event) => updateContent({[kind]:panel.content[kind].map((item) => item.id === line.id ? {...item, character_id:event.target.value || null} : item)})}>
            <option value="">話者なし・画面外</option>{model.studio.characters.map((character) => <option key={character.id} value={character.id}>{character.name}</option>)}
          </select></label>
          <Field label={`${index + 1}の本文`} value={line.text} textarea onChange={(text) => updateContent({[kind]:panel.content[kind].map((item) => item.id === line.id ? {...item,text} : item)})} />
          <button type="button" onClick={() => updateContent({[kind]:panel.content[kind].filter((item) => item.id !== line.id)})}>この行と関連する吹き出しを削除</button>
        </div>)}
        <button type="button" onClick={() => updateContent({[kind]:[...panel.content[kind],{id:crypto.randomUUID(),character_id:null,text:""}]})}>行を追加</button>
      </fieldset>)}
      <p className="comic-inspector-hint">画像を作り直しても、入力した文字は残ります。「台詞を吹き出しに配置」で重複なく配置でき、本文の修正は吹き出しにも反映されます。位置や大きさは「画布」で調整できます。</p>
      <button type="button" className="comic-button comic-button--primary" onClick={placeDialogue}>台詞を吹き出しに配置</button>
      </section>
      <section className="comic-inspector-section" data-section="composition" aria-label="画風と引き継ぎ">
      <Field
        label="このコマだけの画風指定"
        value={panel.content.style_override}
        onChange={(style_override) => updateContent({ style_override })}
      />
      <label className="comic-inspector-field">
        <span>前のコマから引き継ぐもの</span>
        <select
          value={panel.content.continuity.preset}
          onChange={(event) => {
            const preset = event.target.value as PanelContent["continuity"]["preset"];
            const rule = model.studio.continuity_presets.find(
              (item) => item.preset === preset,
            );
            updateContent({
              continuity: {
                ...panel.content.continuity,
                preset,
                previous_panel_id: preset === "scene_change" ? null : panel.content.continuity.previous_panel_id,
                keep: rule ? [...rule.keep] : panel.content.continuity.keep,
                allow_change: rule
                  ? [...rule.allow_change]
                  : panel.content.continuity.allow_change,
              },
            });
          }}
        >
          {model.studio.continuity_presets.map((item) => (
            <option value={item.preset} key={item.preset}>{item.label}</option>
          ))}
        </select>
      </label>
      <fieldset className="comic-character-outfit">
        <legend>前のコマから同じにしたいもの</legend>
        <label className="comic-inspector-field">
          <span>前のコマ</span>
          <select
            value={panel.content.continuity.use_previous_panel === false ? "none" : panel.content.continuity.previous_panel_id ?? ""}
            onChange={(event) =>
              updateContent({
                continuity: {
                  ...panel.content.continuity,
                  previous_panel_id: event.target.value === "none" ? null : event.target.value || null,
                  use_previous_panel: event.target.value !== "none",
                },
              })
            }
          >
            <option value="">自動（読順の直前・場面転換時はなし）</option>
            <option value="none">引き継がない</option>
            {(generationPreview?.previous_options ?? model.page.panels.filter((item) => item.id !== panel.id).map((item) => ({id:item.id,label:`コマ ${item.order}`}))).map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>
        <fieldset className="comic-inspector-group">
          <legend>同じにしたいもの</legend>
          {CONTINUITY_KEEP_OPTIONS.map(([value, label]) => (
            <label key={value} className="comic-inspector-check">
              <input
                type="checkbox"
                checked={panel.content.continuity.keep.includes(value)}
                onChange={(event) => toggleContinuity("keep", value, event.target.checked)}
              />
              {label}
            </label>
          ))}
        </fieldset>
        <fieldset className="comic-inspector-group">
          <legend>変えてよいもの</legend>
          {CONTINUITY_CHANGE_OPTIONS.map(([value, label]) => (
            <label key={value} className="comic-inspector-check">
              <input
                type="checkbox"
                checked={panel.content.continuity.allow_change.includes(value)}
                onChange={(event) => toggleContinuity("allow_change", value, event.target.checked)}
              />
              {label}
            </label>
          ))}
        </fieldset>
      </fieldset>
      <Field
        label="追加指示"
        value={panel.content.extra_description}
        textarea
        onChange={(extra_description) => updateContent({ extra_description })}
      />
      </section>
      <section className="comic-inspector-section" data-section="generation" aria-label="画像生成と候補">
      <label className="comic-inspector-field">
          <span>画像をどう作りますか</span>
        <select
          value={operation}
          onChange={(event) => {
            const nextOperation = event.target.value;
            setOperation(nextOperation);
            setPoseReferenceId("");
          }}
        >
          {model.studio.generation_options.map((option) => (
            <option key={option.operation} value={option.operation}>
              {option.label}（予定 {
                (option.operation === "generate"
                  ? panelGenerationCreditsPerImage(
                      model.studio,
                      model.page,
                      panel,
                      poseReferenceId,
                      referenceAssets,
                    )
                  : option.credits_per_image) * outputCount
              } クレジット）
            </option>
          ))}
        </select>
      </label>
      {operation !== "generate" && (
        <Field label="変更内容" value={requestedChange} onChange={setRequestedChange} />
      )}
      {operation === "generate" && (
        <label className="comic-inspector-field">
          <span>ポーズ参考（任意）</span>
          <select
            value={poseReferenceId}
            onChange={(event) => setPoseReferenceId(event.target.value)}
          >
            <option value="">使用しない</option>
            {referenceAssets
              .filter(
                (asset) =>
                  isReusableImageAsset(asset)
                  && (
                    asset.asset_type === "upload"
                    || asset.asset_type === "character_reference"
                    || (
                      asset.asset_type === "panel_candidate"
                      && asset.panel_id === panel.id
                    )
                  )
              )
              .map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.asset_type === "upload"
                    ? "アップロード"
                    : asset.asset_type === "character_reference"
                      ? "キャラクター設定画"
                      : "このコマの候補"}
                  {" · "}
                  {asset.id.slice(0, 8)}
                </option>
              ))}
          </select>
          <small>
            {referenceAssets.find((asset) => asset.id === poseReferenceId)?.asset_type
                === "character_reference"
              ? "設定画は人物の同一性・衣装・体格を保つ参考として使います。"
              : "アップロード画像とコマ候補は、姿勢と体の向きだけを参考にします。"}
          </small>
          <span className="comic-candidate-grid">{referenceAssets.map((asset) => <button type="button" key={asset.id} aria-label="この画像を参考にする" aria-pressed={poseReferenceId===asset.id} onClick={() => setPoseReferenceId(asset.id)}><img src={asset.download_url} alt="ポーズ・人物参考" style={{width:"100%",height:70,objectFit:"contain"}} loading="lazy" /></button>)}</span>
          <small>
            今回の予定：1枚{selectedCreditsPerImage}クレジット、合計
            {selectedCreditsPerImage * outputCount}クレジットです。
          </small>
        </label>
      )}
      {previewError && <p role="alert">{previewError}</p>}
      {generationPreview && <div className="comic-inspector-group">
        <p>今回 {generationPreview.credits}クレジット・残高 {generationPreview.usable_credits}クレジット</p>
        <p>実際に使う参考画像（{generationPreview.sources.length}枚）</p>
        <div className="comic-candidate-grid">{generationPreview.sources.map((source) => <figure key={source.id}>
          <img src={source.download_url} alt="生成に使用する参考" style={{width:"100%",maxHeight:100,objectFit:"contain"}} />
          <figcaption>{source.kinds.map((kind) => ({character:"主参考",character_extra:"補助参考",outfit:"衣装",scene:"シーン",previous_panel:"前のコマ",pose:"ポーズ",explicit:"編集元",current_panel:"現在の画像"}[kind] ?? kind)).join("・")}</figcaption>
        </figure>)}</div>
        {generationPreview.credits > generationPreview.usable_credits && <p role="alert">クレジットが不足しています。追加後に生成できます。</p>}
      </div>}
      <p className="comic-inspector-hint">候補を生成し、使いたい画像の「採用」を押してください。採用するまで今の画像は変わりません。</p>
      <button
        type="button"
        className="comic-button comic-button--primary comic-inspector-action"
        disabled={
          running
          || panelGenerating
          || !generationPreview
          || generationPreview.credits > generationPreview.usable_credits
          || (operation.startsWith("edit_") && !requestedChange.trim())
        }
        onClick={async () => {
          setRunning(true);
          try {
            await runGeneration(
              operation,
              requestedChange,
              outputCount,
              poseReferenceId || null,
              null,
              generationPreview?.credits,
            );
          } finally {
            setRunning(false);
          }
        }}
      >
        {running || panelGenerating ? "生成中…" : `候補を${outputCount}枚生成`}
      </button>
      {generationFeedback?.panelId === panel.id && (
        <p
          className="comic-operation-status"
          role={generationFeedback.status === "error" ? "alert" : "status"}
        >
          {generationFeedback.message}
        </p>
      )}
      {candidates.length > 0 && (
        <div className="comic-inspector-candidates">
          <strong>画像の候補</strong>
          <div className="comic-candidate-grid">
            {candidates.map((asset) => (
              <CandidateButton
                key={asset.id}
                asset={asset}
                isCurrent={panel.current_asset_id === asset.id}
                canCompare={
                  (Boolean(panel.current_asset_url) && panel.current_asset_id !== asset.id)
                  || candidates.some((item) => item.id !== asset.id)
                }
                onCompare={() => compareCandidate(asset)}
                onSelect={() => void useCandidate(asset)}
              />
            ))}
          </div>
          {comparisonBaseCandidate && (
            <p className="comic-candidate-comparison__prompt" role="status">
              比較するもう1枚の候補で「比較」を押してください。
            </p>
          )}
          {comparison && (
            <CandidateComparison
              aspectRatio={(() => { const placement = model.state.panel_placements.find((item) => item.panel_id === panel.id); const margin = model.state.page.margin ?? 0; return placement ? placement.width * (model.state.page.width - 2 * margin) / (placement.height * (model.state.page.height - 2 * margin)) : undefined; })()}
              baseUrl={comparison.baseUrl}
              baseLabel={comparison.baseLabel}
              candidate={comparison.candidate}
              onClose={() => setComparison(null)}
              onSelect={() => void useCandidate(comparison.candidate)}
            />
          )}
          {candidateHasMore && (
            <button
              type="button"
              className="comic-button comic-button--quiet"
              disabled={candidateLoading}
              onClick={() => void loadMoreCandidates()}
            >
              {candidateLoading ? "読み込み中…" : "過去の候補をさらに表示"}
            </button>
          )}
        </div>
      )}
      </section>
      <section className="comic-inspector-section" data-section="review" aria-label="AIチェックと管理">
      {(panel.current_asset_id || candidates.length > 0) && (
        <section className="comic-panel-analysis">
          <strong>AIチェック</strong>
          <p>{aiCreditCopy.analysisDescription}</p>
          <label className="comic-inspector-field">
            <span>確認する画像</span>
            <select
              value={analysisAssetId}
              onChange={(event) => {
                setAnalysisAssetId(event.target.value);
                setAnalysisResult(null);
                setAnalysisMessage("");
              }}
            >
              {panel.current_asset_id && (
                <option value={panel.current_asset_id}>現在使用中の画像</option>
              )}
              {candidates
                .filter((asset) => asset.id !== panel.current_asset_id)
                .map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    候補 · {asset.id.slice(0, 8)}
                  </option>
                ))}
            </select>
          </label>
          <button
            type="button"
            className="comic-button comic-button--quiet comic-inspector-action"
            disabled={analysisRunning || !analysisAssetId}
            onClick={async () => {
              setAnalysisRunning(true);
              setAnalysisResult(null);
              setAnalysisMessage("");
              try {
                const jobId = await analyzePanel(model.studio, panel.id, analysisAssetId);
                const job = await waitForTrackedJob(model.studio, jobId);
                const parsed = panelAnalysisFromOutput(job.output);
                if (!parsed) {
                  throw new ComicApiError("AIチェック結果の形式が正しくありません。");
                }
                setAnalysisResult(parsed);
                setAnalysisJobId(jobId);
                setAnalysisMessage("AIチェックが完了しました。結果を確認し、必要なら修正候補を生成してください。");
                try {
                  await refreshCredits();
                } catch {
                  setAnalysisMessage(
                    "AIチェックは完了しました。残高を再読み込みできないため、画面を更新してください。",
                  );
                }
              } catch (error) {
                setAnalysisMessage(
                  error instanceof Error ? error.message : "AIチェックを実行できませんでした。",
                );
              } finally {
                setAnalysisRunning(false);
              }
            }}
          >
            {analysisRunning ? "確認中…" : "AIチェックを実行"}
          </button>
          {analysisMessage && <p className="comic-panel-analysis__message" role="status" aria-live="polite">{analysisMessage}</p>}
          {analysisResult && (
            <div className="comic-panel-analysis__result">
              <button
                type="button"
                className="comic-button comic-button--quiet"
                onClick={() => {
                  setAnalysisResult(null);
                  setAnalysisMessage("");
                }}
              >
                レポートを閉じる
              </button>
              <strong>
                {analysisResult.passed
                  ? "大きな問題は見つかりませんでした"
                  : "修正候補があります"}
              </strong>
              <p>{analysisResult.summary}</p>
              {analysisResult.issues.map((issue, index) => (
                <article key={`${issue.category}-${index}`} data-severity={issue.severity}>
                  <strong>
                    {ANALYSIS_CATEGORY_LABELS[issue.category]}
                    {" · "}
                    {issue.severity === "error" ? "要修正" : "要確認"}
                  </strong>
                  <p>{issue.message}</p>
                  <small>{issue.correction_instruction}</small>
                </article>
              ))}
              <p className="comic-inspector-hint">
                AIの判断は誤る場合があります。必ず画像を目視確認してください。
              </p>
              {!analysisResult.passed && analysisResult.correction_prompt && (
                <button
                  type="button"
                  className="comic-button comic-button--primary comic-inspector-action"
                  disabled={running || panelGenerating || !analysisJobId}
                  onClick={() => {
                    if (
                      !window.confirm(
                        aiCreditCopy.correctionConfirmation,
                      )
                    ) {
                      return;
                    }
                    void runGeneration(
                      "apply_ai_correction",
                      analysisResult.correction_prompt,
                      1,
                      analysisAssetId,
                      analysisJobId,
                    );
                  }}
                >
                  {aiCreditCopy.correctionButton}
                </button>
              )}
            </div>
          )}
        </section>
      )}
      <p className="comic-inspector-hint">
        コマの説明は即時保存されます。画像生成は保存後の内容を使います。
      </p>
      <button
        type="button"
        className="comic-button comic-button--quiet comic-inspector-action"
        onClick={() => {
          if (window.confirm(`コマ ${panel.order} を削除しますか？素材は削除されません。`)) {
            void removePanel();
          }
        }}
      >
        コマを削除
      </button>
      </section>
    </div>
  );
}

function CandidateButton({
  asset,
  isCurrent,
  canCompare,
  onCompare,
  onSelect,
}: {
  asset: Asset;
  isCurrent: boolean;
  canCompare: boolean;
  onCompare: () => void;
  onSelect: () => void;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  return (
    <article className="comic-candidate-card">
      {imageFailed
        ? (
          <p className="comic-candidate-image-error" role="alert">
            候補画像を読み込めませんでした。
          </p>
        )
        : (
          <img
            src={asset.download_url}
            alt="生成候補"
            onError={() => setImageFailed(true)}
          />
        )}
      <span>
        {isCurrent ? "使用中" : "候補"}
        {" · "}
        {new Date(asset.created_at).toLocaleDateString("ja-JP")}
      </span>
      <div className="comic-candidate-card__actions">
        <button
          type="button"
          className="comic-button comic-button--quiet"
          disabled={!canCompare}
          onClick={onCompare}
        >
          比較
        </button>
        <button
          type="button"
          className="comic-button comic-button--primary"
          disabled={isCurrent}
          onClick={onSelect}
        >
          {isCurrent ? "使用中" : "採用"}
        </button>
      </div>
    </article>
  );
}

export function CandidateComparison({
  aspectRatio,
  baseUrl,
  baseLabel,
  candidate,
  onClose,
  onSelect,
}: {
  aspectRatio?: number;
  baseUrl: string;
  baseLabel: string;
  candidate: Asset;
  onClose: () => void;
  onSelect: () => void;
}) {
  const [split, setSplit] = useState(50);
  const [baseImageFailed, setBaseImageFailed] = useState(false);
  const [candidateImageFailed, setCandidateImageFailed] = useState(false);
  return (
    <section className="comic-candidate-comparison" aria-label="候補画像の比較">
      <header>
        <strong>{baseLabel}</strong>
        <span>候補</span>
      </header>
      <div className="comic-candidate-comparison__images" style={aspectRatio ? {
        aspectRatio: String(aspectRatio),
        width: `min(100%, ${aspectRatio * 600}px, calc((100vh - 220px) * ${aspectRatio}))`,
        justifySelf: "center",
      } : undefined}>
        {!baseImageFailed && (
          <img src={baseUrl} alt={baseLabel} style={aspectRatio ? {height:"100%",minHeight:0,maxHeight:"none",objectFit:"cover"} : undefined} onError={() => setBaseImageFailed(true)} />
        )}
        {!candidateImageFailed && (
          <img
            src={candidate.download_url}
            alt="比較中の候補"
            style={{ ...(aspectRatio ? {height:"100%", minHeight:0, maxHeight:"none", objectFit:"cover" as const} : {}), clipPath: `inset(0 ${100 - split}% 0 0)` }}
            onError={() => setCandidateImageFailed(true)}
          />
        )}
      </div>
      {(baseImageFailed || candidateImageFailed) && (
        <p className="comic-candidate-image-error" role="alert">
          {baseImageFailed && candidateImageFailed
            ? "比較する画像を読み込めませんでした。"
            : baseImageFailed
              ? "比較元の画像を読み込めませんでした。"
              : "比較先の画像を読み込めませんでした。"}
        </p>
      )}
      <label>
        <span>比較位置 {split}%</span>
        <input
          type="range"
          min="0"
          max="100"
          value={split}
          aria-label="画像の比較位置"
          onChange={(event) => setSplit(Number(event.target.value))}
        />
      </label>
      <div className="comic-candidate-comparison__actions">
        <button type="button" className="comic-button comic-button--quiet" onClick={onClose}>
          閉じる
        </button>
        <button type="button" className="comic-button comic-button--primary" onClick={onSelect}>
          この候補を採用
        </button>
      </div>
    </section>
  );
}

function BubbleInspector({
  model,
  bubble,
  commit,
}: {
  model: EditorModel;
  bubble: Bubble;
  commit: (state: EditorState) => void;
}) {
  const change = (updates: Partial<Bubble>) =>
    commit(updateBubble(model.state, bubble.id, updates));
  return (
    <>
      <p className="comic-kicker">吹き出し設定</p>
      <h3>テキスト</h3>
      <Field label="内容" value={bubble.text} textarea onChange={(text) => change({ text })} />
      {bubble.source_line_id && <p className="comic-inspector-hint">下書きの台詞と連動しています。話者：{bubble.speaker_name || "話者なし"}</p>}
      <label className="comic-inspector-field"><span>読む順番（このコマ内）</span><input type="number" min={1} max={201} value={bubble.reading_order+1} onChange={(event)=>change({reading_order:Math.max(0,Math.min(200,Number(event.target.value)-1))})} /></label>
      <label className="comic-inspector-field">
        <span>種類</span>
        <select value={bubble.type} onChange={(event) => change({ type: event.target.value as Bubble["type"] })}>
          <option value="speech">会話</option>
          <option value="thought">思考</option>
          <option value="narration">ナレーション</option>
          <option value="sound_effect">効果音</option>
          <option value="small_note">小さな注釈</option>
          <option value="plain_text">枠なし文字</option>
        </select>
      </label>
      <label className="comic-inspector-field">
        <span>文字方向</span>
        <select
          value={bubble.writing_mode}
          onChange={(event) => change({ writing_mode: event.target.value as Bubble["writing_mode"] })}
        >
          <option value="vertical-rl">縦書き</option>
          <option value="horizontal-tb">横書き</option>
        </select>
      </label>
      <label className="comic-inspector-field">
        <span>文字サイズ</span>
        <input
          type="range"
          min="8"
          max="80"
          value={bubble.style.font_size}
          onChange={(event) =>
            change({ style: { ...bubble.style, font_size: Number(event.target.value) } })
          }
        />
      </label>
      <label className="comic-inspector-field">
        <span>フォント</span>
        <select
          value={bubble.style.font_family}
          onChange={(event) =>
            change({
              style: {
                ...bubble.style,
                font_family: event.target.value as Bubble["style"]["font_family"],
              },
            })
          }
        >
          <option value="Noto Sans JP">ゴシック</option>
          <option value="Noto Serif JP">明朝</option>
        </select>
      </label>
      <div className="comic-inspector-color-grid">
        {[
          ["文字", "text_color"],
          ["背景", "background_color"],
          ["枠", "border_color"],
        ].map(([label, key]) => (
          <label key={key}>
            <span>{label}</span>
            <input
              type="color"
              value={bubble.style[key as "text_color" | "background_color" | "border_color"]}
              onChange={(event) =>
                change({ style: { ...bubble.style, [key]: event.target.value } })
              }
            />
          </label>
        ))}
      </div>
      <label className="comic-inspector-field">
        <span>枠線の太さ：{bubble.style.border_width}px</span>
        <input
          type="range"
          min="0"
          max="12"
          value={bubble.style.border_width}
          onChange={(event) =>
            change({ style: { ...bubble.style, border_width: Number(event.target.value) } })
          }
        />
      </label>
      <label className="comic-inspector-field">
        <span>しっぽの向き</span>
        <select
          value={bubble.style.tail_direction}
          disabled={!["speech", "thought"].includes(bubble.type)}
          onChange={(event) =>
            change({ style: { ...bubble.style, tail_direction: event.target.value } })
          }
        >
          {["none", "top", "top-right", "right", "bottom-right", "bottom", "bottom-left", "left", "top-left"].map(
            (direction) => <option key={direction} value={direction}>{direction}</option>,
          )}
        </select>
        {!["speech", "thought"].includes(bubble.type) && (
          <small>会話・思考の吹き出しで使用できます。</small>
        )}
      </label>
      <button
        type="button"
        className="comic-button comic-button--quiet comic-inspector-action"
        onClick={() => {
          const duplicate = {
            ...structuredClone(bubble),
            id: crypto.randomUUID(),
            source_line_id: "",
            source_character_id: null,
            speaker_name: "",
            x: Math.min(1 - bubble.width, bubble.x + 0.03),
            y: Math.min(1 - bubble.height, bubble.y + 0.03),
            z_index: bubble.z_index + 1,
          };
          commit({ ...model.state, bubbles: [...model.state.bubbles, duplicate] });
        }}
      >
        吹き出しを複製
      </button>
      <button
        type="button"
        className="comic-button comic-button--quiet comic-inspector-action"
        onClick={() =>
          commit({
            ...model.state,
            bubbles: model.state.bubbles.filter((item) => item.id !== bubble.id),
          })
        }
      >
        吹き出しを削除
      </button>
    </>
  );
}

function Inspector({
  model,
  dispatch,
  commit,
  reopenedAnalysis,
  changeLayout,
  removePanel,
  queuePanelSave,
  flushPanelSaves,
}: {
  model: EditorModel;
  dispatch: React.Dispatch<Parameters<typeof editorReducer>[1]>;
  commit: (state: EditorState) => void;
  reopenedAnalysis: ReopenedPanelAnalysis | null;
  changeLayout: (layoutKey: string) => Promise<void>;
  removePanel: (panelId: string) => Promise<void>;
  queuePanelSave: (panel: Panel) => void;
  flushPanelSaves: (panelId?: string) => Promise<void>;
}) {
  const panel = selectedPanel(model);
  const bubble = selectedBubble(model);
  const [generationFeedback, setGenerationFeedback] = useState<PanelGenerationFeedback | null>(null);
  const updateContent = (updates: Partial<PanelContent>) => {
    if (!panel) {
      return;
    }
    const changed = { ...panel, content: { ...panel.content, ...updates } };
    commit(syncPanelBubbles(model.state, changed, model.studio.characters));
    queuePanelSave(changed);
  };
  const runGeneration = async (
    operation: string,
    requestedChange: string,
    outputCount: number,
    sourceAssetId: string | null = null,
    analysisJobId: string | null = null,
    expectedCredits?: number,
  ) => {
    if (!panel) {
      return;
    }
    setGenerationFeedback(null);
    let generationStarted = false;
    try {
      await flushPanelSaves(panel.id);
      const jobId = await generatePanel(
        model.studio,
        panel.id,
        operation,
        requestedChange,
        outputCount,
        sourceAssetId,
        analysisJobId,
        expectedCredits,
      );
      generationStarted = true;
      dispatch({
        type: "panel-status",
        panel: { ...panel, generation_status: "queued" },
      });
      await waitForTrackedJob(model.studio, jobId);
      const [studio, page] = await Promise.all([
        loadBootstrap(model.studio.urls.bootstrap, model.studio.active_project?.id),
        loadComicPage(model.studio, model.page.id),
      ]);
      dispatch({ type: "refresh-studio", studio: { ...studio, active_page: page } });
      dispatch({ type: "message", message: "画像候補が完成しました。" });
      setGenerationFeedback({
        panelId: panel.id,
        status: "success",
        message: "新しい候補ができました。",
      });
    } catch (error) {
      if (
        generationStarted
        && error instanceof ComicApiError
        && error.code === "job_failed"
      ) {
        dispatch({
          type: "panel-status",
          panel: { ...panel, generation_status: "failed" },
        });
      }
      dispatch({
        type: "message",
        message: error instanceof Error ? error.message : "画像生成を開始できませんでした。",
      });
      setGenerationFeedback({
        panelId: panel.id,
        status: "error",
        message: error instanceof Error ? error.message : "画像生成を完了できませんでした。",
      });
    }
  };
  const refreshCredits = async () => {
    const refreshed = await loadBootstrap(
      model.studio.urls.bootstrap,
      model.studio.active_project?.id,
    );
    dispatch({
      type: "refresh-studio",
      studio: {
        ...model.studio,
        usable_credits: refreshed.usable_credits,
        active_page: model.page,
      },
    });
  };
  return (
    <div className="comic-react-inspector">
      {model.selection.kind === "page" && (
        <PageInspector
          model={model}
          commit={commit}
          changeLayout={changeLayout}
        />
      )}
      {panel && (
        <PanelInspector
          model={model}
          panel={panel}
          commit={commit}
          reopenedAnalysis={reopenedAnalysis}
          refreshCredits={refreshCredits}
          updateContent={updateContent}
          runGeneration={runGeneration}
          generationFeedback={generationFeedback}
          removePanel={() => removePanel(panel.id)}
          placeDialogue={() => commit(placePanelDialogue(model, panel))}
        />
      )}
      {bubble && <BubbleInspector model={model} bubble={bubble} commit={commit} />}
    </div>
  );
}

export interface CharacterDraft {
  source: Character | null;
  name: string;
  profile: Character["profile"];
}

export interface BatchCharacterRow {
  id: string;
  name: string;
  appearance: string;
  characterId: string;
  status:
    | "draft"
    | "creating"
    | "saved"
    | "generating"
    | "generated"
    | "create_failed"
    | "generation_failed";
  generateRequested: boolean;
  error: string;
}

interface BatchCharacterProcessOptions {
  generateSheets: boolean;
  onlyIndex: number | null;
  createCharacter: (row: BatchCharacterRow) => Promise<string>;
  generateCharacter: (characterId: string) => Promise<void>;
  onUpdate: (rows: BatchCharacterRow[]) => void;
}

export async function processBatchCharacterRows(
  initialRows: BatchCharacterRow[],
  options: BatchCharacterProcessOptions,
): Promise<BatchCharacterRow[]> {
  const rows = initialRows.map((row) => ({
    ...row,
    generateRequested: options.onlyIndex === null ? options.generateSheets : row.generateRequested,
  }));
  const publish = () => options.onUpdate(rows.map((row) => ({ ...row })));
  const indexes = options.onlyIndex === null
    ? rows.map((_, index) => index)
    : [options.onlyIndex];
  publish();
  for (const index of indexes) {
    const row = rows[index];
    if (!row || !row.name.trim() || !row.appearance.trim()) {
      continue;
    }
    let characterId = row.characterId;
    try {
      if (!characterId) {
        rows[index] = { ...row, status: "creating", error: "" };
        publish();
        characterId = await options.createCharacter(rows[index]);
        rows[index] = {
          ...rows[index],
          characterId,
          status: "saved",
          error: "",
        };
        publish();
      }
      const shouldGenerate = options.onlyIndex === null
        ? options.generateSheets
        : rows[index].generateRequested;
      if (shouldGenerate && rows[index].status !== "generated") {
        rows[index] = { ...rows[index], status: "generating", error: "" };
        publish();
        await options.generateCharacter(characterId);
        rows[index] = { ...rows[index], status: "generated", error: "" };
        publish();
      }
    } catch (error) {
      const apiError = error instanceof ComicApiError ? error : null;
      const generationFailed = Boolean(characterId);
      rows[index] = {
        ...rows[index],
        characterId,
        status:
          apiError?.code === "job_poll_timeout"
            ? "generating"
            : generationFailed
              ? "generation_failed"
              : "create_failed",
        error:
          apiError?.code === "job_poll_timeout"
            ? "タスクは処理中です。後でタスク一覧から確認してください。"
            : error instanceof Error
              ? error.message
              : "処理に失敗しました。",
      };
      publish();
      if (apiError?.code.includes("insufficient")) {
        break;
      }
    }
  }
  return rows;
}

function newBatchCharacterRow(): BatchCharacterRow {
  return {
    id: crypto.randomUUID(),
    name: "",
    appearance: "",
    characterId: "",
    status: "draft",
    generateRequested: false,
    error: "",
  };
}

export function assetDateRangeError(createdAfter: string, createdBefore: string): string {
  return createdAfter && createdBefore && createdAfter > createdBefore
    ? "開始日は終了日以前の日付を選んでください。"
    : "";
}

function AssetLibrary({
  studio,
  onSelect,
}: {
  studio: StudioBootstrap;
  onSelect: (asset: Asset) => void;
}) {
  const [filters, setFilters] = useState({
    asset_type: "",
    page_id: "",
    panel_id: "",
    character_id: "",
    outfit_id: "",
    scene_id: "",
    favorite: "",
    use_status: "",
    created_after: "",
    created_before: "",
    keyword: "",
  });
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({});
  const [assets, setAssets] = useState<Asset[]>([]);
  const [nextOffset, setNextOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const fetchPage = useCallback(async (offset: number, append: boolean) => {
    setLoading(true);
    setMessage("");
    try {
      const result = await loadAssets(studio, appliedFilters, offset);
      setAssets((current) => append ? [...current, ...result.assets] : result.assets);
      setNextOffset(result.nextOffset);
      setHasMore(result.hasMore);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "素材を読み込めませんでした。");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, studio]);
  useEffect(() => {
    void fetchPage(0, false);
  }, [fetchPage]);
  useEffect(() => {
    const refresh = () => void fetchPage(0, false);
    window.addEventListener("comic:asset-changed", refresh);
    return () => window.removeEventListener("comic:asset-changed", refresh);
  }, [fetchPage]);
  const updateFilter = (key: keyof typeof filters, value: string) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };
  const pages = studio.chapters.flatMap((chapter) =>
    chapter.pages.map((page) => ({ ...page, chapterTitle: chapter.title }))
  );
  const selectedCharacter = studio.characters.find(
    (character) => character.id === filters.character_id,
  );
  return (
    <div className="comic-asset-library">
      <div className="comic-asset-filters">
        <label>
          <span>種類</span>
          <select value={filters.asset_type} onChange={(event) => updateFilter("asset_type", event.target.value)}>
          <option value="">すべて</option>
          <option value="upload">アップロード</option>
          <option value="character_reference">キャラクター参考</option>
          <option value="scene_reference">シーン参考</option>
          <option value="panel_candidate">コマ候補</option>
          <option value="page_export">ページ書き出し</option>
          <option value="project_export">作品書き出し</option>
          </select>
        </label>
        <label className="comic-asset-filters__keyword"><span>キーワード</span><input value={filters.keyword} maxLength={100} placeholder="ファイル名・タグ・生成内容" onChange={(event) => updateFilter("keyword", event.target.value)} /></label>
        <fieldset className="comic-asset-filters__advanced">
          <legend>ページ・人物・日付などで絞り込む</legend>
          <p className="comic-inspector-hint">キャラクター・衣装・シーンは、参考画像や素材の登録情報で絞り込みます。該当する人物・場面を含む全コマの検索ではありません。</p>
          <div>
        <label>
          <span>ページ</span>
          <select value={filters.page_id} onChange={(event) => {
            updateFilter("page_id", event.target.value);
            updateFilter("panel_id", "");
          }}>
            <option value="">すべて</option>
            {pages.map((page) => <option key={page.id} value={page.id}>{page.chapterTitle} / P{page.order}</option>)}
          </select>
        </label>
        <label>
          <span>コマ</span>
          <select
            value={filters.panel_id}
            disabled={Boolean(filters.page_id && filters.page_id !== studio.active_page?.id)}
            onChange={(event) => updateFilter("panel_id", event.target.value)}
          >
            <option value="">すべて</option>
            {(studio.active_page?.panels ?? []).map((panel) => <option key={panel.id} value={panel.id}>コマ {panel.order}</option>)}
          </select>
        </label>
        <label>
          <span>キャラクター</span>
          <select value={filters.character_id} onChange={(event) => {
            updateFilter("character_id", event.target.value);
            updateFilter("outfit_id", "");
          }}>
            <option value="">すべて</option>
            {studio.characters.map((character) => <option key={character.id} value={character.id}>{character.name}</option>)}
          </select>
        </label>
        <label>
          <span>衣装</span>
          <select value={filters.outfit_id} disabled={!selectedCharacter} onChange={(event) => updateFilter("outfit_id", event.target.value)}>
            <option value="">すべて</option>
            {selectedCharacter?.profile.outfits.map((outfit) => <option key={outfit.id} value={outfit.id}>{outfit.name}</option>)}
          </select>
        </label>
        <label>
          <span>シーン</span>
          <select value={filters.scene_id} onChange={(event) => updateFilter("scene_id", event.target.value)}>
            <option value="">すべて</option>
            {studio.project_data?.scene_library.map((scene) => <option key={scene.id} value={scene.id}>{scene.name}</option>)}
          </select>
        </label>
        <label>
          <span>お気に入り</span>
          <select value={filters.favorite} onChange={(event) => updateFilter("favorite", event.target.value)}>
            <option value="">すべて</option>
            <option value="true">お気に入り</option>
            <option value="false">未登録</option>
          </select>
        </label>
        <label>
          <span>使用状態</span>
          <select value={filters.use_status} onChange={(event) => updateFilter("use_status", event.target.value)}>
            <option value="">すべて</option>
            <option value="used">使用中</option>
            <option value="unused">未使用</option>
          </select>
        </label>
        <label><span>開始日</span><input type="date" value={filters.created_after} onChange={(event) => updateFilter("created_after", event.target.value)} /></label>
        <label><span>終了日</span><input type="date" value={filters.created_before} onChange={(event) => updateFilter("created_before", event.target.value)} /></label>
          </div>
        </fieldset>
        <div className="comic-asset-filters__actions">
          <button type="button" disabled={loading} onClick={() => {
            const dateError = assetDateRangeError(filters.created_after, filters.created_before);
            if (dateError) {
              setMessage(dateError);
              return;
            }
            setAppliedFilters({
            ...filters,
            created_after: filters.created_after ? `${filters.created_after}T00:00:00` : "",
            created_before: filters.created_before ? `${filters.created_before}T23:59:59` : "",
            });
          }}>絞り込む</button>
          <button type="button" disabled={loading} onClick={() => {
            const empty = { asset_type: "", page_id: "", panel_id: "", character_id: "", outfit_id: "", scene_id: "", favorite: "", use_status: "", created_after: "", created_before: "", keyword: "" };
            setFilters(empty);
            setAppliedFilters({});
          }}>クリア</button>
        </div>
      </div>
      {message && <p className="comic-form-error">{message}</p>}
      <div className="comic-asset-grid">
        {assets.map((asset) => (
          <button type="button" className="comic-asset-thumb" key={asset.id} onClick={() => onSelect(asset)}>
            {asset.content_type.startsWith("image/")
              ? <img src={asset.download_url} alt="" />
              : <strong>{asset.content_type === "application/pdf" ? "PDF" : "ZIP"}</strong>}
            <span>{assetTypeLabel(asset.asset_type)}</span>
            <small>{asset.is_favorite ? "★ " : ""}{asset.is_in_use ? `使用中: ${asset.usage_types.map((type) => ({ panel: "コマ", character: "人物", outfit: "衣装", scene: "背景" }[type] ?? type)).join(" / ")}` : "未使用"}</small>
          </button>
        ))}
        {!loading && assets.length === 0 && (
          <p className="comic-empty-state">この種類の素材はまだありません。</p>
        )}
      </div>
      {hasMore && (
        <button
          type="button"
          className="comic-button comic-button--quiet"
          disabled={loading}
          onClick={() => void fetchPage(nextOffset, true)}
        >
          {loading ? "読み込み中…" : "さらに読み込む"}
        </button>
      )}
    </div>
  );
}

function AssetActionDialog({
  asset,
  model,
  busy,
  errorMessage = "",
  onUsePanel,
  onUseCharacter,
  onUseOutfit,
  onUseScene,
  onCreateVariant,
  onToggleFavorite,
  onDelete,
  onClose,
}: {
  asset: Asset;
  model: EditorModel;
  busy: boolean;
  errorMessage?: string;
  onUsePanel: () => void;
  onUseCharacter: (characterId: string) => void;
  onUseOutfit: (characterId: string, outfitId: string) => void;
  onUseScene: (sceneId: string) => void;
  onCreateVariant: () => void;
  onToggleFavorite: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [characterId, setCharacterId] = useState(model.studio.characters[0]?.id ?? "");
  const outfitOptions = model.studio.characters.flatMap((character) =>
    character.profile.outfits.map((outfit) => ({
      value: `${character.id}|${outfit.id}`,
      label: `${character.name} / ${outfit.name}`,
    }))
  );
  const [outfitTarget, setOutfitTarget] = useState(outfitOptions[0]?.value ?? "");
  const [sceneId, setSceneId] = useState(model.studio.project_data?.scene_library[0]?.id ?? "");
  const targetPanel = selectedPanel(model);
  const targetChapter = model.studio.chapters.find((chapter) => chapter.id === model.page.chapter_id);
  const isDownloadFile = !asset.content_type.startsWith("image/") || asset.asset_type === "page_export";
  const [downloadError, setDownloadError] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<"ready" | "downloading" | "started">("ready");
  const download = () => {
    setDownloadError("");
    setDownloadStatus("downloading");
    void downloadAsset(model.studio, asset.id, asset.filename)
      .then(() => setDownloadStatus("started"))
      .catch((error: Error) => {
        setDownloadStatus("ready");
        setDownloadError(error.message);
      });
  };
  return (
    <div className="comic-storyboard-overlay" role="dialog" aria-modal="true" aria-label="素材の使い道">
      <section className="comic-entity-dialog comic-asset-action-dialog">
        <header>
          <div><p className="comic-kicker">ASSET</p><h3>この素材を使う</h3></div>
          <button type="button" onClick={onClose} aria-label="閉じる">×</button>
        </header>
        {downloadError && <p role="alert">{downloadError}</p>}
        {isDownloadFile ? (
          <>
            <p className="comic-inspector-hint">
              書き出し完了：<strong>{asset.filename}</strong><br />
              ダウンロード状態：{
                downloadStatus === "downloading"
                  ? "準備中…"
                  : downloadStatus === "started"
                    ? "開始しました"
                    : "開始待ち"
              }
            </p>
            <button type="button" className="comic-button comic-button--primary" disabled={downloadStatus === "downloading"} onClick={download}>
              {downloadStatus === "downloading"
                ? "ダウンロードを準備中…"
                : `${asset.content_type === "application/pdf" ? "PDF" : asset.content_type === "application/zip" ? "ZIP" : "PNG"} をダウンロード`}
            </button>
            <a href={asset.download_url} download={asset.filename} target="_blank" rel="noopener noreferrer">
              自動ダウンロードが始まらない場合はこちら
            </a>
          </>
        ) : (
          <>
            {targetPanel && <p className="comic-asset-target" aria-label="対象のコマ">
              コマ画像・似た画像の対象：<strong>{targetChapter ? `第${targetChapter.order}章「${targetChapter.title}」 / ` : ""}ページ {model.page.order} / コマ {targetPanel.order}</strong>
            </p>}
            <img src={asset.download_url} alt="" />
            <button type="button" className="comic-button comic-button--quiet" onClick={download}>原図をダウンロード</button>
            <button type="button" className="comic-button comic-button--primary" disabled={busy || !selectedPanel(model)} onClick={onUsePanel}>
              選択中のコマ画像にする
            </button>
            <button type="button" className="comic-button comic-button--quiet" disabled={busy || !selectedPanel(model) || model.studio.usable_credits < model.studio.operation_credits.similar_version} onClick={onCreateVariant}>
              似た画像を1枚作る（{model.studio.operation_credits.similar_version}クレジット）
            </button>
            {!selectedPanel(model) && (
              <p className="comic-inspector-hint">
                コマに使うには、「ページ」でコマを選んでから、この素材を開いてください。
              </p>
            )}
            <div className="comic-asset-target-row">
              <select aria-label="キャラクター" value={characterId} onChange={(event) => setCharacterId(event.target.value)}>
                <option value="">キャラクターを選択</option>
                {model.studio.characters.map((character) => <option key={character.id} value={character.id}>{character.name}</option>)}
              </select>
              <button type="button" disabled={busy || !characterId} onClick={() => onUseCharacter(characterId)}>主参考に設定</button>
            </div>
            <div className="comic-asset-target-row">
              <select aria-label="衣装" value={outfitTarget} onChange={(event) => setOutfitTarget(event.target.value)}>
                <option value="">衣装を選択</option>
                {outfitOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <button
                type="button"
                disabled={busy || !outfitTarget}
                onClick={() => {
                  const [targetCharacterId, outfitId] = outfitTarget.split("|");
                  onUseOutfit(targetCharacterId, outfitId);
                }}
              >衣装参考に設定</button>
            </div>
            <div className="comic-asset-target-row">
              <select aria-label="シーン" value={sceneId} onChange={(event) => setSceneId(event.target.value)}>
                <option value="">シーンを選択</option>
                {model.studio.project_data?.scene_library.map((scene) => <option key={scene.id} value={scene.id}>{scene.name}</option>)}
              </select>
              <button type="button" disabled={busy || !sceneId} onClick={() => onUseScene(sceneId)}>シーン参考に設定</button>
            </div>
          </>
        )}
        {errorMessage && <p className="comic-dialog-feedback" role="alert">{errorMessage}</p>}
        <div className="comic-asset-dialog-actions">
          <button type="button" disabled={busy} onClick={onToggleFavorite}>
            {asset.is_favorite ? "★ お気に入りを解除" : "☆ お気に入り"}
          </button>
          <button type="button" disabled={busy || asset.is_in_use} onClick={onDelete}>
            素材を削除
          </button>
        </div>
        {asset.is_in_use && <p className="comic-inspector-hint">使用中の素材は、参照を外すまで削除できません。</p>}
      </section>
    </div>
  );
}

function emptyCharacterProfile(): Character["profile"] {
  return {
    schema_version: 1,
    role: "",
    appearance: {
      description: "",
      hair_style: "",
      hair_color: "",
      eyes: "",
      body_type: "",
      age_impression: "",
      palette: [],
      accessories: [],
    },
    personality: "",
    speech_style: "",
    common_expressions: [],
    outfits: [],
    default_outfit_id: "",
    primary_reference_asset_id: "",
    additional_reference_asset_ids: [],
  };
}

function joinList(values: string[]): string {
  return values.join("、");
}

function splitList(value: string): string[] {
  return value
    .split(/[、,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function isReusableImageAsset(asset: Asset): boolean {
  return (
    asset.content_type.startsWith("image/") &&
    asset.asset_type !== "page_export" &&
    asset.asset_type !== "project_export"
  );
}

interface ProjectSettingsDraft {
  title: string;
  projectData: ProjectData;
}

function ProjectSettingsDialog({
  draft,
  busy,
  errorMessage = "",
  onChange,
  onSave,
  onClose,
}: {
  draft: ProjectSettingsDraft;
  busy: boolean;
  errorMessage?: string;
  onChange: (draft: ProjectSettingsDraft) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const updateProjectData = (updates: Partial<ProjectData>) =>
    onChange({ ...draft, projectData: { ...draft.projectData, ...updates } });
  const style = draft.projectData.default_style;
  const source = draft.projectData.story_source;
  return (
    <div className="comic-storyboard-overlay" role="dialog" aria-modal="true" aria-label="作品設定">
      <section className="comic-entity-dialog">
        <header>
          <div>
            <p className="comic-kicker">PROJECT</p>
            <h3>作品設定</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="閉じる">×</button>
        </header>
        <Field
          label="作品名"
          value={draft.title}
          onChange={(title) => onChange({ ...draft, title })}
        />
        <Field
          label="作品概要・世界観"
          value={draft.projectData.summary}
          textarea
          onChange={(summary) => updateProjectData({ summary })}
        />
        <p className="comic-inspector-hint">
          物語の舞台や時代、作品を通して変えたくない設定を書いてください。
        </p>
        <Field
          label="漫画タイプ・ジャンル"
          value={draft.projectData.genre}
          onChange={(genre) => updateProjectData({ genre })}
        />
        <Field
          label="対象読者"
          value={draft.projectData.target_reader}
          onChange={(target_reader) => updateProjectData({ target_reader })}
        />
        <label className="comic-inspector-field">
          <span>作品内テキストの言語</span>
          <select
            value={
              OUTPUT_LANGUAGE_OPTIONS.some(([code]) => code === draft.projectData.output_language)
                ? draft.projectData.output_language
                : "custom"
            }
            onChange={(event) =>
              updateProjectData({
                output_language: event.target.value === "custom" ? "" : event.target.value,
              })
            }
          >
            {OUTPUT_LANGUAGE_OPTIONS.map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
            <option value="custom">その他の言語</option>
          </select>
          {!OUTPUT_LANGUAGE_OPTIONS.some(
            ([code]) => code === draft.projectData.output_language,
          ) && (
            <input
              type="text"
              maxLength={50}
              value={draft.projectData.output_language}
              placeholder="例：it / Italiano"
              onChange={(event) =>
                updateProjectData({ output_language: event.target.value })
              }
            />
          )}
          <small>
            AIが作るタイトル・セリフ・ナレーション・効果音に使います。入力素材は何語でも構いません。
          </small>
        </label>
        <label className="comic-inspector-field">
          <span>仕上がり</span>
          <select
            value={draft.projectData.color_mode}
            onChange={(event) =>
              updateProjectData({
                color_mode: event.target.value as ProjectData["color_mode"],
              })
            }
          >
            <option value="monochrome">モノクロ</option>
            <option value="color">カラー</option>
          </select>
        </label>
        <label className="comic-inspector-field">
          <span>新規ページの読み方向</span>
          <select
            value={draft.projectData.reading_direction}
            onChange={(event) =>
              updateProjectData({
                reading_direction: event.target.value as ProjectData["reading_direction"],
              })
            }
          >
            <option value="rtl">右から左</option>
            <option value="ltr">左から右</option>
            <option value="webtoon">Webtoon</option>
          </select>
        </label>
        <label className="comic-inspector-field">
          <span>新規ページの形式</span>
          <select
            value={draft.projectData.page_preset}
            onChange={(event) =>
              updateProjectData({
                page_preset: event.target.value as ProjectData["page_preset"],
              })
            }
          >
            <option value="b5">B5 漫画</option>
            <option value="a4">A4</option>
            <option value="square">正方形</option>
            <option value="webtoon">Webtoon 縦長</option>
          </select>
        </label>
        <Field
          label="基本画風"
          value={style.name}
          onChange={(name) =>
            updateProjectData({ default_style: { ...style, name } })
          }
        />
        <Field
          label="画風の説明"
          value={style.description}
          textarea
          onChange={(description) =>
            updateProjectData({ default_style: { ...style, description } })
          }
        />
        <Field
          label="避けたい表現"
          value={style.negative_prompt}
          textarea
          onChange={(negative_prompt) =>
            updateProjectData({ default_style: { ...style, negative_prompt } })
          }
        />
        <label className="comic-inspector-field">
          <span>原稿・素材の種類</span>
          <select
            value={source.type}
            onChange={(event) =>
              updateProjectData({
                story_source: {
                  ...source,
                  type: event.target.value as ProjectData["story_source"]["type"],
                },
              })
            }
          >
            <option value="story">物語・あらすじ</option>
            <option value="chapter_summary">章の概要</option>
            <option value="scene">短い場面</option>
            <option value="dialogue">キャラクターの会話</option>
            <option value="script">脚本</option>
            <option value="character_first">キャラクター・参考画像</option>
            <option value="storyboard">下書き案</option>
            <option value="manual">手動制作</option>
          </select>
        </label>
        <Field
          label="原稿・素材内容"
          value={source.text}
          textarea
          onChange={(text) =>
            updateProjectData({ story_source: { ...source, text } })
          }
        />
        <label className="comic-inspector-field">
          <span>想定ページ数</span>
          <input
            type="number"
            min="1"
            max="200"
            value={source.expected_pages ?? 1}
            onChange={(event) =>
              updateProjectData({
                story_source: {
                  ...source,
                  expected_pages: Math.max(1, Math.min(200, Number(event.target.value) || 1)),
                },
              })
            }
          />
        </label>
        {errorMessage && <p className="comic-dialog-feedback" role="alert">{errorMessage}</p>}
        <footer>
          <button type="button" className="comic-button comic-button--quiet" onClick={onClose}>
            キャンセル
          </button>
          <button
            type="button"
            className="comic-button comic-button--primary"
            disabled={
              busy
              || !draft.title.trim()
              || draft.projectData.output_language.trim().length < 2
            }
            onClick={onSave}
          >
            {busy ? "保存中…" : "保存"}
          </button>
        </footer>
      </section>
    </div>
  );
}

export function CharacterDialog({
  draft,
  assets,
  operationCredits,
  busy,
  generationPhase = "idle",
  errorMessage = "",
  onChange,
  onSave,
  onGenerate,
  onSetPrimary,
  onDelete,
  onClose,
}: {
  draft: CharacterDraft;
  assets: Asset[];
  operationCredits: Record<string, number>;
  busy: boolean;
  generationPhase?: CharacterGenerationPhase;
  errorMessage?: string;
  onChange: (draft: CharacterDraft) => void;
  onSave: () => void;
  onGenerate: (sourceAssetIds: string[]) => void;
  onSetPrimary: (assetId: string) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [generationSources, setGenerationSources] = useState<string[]>([]);
  const characterSheetCredits = generationSources.length >= 2
    ? operationCredits.character_sheet_generate_complex
    : operationCredits.character_sheet_generate;
  const updateProfile = (updates: Partial<Character["profile"]>) =>
    onChange({ ...draft, profile: { ...draft.profile, ...updates } });
  const appearance = draft.profile.appearance;
  const updateAppearance = (updates: Partial<typeof appearance>) =>
    updateProfile({ appearance: { ...appearance, ...updates } });
  const updateOutfit = (
    outfitId: string,
    updates: Partial<Character["profile"]["outfits"][number]>,
  ) => {
    updateProfile({
      outfits: draft.profile.outfits.map((outfit) =>
        outfit.id === outfitId ? { ...outfit, ...updates } : outfit
      ),
    });
  };
  const generationButtonLabel = generationPhase === "saving"
    ? "人物設定を保存中…"
    : generationPhase === "generating"
      ? "画像を生成中…"
      : busy
        ? "処理中…"
        : "設定画を生成";
  const addOutfit = () => {
    const id = crypto.randomUUID();
    updateProfile({
      outfits: [
        ...draft.profile.outfits,
        {
          id,
          name: "新しい衣装",
          description: "",
          palette: [],
          accessories: [],
          hair_change: "",
          reference_asset_ids: [],
        },
      ],
      default_outfit_id: draft.profile.default_outfit_id || id,
    });
  };
  const removeOutfit = (outfitId: string) => {
    const outfits = draft.profile.outfits.filter((outfit) => outfit.id !== outfitId);
    updateProfile({
      outfits,
      default_outfit_id:
        draft.profile.default_outfit_id === outfitId
          ? (outfits[0]?.id ?? "")
          : draft.profile.default_outfit_id,
    });
  };
  return (
    <div className="comic-storyboard-overlay" role="dialog" aria-modal="true" aria-label="キャラクター編集">
      <section className="comic-entity-dialog">
        <header>
          <div>
            <p className="comic-kicker">CHARACTER</p>
            <h3>{draft.source ? "キャラクターを編集" : "キャラクターを追加"}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="閉じる">×</button>
        </header>
        <Field label="名前" value={draft.name} onChange={(name) => onChange({ ...draft, name })} />
        <p className="comic-inspector-hint">名前は必須です。保存すると、コマの人物に選べます。</p>
        <Field
          label="役割"
          value={draft.profile.role}
          onChange={(role) => updateProfile({ role })}
        />
        <Field
          label="外見の要約"
          value={appearance.description}
          textarea
          onChange={(description) => updateAppearance({ description })}
        />
        <Field label="髪型" value={appearance.hair_style} onChange={(hair_style) => updateAppearance({ hair_style })} />
        <Field label="髪色" value={appearance.hair_color} onChange={(hair_color) => updateAppearance({ hair_color })} />
        <Field label="目・目つき" value={appearance.eyes} onChange={(eyes) => updateAppearance({ eyes })} />
        <Field label="体格" value={appearance.body_type} onChange={(body_type) => updateAppearance({ body_type })} />
        <Field
          label="見た目年齢"
          value={appearance.age_impression}
          onChange={(age_impression) => updateAppearance({ age_impression })}
        />
        <Field
          label="基本色（読点区切り）"
          value={joinList(appearance.palette)}
          onChange={(value) => updateAppearance({ palette: splitList(value) })}
        />
        <Field
          label="固定アクセサリー（読点区切り）"
          value={joinList(appearance.accessories)}
          onChange={(value) => updateAppearance({ accessories: splitList(value) })}
        />
        <Field
          label="性格・行動傾向"
          value={draft.profile.personality}
          textarea
          onChange={(personality) => updateProfile({ personality })}
        />
        <Field
          label="話し方"
          value={draft.profile.speech_style}
          textarea
          onChange={(speech_style) => updateProfile({ speech_style })}
        />
        <Field
          label="よく使う表情（読点区切り）"
          value={joinList(draft.profile.common_expressions)}
          onChange={(value) => updateProfile({ common_expressions: splitList(value) })}
        />
        <label className="comic-inspector-field">
          <span>主な参考画像</span>
          <select
            value={draft.profile.primary_reference_asset_id}
            onChange={(event) => {
              const assetId = event.target.value;
              updateProfile({
                primary_reference_asset_id: assetId,
                additional_reference_asset_ids:
                  draft.profile.additional_reference_asset_ids.filter(
                    (referenceId) => referenceId !== assetId,
                  ),
              });
            }}
          >
            <option value="">設定しない</option>
            {assets
              .filter(isReusableImageAsset)
              .map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {assetTypeLabel(asset.asset_type)} · {asset.id.slice(0, 8)}
                </option>
              ))}
          </select>
        </label>
        <fieldset className="comic-character-outfit">
          <legend>生成に使う補助参考（任意）</legend>
          <p>過去の主参考は自動では使いません。必要な別角度だけ選んでください。</p>
          <div className="comic-candidate-grid">{assets.filter((asset) => asset.content_type.startsWith("image/") && asset.id !== draft.profile.primary_reference_asset_id).map((asset) => <label key={asset.id}>
            <img src={asset.download_url} alt="補助参考" style={{width:"100%",maxHeight:100,objectFit:"contain"}} />
            <input type="checkbox" checked={draft.profile.additional_reference_asset_ids.includes(asset.id)} onChange={(event) => updateProfile({additional_reference_asset_ids:event.target.checked ? [...draft.profile.additional_reference_asset_ids,asset.id] : draft.profile.additional_reference_asset_ids.filter((id) => id !== asset.id)})} />使う
          </label>)}</div>
        </fieldset>
        <section className="comic-character-generation">
          <header>
            <div>
              <strong>AIキャラクター設定画</strong>
              <small>正面・側面・背面・表情を1枚に生成します。</small>
            </div>
            <span>{characterSheetCredits} クレジット</span>
          </header>
          <div className="comic-character-generation__sources">
            {assets.filter(isReusableImageAsset).map((asset) => (
              <label key={asset.id}>
                <input
                  type="checkbox"
                  checked={generationSources.includes(asset.id)}
                  disabled={
                    !generationSources.includes(asset.id)
                    && generationSources.length >= 4
                  }
                  onChange={(event) =>
                    setGenerationSources((current) =>
                      event.target.checked
                        ? [...current, asset.id]
                        : current.filter((id) => id !== asset.id)
                    )
                  }
                />
                <img src={asset.download_url} alt="" />
                <span>{assetTypeLabel(asset.asset_type)} · {asset.id.slice(0, 8)}</span>
              </label>
            ))}
          </div>
          <button
            type="button"
            className="comic-button comic-button--primary"
            disabled={busy || !draft.source}
            onClick={() => onGenerate(generationSources)}
          >
            {generationButtonLabel}
          </button>
          {generationPhase === "saving" && (
            <p className="comic-operation-status" role="status">
              人物設定を保存してから、画像生成を始めます。
            </p>
          )}
          {generationPhase === "generating" && (
            <p className="comic-inspector-hint" role="status">
              画像を作っています。完成した候補はこの下に表示され、画面を閉じても「4 素材」から確認できます。
            </p>
          )}
          {generationPhase === "success" && (
            <p className="comic-operation-status" role="status">
              キャラクター設定画が完成しました。内容を確認して、メイン参考に設定できます。
            </p>
          )}
          {generationPhase === "error" && errorMessage && (
            <p className="comic-dialog-feedback" role="alert">{errorMessage}</p>
          )}
          {!draft.source && (
            <p className="comic-inspector-hint">先にキャラクターを保存すると生成できます。</p>
          )}
          {draft.source && (
            <div className="comic-character-generation__results">
              {assets
                .filter(
                  (asset) =>
                    asset.asset_type === "character_reference"
                    && asset.character_id === draft.source?.id
                )
                .map((asset) => (
                  <article key={asset.id}>
                    <img src={asset.download_url} alt={`${draft.name}の設定画`} />
                    <button
                      type="button"
                      className="comic-button comic-button--quiet"
                      disabled={
                        busy
                        || draft.profile.primary_reference_asset_id === asset.id
                      }
                      onClick={() => onSetPrimary(asset.id)}
                    >
                      {draft.profile.primary_reference_asset_id === asset.id
                        ? "メイン参考に設定済み"
                        : "メイン参考に設定"}
                    </button>
                  </article>
                ))}
            </div>
          )}
        </section>
        <div className="comic-character-outfits">
          <header>
            <div>
              <strong>衣装・造型</strong>
              <small>場面ごとの服装と参考画像を固定します。</small>
            </div>
            <button type="button" className="comic-button comic-button--quiet" onClick={addOutfit}>
              ＋ 衣装
            </button>
          </header>
          {draft.profile.outfits.map((outfit, index) => (
            <fieldset className="comic-character-outfit" key={outfit.id}>
              <legend>{outfit.name || `衣装 ${index + 1}`}</legend>
              <Field label="衣装名" value={outfit.name} onChange={(name) => updateOutfit(outfit.id, { name })} />
              <Field
                label="服装の詳細"
                value={outfit.description}
                textarea
                onChange={(description) => updateOutfit(outfit.id, { description })}
              />
              <Field
                label="衣装色（読点区切り）"
                value={joinList(outfit.palette)}
                onChange={(value) => updateOutfit(outfit.id, { palette: splitList(value) })}
              />
              <Field
                label="小物（読点区切り）"
                value={joinList(outfit.accessories)}
                onChange={(value) => updateOutfit(outfit.id, { accessories: splitList(value) })}
              />
              <Field
                label="髪型の変更"
                value={outfit.hair_change}
                onChange={(hair_change) => updateOutfit(outfit.id, { hair_change })}
              />
              <label className="comic-inspector-field">
                <span>衣装参考画像</span>
                <select
                  value={outfit.reference_asset_ids[0] ?? ""}
                  onChange={(event) =>
                    updateOutfit(outfit.id, {
                      reference_asset_ids: event.target.value ? [event.target.value] : [],
                    })
                  }
                >
                  <option value="">設定しない</option>
                  {assets
                    .filter(isReusableImageAsset)
                    .map((asset) => (
                      <option key={asset.id} value={asset.id}>
                        {assetTypeLabel(asset.asset_type)} · {asset.id.slice(0, 8)}
                      </option>
                    ))}
                </select>
              </label>
              <label className="comic-inspector-check">
                <input
                  type="radio"
                  name="default-outfit"
                  checked={draft.profile.default_outfit_id === outfit.id}
                  onChange={() => updateProfile({ default_outfit_id: outfit.id })}
                />
                標準衣装にする
              </label>
              <button
                type="button"
                className="comic-button comic-button--danger"
                onClick={() => removeOutfit(outfit.id)}
              >
                この衣装を削除
              </button>
            </fieldset>
          ))}
        </div>
        <footer>
          {generationPhase !== "error" && errorMessage && (
            <p className="comic-dialog-feedback" role="alert">{errorMessage}</p>
          )}
          {draft.source && (
            <button
              type="button"
              className="comic-button comic-button--danger"
              disabled={busy}
              onClick={onDelete}
            >
              キャラクターを削除
            </button>
          )}
          <button type="button" className="comic-button comic-button--quiet" onClick={onClose}>
            キャンセル
          </button>
          <button
            type="button"
            className="comic-button comic-button--primary"
            disabled={busy || !draft.name.trim()}
            onClick={onSave}
          >
            {generationPhase === "generating" ? "保存" : busy ? "保存中…" : "保存"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function BatchCharacterDialog({
  rows,
  characterSheetCredits,
  busy,
  onChange,
  onSubmit,
  onRetry,
  onClose,
}: {
  rows: BatchCharacterRow[];
  characterSheetCredits: number;
  busy: boolean;
  onChange: (rows: BatchCharacterRow[]) => void;
  onSubmit: (generateSheets: boolean) => void;
  onRetry: (index: number) => void;
  onClose: () => void;
}) {
  const validRows = rows.filter((row) => row.name.trim() && row.appearance.trim());
  const updateRow = (index: number, updates: Partial<BatchCharacterRow>) => {
    onChange(rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...updates } : row));
  };
  const statusLabel = (row: BatchCharacterRow) => {
    const labels: Record<BatchCharacterRow["status"], string> = {
      draft: "未送信",
      creating: "保存中…",
      saved: "保存済み",
      generating: "設定画を生成中…",
      generated: "設定画完成",
      create_failed: "保存失敗",
      generation_failed: "設定画失敗",
    };
    return labels[row.status];
  };
  return (
    <div
      className="comic-storyboard-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="複数キャラクターを追加"
    >
      <section className="comic-entity-dialog comic-batch-characters">
        <header>
          <div>
            <p className="comic-kicker">CHARACTERS</p>
            <h3>複数キャラクターを追加</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="閉じる">×</button>
        </header>
        <p className="comic-inspector-hint">
          名前と外見だけで先に登録できます。衣装や性格は保存後に個別編集してください。
        </p>
        <div className="comic-batch-characters__rows">
          {rows.map((row, index) => (
            <article key={row.id}>
              <strong>キャラクター {index + 1}</strong>
              <input
                value={row.name}
                maxLength={80}
                placeholder="名前"
                disabled={busy || Boolean(row.characterId)}
                onChange={(event) => updateRow(index, { name: event.target.value })}
              />
              <textarea
                value={row.appearance}
                maxLength={1000}
                placeholder="外見の説明（必須）"
                disabled={busy || Boolean(row.characterId)}
                onChange={(event) => updateRow(index, { appearance: event.target.value })}
              />
              <div>
                <span data-status={row.status}>{statusLabel(row)}</span>
                {!row.characterId && rows.length > 1 && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
                  >
                    削除
                  </button>
                )}
                {["create_failed", "generation_failed"].includes(row.status) && (
                  <button type="button" disabled={busy} onClick={() => onRetry(index)}>
                    再試行
                  </button>
                )}
              </div>
              {row.error && <small>{row.error}</small>}
            </article>
          ))}
        </div>
        <button
          type="button"
          className="comic-button comic-button--quiet"
          disabled={busy || rows.length >= 8}
          onClick={() => onChange([...rows, newBatchCharacterRow()])}
        >
          ＋ 行を追加（最大8人）
        </button>
        <p className="comic-inspector-hint">
          設定画を生成する場合の予定：
          {validRows.length * characterSheetCredits} クレジット
        </p>
        <footer>
          <button type="button" className="comic-button comic-button--quiet" onClick={onClose}>
            閉じる
          </button>
          <button
            type="button"
            className="comic-button comic-button--quiet"
            disabled={busy || validRows.length === 0}
            onClick={() => onSubmit(false)}
          >
            キャラクターだけ保存
          </button>
          <button
            type="button"
            className="comic-button comic-button--primary"
            disabled={busy || validRows.length === 0}
            onClick={() => onSubmit(true)}
          >
            保存して設定画を生成
          </button>
        </footer>
      </section>
    </div>
  );
}

interface SceneDraft {
  source: Scene | null;
  name: string;
  description: string;
  time: string;
  light: string;
  weather: string;
  palette: string[];
  fixedObjects: string[];
  characterPositions: string[];
  referenceAssetIds: string[];
}

function SceneDialog({
  draft,
  studio,
  busy,
  errorMessage = "",
  onChange,
  onSave,
  onDelete,
  onClose,
}: {
  draft: SceneDraft;
  studio: StudioBootstrap;
  busy: boolean;
  errorMessage?: string;
  onChange: (draft: SceneDraft) => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [referenceAssets, setReferenceAssets] = useState(studio.recent_assets);
  useEffect(() => {
    let active = true;
    setReferenceAssets(studio.recent_assets);
    const missingIds = (draft.source?.reference_asset_ids ?? []).filter(
      (id) => !studio.recent_assets.some((asset) => asset.id === id),
    );
    if (missingIds.length) {
      void Promise.allSettled(missingIds.map((id) => refreshAsset(studio, id)))
        .then((results) => {
          if (!active) return;
          setReferenceAssets([
            ...studio.recent_assets,
            ...results.flatMap((result) => result.status === "fulfilled" ? [result.value] : []),
          ]);
        });
    }
    return () => { active = false; };
  }, [draft.source, studio]);
  return (
    <div className="comic-storyboard-overlay" role="dialog" aria-modal="true" aria-label="シーン編集">
      <section className="comic-entity-dialog">
        <header>
          <div>
            <p className="comic-kicker">SCENE</p>
            <h3>{draft.source ? "シーンを編集" : "シーンを追加"}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="閉じる">×</button>
        </header>
        <Field label="名前" value={draft.name} onChange={(name) => onChange({ ...draft, name })} />
        <p className="comic-inspector-hint">名前は必須です。保存した場所や時間は、コマの「構図」で選んで使えます。</p>
        <Field
          label="場所・背景の説明"
          value={draft.description}
          textarea
          onChange={(description) => onChange({ ...draft, description })}
        />
        <Field label="時間" value={draft.time} onChange={(time) => onChange({ ...draft, time })} />
        <Field label="光" value={draft.light} onChange={(light) => onChange({ ...draft, light })} />
        <Field
          label="天気"
          value={draft.weather}
          onChange={(weather) => onChange({ ...draft, weather })}
        />
        <Field
          label="主な配色（読点区切り）"
          value={joinList(draft.palette)}
          onChange={(value) => onChange({ ...draft, palette: splitList(value) })}
        />
        <Field
          label="固定する物件（1行につき1件）"
          value={draft.fixedObjects.join("\n")}
          textarea
          onChange={(value) => onChange({
            ...draft,
            fixedObjects: value.split("\n").map((item) => item.trim()).filter(Boolean),
          })}
        />
        <Field
          label="人物の定位置（1行につき1件）"
          value={draft.characterPositions.join("\n")}
          textarea
          onChange={(value) => onChange({
            ...draft,
            characterPositions: value.split("\n").map((item) => item.trim()).filter(Boolean),
          })}
        />
        <fieldset className="comic-character-outfit" disabled={busy}>
          <legend>シーンの参考画像</legend>
          <p className="comic-inspector-hint">保存すると参考から外れます。素材ライブラリの画像は削除されません。</p>
          {draft.referenceAssetIds.length === 0 ? <p>参考画像は設定されていません。</p> : (
            <div className="comic-candidate-grid">
              {draft.referenceAssetIds.map((id, index) => {
                const asset = referenceAssets.find((item) => item.id === id);
                return <div key={id}>
                  {asset ? <img src={asset.download_url} alt={`シーン参考 ${index + 1}`} style={{width:"100%", height:100, objectFit:"contain"}} />
                    : <p>参考画像 {index + 1}（プレビューなし）</p>}
                  <button type="button" className="comic-button comic-button--quiet" aria-label={`参考画像 ${index + 1} を外す`}
                    onClick={() => onChange({...draft, referenceAssetIds:draft.referenceAssetIds.filter((item) => item !== id)})}>
                    参考から外す
                  </button>
                </div>;
              })}
            </div>
          )}
        </fieldset>
        {errorMessage && <p className="comic-dialog-feedback" role="alert">{errorMessage}</p>}
        <footer>
          {draft.source && (
            <button
              type="button"
              className="comic-button comic-button--danger"
              disabled={busy}
              onClick={onDelete}
            >
              シーンを削除
            </button>
          )}
          <button type="button" className="comic-button comic-button--quiet" onClick={onClose}>
            キャンセル
          </button>
          <button
            type="button"
            className="comic-button comic-button--primary"
            disabled={busy || !draft.name.trim()}
            onClick={onSave}
          >
            {busy ? "保存中…" : "保存"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function ProjectExportDialog({
  pageCount,
  busy,
  errorMessage = "",
  onStart,
  onClose,
}: {
  pageCount: number;
  busy: boolean;
  errorMessage?: string;
  onStart: (format: "zip" | "pdf", scale: 1 | 2) => void;
  onClose: () => void;
}) {
  const [format, setFormat] = useState<"zip" | "pdf">("zip");
  const [scale, setScale] = useState<1 | 2>(1);
  return (
    <div className="comic-storyboard-overlay" role="dialog" aria-modal="true" aria-label="作品書き出し">
      <section className="comic-entity-dialog">
        <header>
          <div><p className="comic-kicker">PROJECT EXPORT</p><h3>作品を書き出す</h3></div>
          <button type="button" disabled={busy} onClick={onClose} aria-label="閉じる">×</button>
        </header>
        <label className="comic-inspector-field">
          <span>ファイル形式</span>
          <select value={format} onChange={(event) => setFormat(event.target.value as "zip" | "pdf")}>
            <option value="zip">ZIP（ページごとの PNG）</option>
            <option value="pdf">PDF（全ページを一冊にまとめる）</option>
          </select>
        </label>
        <label className="comic-inspector-field">
          <span>解像度</span>
          <select value={scale} onChange={(event) => setScale(Number(event.target.value) as 1 | 2)}>
            <option value={1}>標準（1×）</option>
            <option value={2}>高解像度（2×）</option>
          </select>
        </label>
        <p className="comic-inspector-hint">
          全 {pageCount} ページを今の章・ページ順で書き出します（予定 {pageCount} クレジット）。完成ファイルは「4 素材」から再ダウンロードできます。
        </p>
        {errorMessage && <p className="comic-dialog-feedback" role="alert">{errorMessage}</p>}
        <footer>
          <button type="button" className="comic-button comic-button--quiet" disabled={busy} onClick={onClose}>
            キャンセル
          </button>
          <button type="button" className="comic-button comic-button--primary" disabled={busy} onClick={() => onStart(format, scale)}>
            {busy ? "書き出しを準備中…" : "書き出しを開始"}
          </button>
        </footer>
      </section>
    </div>
  );
}

type StoryboardSourceType =
  | "story"
  | "chapter_summary"
  | "scene"
  | "dialogue"
  | "script"
  | "storyboard";

interface StoryboardGenerationDraft {
  polishDialogue: boolean;
  sourceType: StoryboardSourceType;
  storyText: string;
  expectedPages: number;
  layoutKey: string;
}

export function createStoryboardGenerationDraft(
  source: ProjectData["story_source"],
  layoutKey: string,
): StoryboardGenerationDraft {
  const supportedTypes: StoryboardSourceType[] = [
    "story",
    "chapter_summary",
    "scene",
    "dialogue",
    "script",
    "storyboard",
  ];
  return {
    sourceType: supportedTypes.includes(source.type as StoryboardSourceType)
      ? source.type as StoryboardSourceType
      : "story",
    storyText: source.text,
    polishDialogue: false,
    expectedPages: 1,
    layoutKey,
  };
}

export function mergeStoryboardGenerationSource(
  source: ProjectData["story_source"],
  draft: StoryboardGenerationDraft,
): ProjectData["story_source"] {
  return {
    type: draft.sourceType,
    text: draft.storyText,
    expected_pages: source.expected_pages,
  };
}

export function StoryboardGenerationDialog({
  draft,
  layouts,
  outputLanguage,
  operationCredits,
  busy,
  errorMessage = "",
  onChange,
  onStart,
  onClose,
}: {
  draft: StoryboardGenerationDraft;
  layouts: LayoutOption[];
  outputLanguage: string;
  operationCredits: Record<string, number>;
  busy: boolean;
  errorMessage?: string;
  onChange: (draft: StoryboardGenerationDraft) => void;
  onStart: () => void;
  onClose: () => void;
}) {
  const selectedPreset = STORYBOARD_PAGE_PRESETS.find((preset) => preset.pages === draft.expectedPages);
  const configuredBaseCredits = operationCredits.storyboard;
  const baseCredits = (
    typeof configuredBaseCredits === "number"
    && Number.isFinite(configuredBaseCredits)
    && configuredBaseCredits > 0
  )
    ? configuredBaseCredits
    : 5;
  const expectedPages = (
    typeof draft.expectedPages === "number"
    && Number.isFinite(draft.expectedPages)
    && draft.expectedPages >= 1
  )
    ? Math.max(1, Math.min(30, Math.floor(draft.expectedPages)))
    : 1;
  const selectedLayout = layouts.find((layout) => layout.key === draft.layoutKey);
  const selectedPanelCount = selectedLayout?.panel_rects?.length ?? 0;
  const panelsPerPage = selectedPanelCount > 0
    ? selectedPanelCount
    : 4;
  const estimatedPanels = Math.max(1, expectedPages * panelsPerPage);
  const estimatedCredits = baseCredits + Math.ceil(estimatedPanels / 2);
  const setExpectedPages = (expectedPages: number) =>
    onChange({ ...draft, expectedPages: Math.max(1, Math.min(30, expectedPages)) });
  return (
    <div className="comic-storyboard-overlay" role="dialog" aria-modal="true" aria-label="AI下書き生成">
      <section className="comic-entity-dialog">
        <header>
          <div><p className="comic-kicker">下書きの確認</p><h3>漫画の下書きを作る</h3></div>
          <button type="button" disabled={busy} onClick={onClose} aria-label="閉じる">×</button>
        </header>
        <label className="comic-inspector-field">
          <span>入力内容の種類</span>
          <select
            value={draft.sourceType}
            onChange={(event) => onChange({ ...draft, sourceType: event.target.value as StoryboardSourceType })}
          >
            <option value="story">物語・あらすじ</option>
            <option value="chapter_summary">章の概要</option>
            <option value="scene">場面の説明</option>
            <option value="dialogue">会話・セリフ</option>
            <option value="script">脚本</option>
            <option value="storyboard">既存の下書き案</option>
          </select>
        </label>
        <Field
          label="内容"
          value={draft.storyText}
          textarea
          onChange={(storyText) => onChange({ ...draft, storyText })}
        />
        <p className="comic-inspector-hint">あらすじや会話を10文字以上で入力してください。新しく作るセリフの言語は「{outputLanguageLabel(outputLanguage)}」です。</p>
        <label className="comic-inspector-check">
          <input type="checkbox" checked={draft.polishDialogue} onChange={(event) => onChange({ ...draft, polishDialogue: event.target.checked })} />
          セリフをAIで読みやすく整える
        </label>
        <p className="comic-inspector-hint">オフなら原文を使い、オンなら意味や話し手、言語を変えずに表現を整えます。</p>
        <fieldset className="comic-inspector-group">
          <legend>作りたい長さ</legend>
          <div role="group" aria-label="ページ数プリセット">
            {STORYBOARD_PAGE_PRESETS.map((preset) => (
              <button
                key={preset.pages}
                type="button"
                className="comic-button comic-button--quiet"
                aria-pressed={selectedPreset?.pages === preset.pages}
                disabled={busy}
                onClick={() => setExpectedPages(preset.pages)}
              >
                {preset.label} · {preset.description}
              </button>
            ))}
          </div>
          <p className="comic-inspector-hint">初めてなら、内容を確認しやすい1〜8ページがおすすめです。</p>
          <fieldset className="comic-inspector-group">
            <legend>ページ数を細かく指定（1〜30）</legend>
            <label className="comic-inspector-field">
              <span>カスタムページ数</span>
              <input
                type="number"
                min={1}
                max={30}
                value={draft.expectedPages}
                disabled={busy}
                onChange={(event) => setExpectedPages(Number(event.target.value) || 1)}
              />
            </label>
          </fieldset>
          {draft.expectedPages > 15 && (
            <p className="comic-inspector-hint" role="alert">
              15ページを超えると時間がかかる場合がありますが、画面を閉じても生成は続きます。
            </p>
          )}
        </fieldset>
        <label className="comic-inspector-field">
          <span>基本レイアウト</span>
          <select
            value={draft.layoutKey}
            onChange={(event) => onChange({ ...draft, layoutKey: event.target.value })}
          >
            {layouts.map((layout) => <option key={layout.key} value={layout.key}>{layout.label}</option>)}
          </select>
        </label>
        <p className="comic-inspector-hint">生成後にコマやセリフを確認・修正してから、漫画に追加できます。</p>
        <p className="comic-inspector-hint">
          予定は{expectedPages}ページ×{panelsPerPage}コマ、計{estimatedPanels}コマで、{estimatedCredits}クレジットです（基本{baseCredits}＋2コマごとに1、端数切り上げ）。コマ数が少なければ差額を返し、表示額を超えて請求しません。
        </p>
        {errorMessage && <p className="comic-dialog-feedback" role="alert">{errorMessage}</p>}
        <footer>
          <button type="button" className="comic-button comic-button--quiet" disabled={busy} onClick={onClose}>
            キャンセル
          </button>
          <button
            type="button"
            className="comic-button comic-button--primary"
            disabled={busy || draft.storyText.trim().length < 10}
            onClick={onStart}
          >
            {busy ? "下書きを生成中…" : "下書き案を生成"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function Editor({ bootstrap }: { bootstrap: StudioBootstrap }) {
  const initial = useMemo(() => initialEditorModel(bootstrap), [bootstrap]);
  if (!initial) {
    return null;
  }
  return <ActiveEditor initial={initial} />;
}

type StoryboardReviewState = {
  jobId: string;
  value: StoryboardResult;
  metadata: StoryboardGenerationMetadata | null;
  applied?: boolean;
  draft?: StoryboardDraft;
  production?: ProductionRun;
};

function ActiveEditor({ initial }: { initial: EditorModel }) {
  const [model, reactDispatch] = useReducer(editorReducer, initial);
  const modelRef = useRef(model);
  modelRef.current = model;
  const dispatch = useCallback((action: EditorAction) => {
    modelRef.current = editorReducer(modelRef.current, action);
    reactDispatch(action);
  }, []);
  const [activeJobs, setActiveJobs] = useState<ComicJobSummary[]>(
    initial.studio.recent_jobs.filter((job) => job.status === "processing" && !job.production_run_id),
  );
  const [jobClock, setJobClock] = useState(Date.now());
  const [reopenedAnalysis, setReopenedAnalysis] = useState<ReopenedPanelAnalysis | null>(null);
  const [storyboardReview, setStoryboardReview] = useState<StoryboardReviewState | null>(null);
  const storyboardReviewRef = useRef(storyboardReview);
  storyboardReviewRef.current = storyboardReview;
  const draftSaveInFlight = useRef<Promise<void> | null>(null);
  const productionSyncInFlight = useRef(false);
  const productionSyncKey = useRef("");
  const [storyboardError, setStoryboardError] = useState("");
  const [storyboardAction, setStoryboardAction] = useState<StoryboardAction>(null);
  const [projectSettingsDraft, setProjectSettingsDraft] =
    useState<ProjectSettingsDraft | null>(null);
  const [characterDraft, setCharacterDraft] = useState<CharacterDraft | null>(null);
  const [characterAssets, setCharacterAssets] = useState(initial.studio.recent_assets);
  const [batchCharacterRows, setBatchCharacterRows] =
    useState<BatchCharacterRow[] | null>(null);
  const [batchCharacterOpen, setBatchCharacterOpen] = useState(false);
  const [batchCharacterBusy, setBatchCharacterBusy] = useState(false);
  const [sceneDraft, setSceneDraft] = useState<SceneDraft | null>(null);
  const [assetDraft, setAssetDraft] = useState<Asset | null>(null);
  const [continuousPreview, setContinuousPreview] = useState<ComicPage[] | null>(null);
  const [projectExportOpen, setProjectExportOpen] = useState(false);
  const [storyboardGenerationDraft, setStoryboardGenerationDraft] =
    useState<StoryboardGenerationDraft | null>(null);
  const [entityBusy, setEntityBusy] = useState(false);
  const [entityError, setEntityError] = useState("");
  const [characterGenerationPhase, setCharacterGenerationPhase] =
    useState<CharacterGenerationPhase>("idle");
  const storyboardBusy = storyboardAction !== null;
  const saveTimer = useRef<number | null>(null);
  const saveRetryCount = useRef(0);
  const saveInFlight = useRef<Promise<void> | null>(null);
  const inspector = document.getElementById("comic-inspector-root");
  const assetLibrary = document.getElementById("comic-asset-library-root");
  const unplacedPanelsTray = document.getElementById("comic-unplaced-panels-root");
  const commit = useCallback((state: EditorState) => {
    dispatch({ type: "commit", state });
  }, []);
  const panelSaveQueue = usePanelSaveQueue(model.studio, dispatch);

  const reloadStudio = async (start = "project") => {
    if (batchCharacterBusy) {
      const current = modelRef.current;
      const studio = await loadBootstrap(current.studio.urls.bootstrap, current.studio.active_project?.id);
      dispatch({ type: "refresh-studio", studio });
      setCharacterDraft(null);
      setSceneDraft(null);
      setProjectSettingsDraft(null);
      setEntityBusy(false);
      return;
    }
    const url = new URL(window.location.href);
    const projectId = model.studio.active_project?.id;
    if (projectId) {
      url.searchParams.set("project_id", projectId);
    }
    url.searchParams.set("start", start);
    url.hash = "tool-section";
    if (url.toString() === window.location.href) {
      window.location.reload();
      return;
    }
    window.location.assign(url.toString());
  };

  useEffect(() => {
    if (!batchCharacterBusy) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [batchCharacterBusy]);

  useEffect(() => {
    const jobStatusChanged = (event: Event) => {
      const job = (event as CustomEvent<{ job?: ComicJobSummary }>).detail?.job;
      if (!job || job.production_run_id) {
        return;
      }
      setActiveJobs((current) => {
        const withoutJob = current.filter((item) => item.id !== job.id);
        return job.status === "processing"
          ? [job, ...withoutJob].sort((left, right) =>
              right.created_at.localeCompare(left.created_at)
            )
          : withoutJob;
      });
    };
    const recovered = () => dispatch({type:"message",message:"前回の受付結果を復元しています。変更した設定で新しく作る場合は、前回の完了を確認してから生成してください。"});
    window.addEventListener("comic:submission-recovered", recovered);
    window.addEventListener("comic:job-status-changed", jobStatusChanged);
    return () => {window.removeEventListener("comic:job-status-changed", jobStatusChanged); window.removeEventListener("comic:submission-recovered",recovered);};
  }, []);

  useEffect(() => {
    if (!activeJobs.length) {
      return;
    }
    setJobClock(Date.now());
    const timer = window.setInterval(() => setJobClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [activeJobs.length]);

  useEffect(() => {
    const controller = new AbortController();
    const queryJobId = new URL(window.location.href).searchParams.get("storyboard_job_id");
    const resumedJobs = initial.studio.recent_jobs.filter(
      (job) => job.status === "processing" && job.id !== queryJobId && !job.production_run_id,
    );
    for (const summary of resumedJobs) {
      void waitForTrackedJob(initial.studio, summary.id, controller.signal)
        .then(async (job) => {
          if (job.production_run_id) return;
          if (job.job_type === "comic_storyboard_generate") {
            const review = storyboardReviewFromJob(job);
            if (!review) {
              throw new ComicApiError("下書き生成結果に編集可能なデータがありません。");
            }
            setStoryboardError("");
            setStoryboardReview({ jobId: job.id, ...review });
            dispatch({ type: "message", message: "下書き案が完成しました。内容を確認してください。" });
            return;
          }
          const current = modelRef.current;
          const studio = await loadBootstrap(
            current.studio.urls.bootstrap,
            current.studio.active_project?.id,
          );
          const page = job.resource.page_id === current.page.id
            ? await loadComicPage(current.studio, current.page.id)
            : current.page;
          dispatch({ type: "refresh-studio", studio: { ...studio, active_page: page } });
          dispatch({ type: "message", message: "バックグラウンドの漫画タスクが完了しました。" });
        })
        .catch((error: unknown) => {
          if (error instanceof ComicApiError && error.code === "job_poll_aborted") {
            return;
          }
          dispatch({
            type: "message",
            message: error instanceof Error ? error.message : "漫画タスクの状態を確認できませんでした。",
          });
        });
    }
    return () => controller.abort();
  }, [initial]);

  useEffect(() => {
    if (!characterDraft) {
      return;
    }
    let active = true;
    void loadAssets(model.studio, {}, 0, 100)
      .then((result) => {
        if (active) {
          setCharacterAssets(result.assets);
        }
      })
      .catch(() => {
        if (active) {
          setCharacterAssets(model.studio.recent_assets);
        }
      });
    return () => {
      active = false;
    };
  }, [
    characterDraft !== null,
    characterDraft?.source?.id,
    model.studio.active_project?.id,
  ]);

  useEffect(() => {
    setCharacterGenerationPhase("idle");
  }, [
    characterDraft !== null,
    characterDraft?.source?.id,
  ]);

  useEffect(() => {
    setEntityError("");
  }, [
    characterDraft !== null,
    characterDraft?.source?.id,
    sceneDraft !== null,
    sceneDraft?.source?.id,
    projectSettingsDraft !== null,
    storyboardGenerationDraft !== null,
    projectExportOpen,
    assetDraft?.id,
  ]);

  useEffect(() => {
    notifySaveState(model.saveStatus === "conflict" ? "error" : model.saveStatus);
  }, [model.saveStatus]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("comic:studio-bootstrap-changed", {
        detail: { studio: model.studio },
      }),
    );
  }, [model.studio]);

  useEffect(() => {
    const chapter = model.studio.chapters.find(
      (item) => item.id === model.page.chapter_id,
    );
    const selectedPanelId = model.selection.kind === "panel" ? model.selection.id : null;
    const selectedPanelForContext = selectedPanelId
      ? model.page.panels.find((panel) => panel.id === selectedPanelId)
      : null;
    const selectionLabel = model.selection.kind === "panel"
      ? selectedPanelForContext
        ? `コマ${selectedPanelForContext.order}を編集`
        : "コマを編集"
      : model.selection.kind === "bubble"
        ? "吹き出しを編集"
        : "ページの設定を開く";
    window.dispatchEvent(
      new CustomEvent("comic:studio-context-changed", {
        detail: {
          chapterTitle: chapter?.title ?? "",
          pageOrder: model.page.order,
          selectionLabel,
          usableCredits: model.studio.usable_credits,
        },
      }),
    );
  }, [
    model.page.chapter_id,
    model.page.order,
    model.page.panels,
    model.selection,
    model.studio.chapters,
    model.studio.usable_credits,
  ]);

  const flushPageSave = useCallback(async (): Promise<void> => {
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    if (saveInFlight.current) await saveInFlight.current;
    const current = modelRef.current;
    if (current.saveStatus === "conflict") {
      throw new ComicApiError("別の画面で更新されています。未保存の変更を保持しています。競合を解決してから続けてください。");
    }
    if (!["dirty", "error", "saving"].includes(current.saveStatus)) return;
    const work = (async () => {
      while (["dirty", "error", "saving"].includes(modelRef.current.saveStatus)) {
        const snapshot = modelRef.current;
        dispatch({ type: "save-start" });
        try {
          const result = await saveEditorState(snapshot.studio, snapshot.page, snapshot.state);
          if (modelRef.current.page.id !== snapshot.page.id) {
            throw new ComicApiError("保存中にページが変更されました。");
          }
          dispatch({ type: "save-success", revision: result.revision,
            keepDirty: modelRef.current.state !== snapshot.state });
          saveRetryCount.current = 0;
        } catch (error) {
          dispatch({ type: "save-failure",
            conflict: error instanceof ComicApiError && error.code === "revision_conflict",
            message: error instanceof Error ? error.message : "ページを保存できませんでした。" });
          throw error;
        }
      }
    })();
    saveInFlight.current = work;
    try { await work; } finally { saveInFlight.current = null; }
  }, [dispatch]);

  const flushAllSaves = useCallback(async () => {
    try { await panelSaveQueue.flush(); }
    catch (error) {
      dispatch({type:"save-failure",conflict:false,message:error instanceof Error ? error.message : "コマを保存できませんでした。"});
      throw error;
    }
    await flushPageSave();
  }, [panelSaveQueue.flush, flushPageSave]);

  const syncProduction = useCallback(async (run: ProductionRun) => {
    const key = JSON.stringify([run.id, run.revision, run.status, run.steps]);
    if (productionSyncInFlight.current || productionSyncKey.current === key) return;
    productionSyncInFlight.current = true;
    try {
      const before = modelRef.current;
      if (run.project_id !== before.studio.active_project?.id) return;
      const studio = await loadBootstrap(before.studio.urls.bootstrap, run.project_id);
      const pageExists = studio.chapters.some((chapter) => chapter.pages.some((page) => page.id === before.page.id));
      const page = pageExists ? await loadComicPage(studio, before.page.id) : studio.active_page;
      if (!page) return;
      const current = modelRef.current;
      if (current.page.id !== before.page.id || current.studio.active_project?.id !== run.project_id) return;
      const replaceEditor = !panelSaveQueue.isPending() && !saveInFlight.current
        && !["dirty", "saving", "error", "conflict"].includes(current.saveStatus)
        && current.state === before.state;
      dispatch({ type: "production-refresh", studio: { ...studio, active_page: page }, page, replaceEditor });
      if (replaceEditor) productionSyncKey.current = key;
    } finally { productionSyncInFlight.current = false; }
  }, [dispatch, panelSaveQueue.isPending]);

  const openProductionPanel = useCallback(async (pageId: string, panelId: string) => {
    await flushAllSaves();
    const current = modelRef.current;
    let page = current.page;
    if (page.id !== pageId) {
      page = await loadComicPage(current.studio, pageId);
      if (!page.panels.some((panel) => panel.id === panelId)) {
        throw new ComicApiError("対象コマが見つかりません。ページを確認してから再試行してください。");
      }
      dispatch({ type: "replace-studio", studio: { ...current.studio, active_page: page } });
      window.dispatchEvent(new CustomEvent("comic:page-opened", { detail: { pageId: page.id } }));
    } else if (!page.panels.some((panel) => panel.id === panelId)) {
      throw new ComicApiError("対象コマが見つかりません。ページを確認してから再試行してください。");
    }
    dispatch({ type: "select", selection: { kind: "panel", id: panelId } });
    window.dispatchEvent(new CustomEvent("comic:editor-action", {
      detail: { action: "inspect-panel", pageId, panelId },
    }));
  }, [dispatch, flushAllSaves]);

  const flushStoryboardDraft = useCallback(async () => {
    if (draftSaveInFlight.current) return draftSaveInFlight.current;
    const work = (async () => {
      while (true) {
        const review = storyboardReviewRef.current;
        if (!review?.draft || review.draft.applied_chapter_id
          || JSON.stringify(review.value) === JSON.stringify(review.draft.content)) return;
        const saved = await saveStoryboardDraft(modelRef.current.studio, review.draft, review.value);
        const current = storyboardReviewRef.current;
        if (current?.jobId !== review.jobId) return;
        const updated = {
          ...current,
          draft: saved,
          value: current.value === review.value ? saved.content : current.value,
        };
        storyboardReviewRef.current = updated;
        setStoryboardReview(updated);
      }
    })();
    draftSaveInFlight.current = work;
    try { await work; } finally { draftSaveInFlight.current = null; }
  }, []);

  useEffect(() => {
    if (!storyboardReview?.draft || JSON.stringify(storyboardReview.value) === JSON.stringify(storyboardReview.draft.content)) return;
    const timer = window.setTimeout(() => {
      void flushStoryboardDraft().catch((reason) => setStoryboardError(reason instanceof Error ? reason.message : "下書きを保存できませんでした。変更を保持しています。"));
    }, 600);
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => { window.clearTimeout(timer); window.removeEventListener("beforeunload", warn); };
  }, [storyboardReview?.value, storyboardReview?.draft, flushStoryboardDraft]);

  const reviewProduction = async (run: ProductionRun) => {
    try {
      await flushAllSaves();
      await flushStoryboardDraft();
      const draft = await loadStoryboardDraft(modelRef.current.studio, run.source_job_id);
      const review = { jobId: draft.source_job_id, value: draft.content, metadata: null, draft, production: run };
      storyboardReviewRef.current = review;
      setStoryboardReview(review);
      setStoryboardError("");
    } catch (reason) { dispatch({ type: "message", message: reason instanceof Error ? reason.message : "下書きを開けませんでした。" }); }
  };

  useEffect(() => {
    if (model.saveStatus !== "dirty" && model.saveStatus !== "error") return;
    if (model.saveStatus === "error" && saveRetryCount.current >= 3) return;
    const delay = model.saveStatus === "error" ? 800 * 2 ** saveRetryCount.current : 800;
    saveTimer.current = window.setTimeout(() => {
      void flushAllSaves().catch(() => { saveRetryCount.current += 1; });
    }, delay);
    return () => { if (saveTimer.current !== null) window.clearTimeout(saveTimer.current); };
  }, [model.state, model.saveStatus, flushAllSaves]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (["dirty", "saving", "error", "conflict"].includes(modelRef.current.saveStatus)) event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  useEffect(() => {
    const editorAction = (event: Event) => {
      const action = (event as CustomEvent<{ action?: string }>).detail?.action;
      if (action === "preview") {
        dispatch({ type: "preview", value: true });
      }
      if (action === "continuous-preview") {
        void (async () => {
          try {
            dispatch({ type: "message", message: "作品全体を読み込んでいます…" });
            await flushAllSaves();
            const pages: ComicPage[] = [];
            for (const chapter of model.studio.chapters) {
              for (const page of chapter.pages) {
                pages.push(await loadComicPage(model.studio, page.id));
              }
            }
            setContinuousPreview(pages);
            dispatch({ type: "message", message: "" });
          } catch (error) {
            dispatch({
              type: "message",
              message: error instanceof Error ? error.message : "全体プレビューを読み込めませんでした。",
            });
          }
        })();
      }
      if (action === "export") {
        void flushAllSaves().then(() => exportPage(modelRef.current.studio, modelRef.current.page.id, modelRef.current.page.revision))
          .then(async (jobId) => {
            const completed = await waitForTrackedJob(model.studio, jobId);
            const exportIds = completed.output.asset_ids;
            let exportedAsset: Asset | null = null;
            if (Array.isArray(exportIds) && typeof exportIds[0] === "string") {
              exportedAsset = await refreshAsset(model.studio, exportIds[0], "view");
              setAssetDraft(exportedAsset);
            }
            const [studio, page] = await Promise.all([
              loadBootstrap(model.studio.urls.bootstrap, model.studio.active_project?.id),
              loadComicPage(model.studio, model.page.id),
            ]);
            dispatch({ type: "refresh-studio", studio: { ...studio, active_page: page } });
            dispatch({
              type: "message",
              message: exportedAsset
                ? `${exportedAsset.filename} の書き出しが完了しました。ダウンロード画面を開きました。`
                : "PNG書き出しが完了しました。素材ライブラリに保存しました。",
            });
          })
          .catch((error: unknown) =>
            dispatch({
              type: error instanceof ComicApiError && error.code === "revision_conflict" ? "save-failure" : "message",
              conflict: error instanceof ComicApiError && error.code === "revision_conflict",
              message: error instanceof Error ? error.message : "書き出しを開始できませんでした。",
            }),
          );
      }
      if (action === "project-export") {
        setProjectExportOpen(true);
      }
      if (action === "create-storyboard" && model.studio.project_data) {
        setStoryboardGenerationDraft(
          createStoryboardGenerationDraft(
            model.studio.project_data.story_source,
            model.page.layout_key,
          ),
        );
      }
      if (action === "add-bubble") {
        const panelId = model.selection.kind === "panel" ? model.selection.id : null;
        const bubble = newBubble(panelId, model.studio.project_data?.output_language ?? "ja");
        commit({ ...model.state, bubbles: [...model.state.bubbles, bubble] });
        dispatch({ type: "select", selection: { kind: "bubble", id: bubble.id } });
      }
      if (
        action === "project-settings" &&
        model.studio.active_project &&
        model.studio.project_data
      ) {
        setProjectSettingsDraft({
          title: model.studio.active_project.title,
          projectData: structuredClone(model.studio.project_data),
        });
      }
      if (action === "add-character") {
        setCharacterDraft({
          source: null,
          name: "",
          profile: emptyCharacterProfile(),
        });
      }
      if (action === "batch-characters") {
        if (!batchCharacterBusy) {
          setBatchCharacterRows([newBatchCharacterRow(), newBatchCharacterRow()]);
        }
        setBatchCharacterOpen(true);
      }
      if (action === "add-scene") {
        setSceneDraft({
          source: null,
          name: "",
          description: "",
          time: "",
          light: "",
          weather: "",
          palette: [],
          fixedObjects: [],
          characterPositions: [],
          referenceAssetIds: [],
        });
      }
    };
    const panelUpdated = (event: Event) => {
      const panel = (event as CustomEvent<{ panel?: Panel }>).detail?.panel;
      if (panel) {
        dispatch({ type: "panel-image", panel });
      }
    };
    const pageRequested = (event: Event) => {
      const pageId = (event as CustomEvent<{ pageId?: string }>).detail?.pageId;
      if (!pageId) {
        return;
      }
      if (pageId === model.page.id) {
        window.dispatchEvent(new CustomEvent("comic:page-opened", { detail: { pageId } }));
        return;
      }
      dispatch({ type: "message", message: "ページを読み込んでいます…" });
      void flushAllSaves()
        .then(() => loadComicPage(model.studio, pageId))
        .then(async (page) => {
          const projectData = model.studio.project_data
            ? structuredClone(model.studio.project_data)
            : null;
          const chapter = model.studio.chapters.find((item) =>
            item.pages.some((candidate) => candidate.id === page.id),
          );
          if (projectData) {
            projectData.current_location = {
              chapter_id: chapter?.id ?? "",
              page_id: page.id,
              panel_id: page.panels[0]?.id ?? "",
            };
            await updateProjectData(model.studio, projectData);
          }
          dispatch({
            type: "replace-studio",
            studio: { ...model.studio, project_data: projectData, active_page: page },
          });
          dispatch({ type: "message", message: "" });
          window.dispatchEvent(new CustomEvent("comic:page-opened", { detail: { pageId: page.id } }));
        })
        .catch((error: unknown) =>
          dispatch({
            type: "message",
            message: error instanceof Error ? error.message : "ページを読み込めませんでした。",
          }),
        );
    };
    const characterSelected = (event: Event) => {
      const characterId = (event as CustomEvent<{ characterId?: string }>).detail?.characterId;
      const character = model.studio.characters.find((item) => item.id === characterId);
      if (!character) {
        return;
      }
      const panel = selectedPanel(model);
      if (!panel) {
        setCharacterDraft({
          source: character,
          name: character.name,
          profile: structuredClone(character.profile),
        });
        return;
      }
      if (panel.content.characters.some((item) => item.character_id === character.id)) {
        dispatch({ type: "message", message: `${character.name} はすでにこのコマにいます。` });
        return;
      }
      const changed = {
        ...panel,
        content: {
          ...panel.content,
          characters: [
            ...panel.content.characters,
            {
              character_id: character.id,
              outfit_id: character.profile.default_outfit_id,
              expression: "",
              action: "",
              pose: "",
              position: "",
              relationship_note: "",
            },
          ],
        },
      };
      panelSaveQueue.queue(changed);
    };
    const characterEditRequested = (event: Event) => {
      const characterId = (event as CustomEvent<{ characterId?: string }>).detail?.characterId;
      const character = model.studio.characters.find((item) => item.id === characterId);
      if (character) {
        setCharacterDraft({
          source: character,
          name: character.name,
          profile: structuredClone(character.profile),
        });
      }
    };
    const sceneSelected = (event: Event) => {
      const sceneId = (event as CustomEvent<{ sceneId?: string }>).detail?.sceneId;
      const scene = model.studio.project_data?.scene_library.find((item) => item.id === sceneId);
      if (!scene) {
        return;
      }
      const panel = selectedPanel(model);
      if (!panel) {
        setSceneDraft({
          source: scene,
          name: scene.name,
          description: scene.description,
          time: scene.time,
          light: scene.light,
          weather: scene.weather,
          palette: scene.palette,
          fixedObjects: scene.fixed_objects,
          characterPositions: scene.character_positions,
          referenceAssetIds: [...scene.reference_asset_ids],
        });
        return;
      }
      const changed = {
        ...panel,
        content: { ...panel.content, scene_id: scene.id, time: scene.time },
      };
      panelSaveQueue.queue(changed);
    };
    const sceneEditRequested = (event: Event) => {
      const sceneId = (event as CustomEvent<{ sceneId?: string }>).detail?.sceneId;
      const scene = model.studio.project_data?.scene_library.find((item) => item.id === sceneId);
      if (scene) {
        setSceneDraft({
          source: scene,
          name: scene.name,
          description: scene.description,
          time: scene.time,
          light: scene.light,
          weather: scene.weather,
          palette: scene.palette,
          fixedObjects: scene.fixed_objects,
          characterPositions: scene.character_positions,
          referenceAssetIds: [...scene.reference_asset_ids],
        });
      }
    };
    const assetSelected = (event: Event) => {
      const assetId = (event as CustomEvent<{ assetId?: string }>).detail?.assetId;
      const panel = selectedPanel(model);
      if (!assetId || !panel) {
        dispatch({ type: "message", message: "素材を使うコマを先に選択してください。" });
        return;
      }
      void selectPanelAsset(model.studio, panel.id, assetId)
        .then((saved) => dispatch({ type: "panel-image", panel: saved }))
        .catch((error: unknown) =>
          dispatch({
            type: "message",
            message: error instanceof Error ? error.message : "素材をコマへ設定できませんでした。",
          }),
        );
    };
    const panelSelected = (event: Event) => {
      const panelId = (event as CustomEvent<{ panelId?: string }>).detail?.panelId;
      if (panelId && model.page.panels.some((panel) => panel.id === panelId)) {
        dispatch({ type: "select", selection: { kind: "panel", id: panelId } });
      }
    };
    const analysisJobRequested = (event: Event) => {
      const jobId = (event as CustomEvent<{ jobId?: string }>).detail?.jobId;
      if (!jobId) {
        return;
      }
      void waitForTrackedJob(model.studio, jobId)
        .then(async (job) => {
          const result = panelAnalysisFromOutput(job.output);
          const pageId = job.resource.page_id;
          const panelId = job.resource.panel_id;
          const assetId = job.resource.asset_id;
          if (
            job.job_type !== "comic_panel_analyze"
            || !result
            || !pageId
            || !panelId
            || !assetId
          ) {
            throw new ComicApiError("保存済みのAIチェック結果を開けませんでした。");
          }
          let page = model.page;
          if (page.id !== pageId) {
            await flushAllSaves();
            page = await loadComicPage(model.studio, pageId);
            dispatch({
              type: "replace-studio",
              studio: { ...model.studio, active_page: page },
            });
          }
          if (!page.panels.some((candidate) => candidate.id === panelId)) {
            throw new ComicApiError("AIチェック対象のコマが見つかりません。");
          }
          dispatch({ type: "select", selection: { kind: "panel", id: panelId } });
          setReopenedAnalysis({ jobId, panelId, assetId, result });
        })
        .catch((error: unknown) => {
          dispatch({
            type: "message",
            message: error instanceof Error ? error.message : "AIチェック結果を開けませんでした。",
          });
        });
    };
    window.addEventListener("comic:editor-action", editorAction);
    window.addEventListener("comic:panel-updated", panelUpdated);
    window.addEventListener("comic:page-load-requested", pageRequested);
    window.addEventListener("comic:character-selected", characterSelected);
    window.addEventListener("comic:character-edit-requested", characterEditRequested);
    window.addEventListener("comic:scene-selected", sceneSelected);
    window.addEventListener("comic:scene-edit-requested", sceneEditRequested);
    const assetUploaded = (event: Event) => {
      const asset = (event as CustomEvent<{asset?:Asset}>).detail?.asset;
      if (asset) {
        setEntityError("");
        setAssetDraft(asset);
        window.dispatchEvent(new CustomEvent("comic:asset-changed"));
      }
    };
    window.addEventListener("comic:asset-uploaded", assetUploaded);
    window.addEventListener("comic:asset-selected", assetSelected);
    window.addEventListener("comic:panel-select-requested", panelSelected);
    window.addEventListener("comic:analysis-job-requested", analysisJobRequested);
    return () => {
      window.removeEventListener("comic:editor-action", editorAction);
      window.removeEventListener("comic:panel-updated", panelUpdated);
      window.removeEventListener("comic:page-load-requested", pageRequested);
      window.removeEventListener("comic:character-selected", characterSelected);
      window.removeEventListener("comic:character-edit-requested", characterEditRequested);
      window.removeEventListener("comic:scene-selected", sceneSelected);
      window.removeEventListener("comic:scene-edit-requested", sceneEditRequested);
      window.removeEventListener("comic:asset-selected", assetSelected);
      window.removeEventListener("comic:asset-uploaded", assetUploaded);
      window.removeEventListener("comic:panel-select-requested", panelSelected);
      window.removeEventListener("comic:analysis-job-requested", analysisJobRequested);
    };
  }, [commit, model, batchCharacterBusy]);

  useEffect(() => {
    const keyHandler = (event: KeyboardEvent) => {
      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        dispatch({ type: event.shiftKey ? "redo" : "undo" });
      }
    };
    window.addEventListener("keydown", keyHandler);
    return () => window.removeEventListener("keydown", keyHandler);
  }, []);

  useEffect(() => {
    const jobId = new URL(window.location.href).searchParams.get("storyboard_job_id");
    if (!jobId) {
      return;
    }
    const controller = new AbortController();
    void waitForTrackedJob(model.studio, jobId, controller.signal)
      .then((job) => {
        if (job.production_run_id) {
          void loadProduction(model.studio, job.production_run_id).then(syncProduction);
          return;
        }
        const review = storyboardReviewFromJob(job);
        if (!review) {
          throw new ComicApiError("下書き生成結果に編集可能なデータがありません。");
        }
        setStoryboardError("");
        setStoryboardReview({ jobId, ...review });
        dispatch({ type: "message", message: "" });
      })
      .catch((error: unknown) =>
        error instanceof ComicApiError && error.code === "job_poll_aborted"
          ? undefined
          : dispatch({
              type: "message",
              message: error instanceof Error ? error.message : "下書きを読み込めませんでした。",
            }),
      );
    return () => controller.abort();
  }, [model.studio.active_project?.id]);

  const closeStoryboard = () => {
    void flushStoryboardDraft().then(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete("storyboard_job_id");
      window.history.replaceState({}, "", url);
      setStoryboardError("");
      storyboardReviewRef.current = null;
      setStoryboardReview(null);
      returnToEditorSource();
    }).catch((reason) => setStoryboardError(reason instanceof Error ? reason.message : "下書きを保存できませんでした。"));
  };

  const continueStoryboard = async () => {
    const current = storyboardReview;
    const currentMetadata = current?.metadata;
    if (storyboardBusy || !current || !currentMetadata?.partial) {
      return;
    }
    setStoryboardError("");
    setStoryboardAction("continue");
    try {
      const nextJobId = await retryJob(model.studio, current.jobId);
      const completedJob = await waitForTrackedJob(model.studio, nextJobId);
      const review = storyboardReviewFromJob(completedJob);
      if (!review) {
        throw new ComicApiError("続きの下書き生成結果に編集可能なデータがありません。");
      }
      const completedPages = currentMetadata.completed_pages;
      const mergedValue: StoryboardResult = {
        ...review.value,
        pages: review.value.pages.map((page, index) =>
          index < completedPages ? current.value.pages[index] ?? page : page,
        ),
      };
      const url = new URL(window.location.href);
      url.searchParams.set("storyboard_job_id", completedJob.id);
      window.history.replaceState({}, "", url);
      setStoryboardError("");
      setStoryboardReview({ jobId: completedJob.id, value: mergedValue, metadata: review.metadata });
      dispatch({ type: "message", message: "下書きの続きが完成しました。内容を確認してください。" });
    } catch (error) {
      setStoryboardError(error instanceof Error ? error.message : "下書きの続きを生成できませんでした。");
      dispatch({
        type: "message",
        message: error instanceof Error ? error.message : "下書きの続きを生成できませんでした。",
      });
    } finally {
      setStoryboardAction(null);
    }
  };

  const changeLayout = async (layoutKey: string) => {
    try {
      await flushAllSaves();
      const page = await changePageLayout(model.studio, model.page.id, layoutKey);
      dispatch({
        type: "replace-studio",
        studio: { ...model.studio, active_page: page },
      });
    } catch (error) {
      dispatch({
        type: "message",
        message: error instanceof Error ? error.message : "レイアウトを変更できませんでした。",
      });
    }
  };

  const addPanel = async () => {
    try {
      await flushAllSaves();
      const panel = await createPanel(model.studio, model.page.id);
      const page = await loadComicPage(model.studio, model.page.id);
      dispatch({
        type: "replace-studio",
        studio: { ...model.studio, active_page: page },
      });
      dispatch({ type: "select", selection: { kind: "panel", id: panel.id } });
      dispatch({ type: "message", message: "未配置のコマを追加しました。" });
    } catch (error) {
      dispatch({
        type: "message",
        message: error instanceof Error ? error.message : "コマを追加できませんでした。",
      });
    }
  };

  const removePanel = async (panelId: string) => {
    try {
      await panelSaveQueue.flush(panelId);
      await flushAllSaves();
      await deletePanel(model.studio, panelId);
      const page = await loadComicPage(model.studio, model.page.id);
      dispatch({
        type: "replace-studio",
        studio: { ...model.studio, active_page: page },
      });
      dispatch({ type: "message", message: "コマを削除しました。素材は履歴に残っています。" });
    } catch (error) {
      dispatch({
        type: "message",
        message: error instanceof Error ? error.message : "コマを削除できませんでした。",
      });
    }
  };

  const saveCharacterDraft = async () => {
    if (!characterDraft) {
      return;
    }
    setEntityError("");
    setCharacterGenerationPhase("idle");
    setEntityBusy(true);
    try {
      await flushAllSaves();
      let savedCharacter: Character;
      if (characterDraft.source) {
        savedCharacter = await updateCharacter(model.studio, {
          ...characterDraft.source,
          name: characterDraft.name,
          profile: characterDraft.profile,
        });
      } else {
        savedCharacter = await createCharacter(
          model.studio,
          characterDraft.name,
          characterDraft.profile,
        );
      }
      if (characterDraft.profile.primary_reference_asset_id) {
        await setCharacterReference(
          model.studio,
          savedCharacter.id,
          characterDraft.profile.primary_reference_asset_id,
        );
      }
      await reloadStudio("characters");
    } catch (error) {
      const message = error instanceof Error ? error.message : "キャラクターを保存できませんでした。";
      setEntityError(message);
      dispatch({
        type: "message",
        message,
      });
      setEntityBusy(false);
    }
  };

  const removeCharacterDraft = async () => {
    if (!characterDraft?.source) {
      return;
    }
    if (!window.confirm(`キャラクター「${characterDraft.name}」を削除しますか？コマ内の人物指定は解除され、セリフ本文と素材は残ります。`)) {
      return;
    }
    setEntityError("");
    setCharacterGenerationPhase("idle");
    setEntityBusy(true);
    try {
      await flushAllSaves();
      await archiveCharacter(model.studio, characterDraft.source.id);
      await reloadStudio("characters");
    } catch (error) {
      const message = error instanceof Error ? error.message : "キャラクターを削除できませんでした。";
      setEntityError(message);
      dispatch({
        type: "message",
        message,
      });
      setEntityBusy(false);
    }
  };

  const startCharacterGeneration = async (sourceAssetIds: string[]) => {
    if (!characterDraft?.source) {
      return;
    }
    setEntityError("");
    setCharacterGenerationPhase("saving");
    setEntityBusy(true);
    let generationCompleted = false;
    try {
      const saved = await updateCharacter(model.studio, {
        ...characterDraft.source,
        name: characterDraft.name,
        profile: characterDraft.profile,
      });
      const jobId = await generateCharacterSheet(
        model.studio,
        saved.id,
        sourceAssetIds,
      );
      setCharacterGenerationPhase("generating");
      await waitForTrackedJob(model.studio, jobId);
      generationCompleted = true;
      const studio = await loadBootstrap(
        model.studio.urls.bootstrap,
        model.studio.active_project?.id,
      );
      const refreshed = studio.characters.find((item) => item.id === saved.id) ?? saved;
      const assetPage = await loadAssets(studio, {}, 0, 100);
      setCharacterAssets(assetPage.assets);
      dispatch({
        type: "refresh-studio",
        studio: { ...studio, active_page: model.page },
      });
      setCharacterDraft({
        source: refreshed,
        name: refreshed.name,
        profile: structuredClone(refreshed.profile),
      });
      setCharacterGenerationPhase("success");
      dispatch({
        type: "message",
        message: "キャラクター設定画が完成しました。確認後にメイン参考を選んでください。",
      });
    } catch (error) {
      const message = generationCompleted
        ? "画像は完成しましたが、一覧を更新できませんでした。「4 素材」で確認してください。"
        : error instanceof Error
          ? error.message
          : "キャラクター設定画を生成できませんでした。";
      setCharacterGenerationPhase(generationCompleted ? "success" : "error");
      setEntityError(message);
      dispatch({
        type: "message",
        message,
      });
    } finally {
      setEntityBusy(false);
    }
  };

  const setGeneratedCharacterPrimary = async (assetId: string) => {
    if (!characterDraft?.source) {
      return;
    }
    setEntityError("");
    setEntityBusy(true);
    try {
      const saved = await setCharacterReference(
        model.studio,
        characterDraft.source.id,
        assetId,
      );
      setCharacterDraft({
        source: saved,
        name: saved.name,
        profile: structuredClone(saved.profile),
      });
      dispatch({ type: "message", message: "メイン参考画像を更新しました。" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "メイン参考画像を設定できませんでした。";
      setEntityError(message);
      dispatch({
        type: "message",
        message,
      });
    } finally {
      setEntityBusy(false);
    }
  };

  const processBatchCharacters = async (
    generateSheets: boolean,
    onlyIndex: number | null = null,
  ) => {
    if (!batchCharacterRows || batchCharacterBusy) {
      return;
    }
    setBatchCharacterBusy(true);
    await processBatchCharacterRows(batchCharacterRows, {
      generateSheets,
      onlyIndex,
      createCharacter: async (row) => {
          const profile = emptyCharacterProfile();
          profile.appearance.description = row.appearance.trim();
          const saved = await createCharacter(
            model.studio,
            row.name.trim(),
            profile,
          );
        return saved.id;
      },
      generateCharacter: async (characterId) => {
        const jobId = await generateCharacterSheet(model.studio, characterId, []);
        await waitForTrackedJob(model.studio, jobId);
      },
      onUpdate: setBatchCharacterRows,
    });
    try {
      const studio = await loadBootstrap(
        model.studio.urls.bootstrap,
        model.studio.active_project?.id,
      );
      dispatch({
        type: "refresh-studio",
        studio: { ...studio, active_page: model.page },
      });
    } catch {
      dispatch({
        type: "message",
        message: "キャラクターは保存されましたが、一覧を更新できませんでした。",
      });
    } finally {
      setBatchCharacterBusy(false);
    }
  };

  const saveSceneDraft = async () => {
    if (!sceneDraft || !model.studio.project_data) {
      return;
    }
    setEntityError("");
    setEntityBusy(true);
    const next = structuredClone(model.studio.project_data);
    const scene: Scene = {
      id: sceneDraft.source?.id ?? crypto.randomUUID(),
      name: sceneDraft.name,
      description: sceneDraft.description,
      time: sceneDraft.time,
      light: sceneDraft.light,
      weather: sceneDraft.weather,
      palette: sceneDraft.palette,
      fixed_objects: sceneDraft.fixedObjects,
      character_positions: sceneDraft.characterPositions,
      reference_asset_ids: [...sceneDraft.referenceAssetIds],
    };
    const index = next.scene_library.findIndex((item) => item.id === scene.id);
    if (index >= 0) {
      next.scene_library[index] = scene;
    } else {
      next.scene_library.push(scene);
    }
    try {
      await flushAllSaves();
      await updateProjectData(model.studio, next);
      await reloadStudio("scenes");
    } catch (error) {
      const message = error instanceof Error ? error.message : "シーンを保存できませんでした。";
      setEntityError(message);
      dispatch({
        type: "message",
        message,
      });
      setEntityBusy(false);
    }
  };

  const removeSceneDraft = async () => {
    if (!sceneDraft?.source) {
      return;
    }
    if (!window.confirm(`シーン「${sceneDraft.name}」を削除しますか？コマ内のシーン指定は解除され、素材は残ります。`)) {
      return;
    }
    setEntityError("");
    setEntityBusy(true);
    try {
      await flushAllSaves();
      await deleteScene(model.studio, sceneDraft.source.id);
      await reloadStudio("scenes");
    } catch (error) {
      const message = error instanceof Error ? error.message : "シーンを削除できませんでした。";
      setEntityError(message);
      dispatch({
        type: "message",
        message,
      });
      setEntityBusy(false);
    }
  };

  const saveProjectSettings = async () => {
    if (!projectSettingsDraft) {
      return;
    }
    setEntityError("");
    setEntityBusy(true);
    try {
      await flushAllSaves();
      await updateProjectData(
        model.studio,
        projectSettingsDraft.projectData,
        projectSettingsDraft.title.trim(),
      );
      await reloadStudio();
    } catch (error) {
      const message = error instanceof Error ? error.message : "作品設定を保存できませんでした。";
      setEntityError(message);
      dispatch({
        type: "message",
        message,
      });
      setEntityBusy(false);
    }
  };

  const startProjectExport = async (format: "zip" | "pdf", scale: 1 | 2) => {
    setEntityError("");
    setEntityBusy(true);
    let accepted = false;
    try {
      await flushAllSaves();
      const jobId = await exportProject(model.studio, format, scale, {[modelRef.current.page.id]:modelRef.current.page.revision});
      accepted = true;
      setProjectExportOpen(false);
      const completed = await waitForTrackedJob(model.studio, jobId);
      const exportIds = completed.output.asset_ids;
      let exportedAsset: Asset | null = null;
      if (Array.isArray(exportIds) && typeof exportIds[0] === "string") {
        exportedAsset = await refreshAsset(model.studio, exportIds[0], "view");
        setAssetDraft(exportedAsset);
      }
      const studio = await loadBootstrap(
        model.studio.urls.bootstrap,
        model.studio.active_project?.id,
      );
      dispatch({ type: "refresh-studio", studio: { ...studio, active_page: model.page } });
      dispatch({
        type: "message",
        message: exportedAsset
          ? `${exportedAsset.filename} の書き出しが完了しました。ダウンロード画面を開きました。`
          : `${format.toUpperCase()} が完成しました。素材ライブラリからダウンロードできます。`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "作品の書き出しを開始できませんでした。";
      if (!accepted) {
        setEntityError(message);
      }
      dispatch({
        type: error instanceof ComicApiError && error.code === "revision_conflict" ? "save-failure" : "message",
        conflict: error instanceof ComicApiError && error.code === "revision_conflict",
        message,
      });
    } finally {
      setEntityBusy(false);
    }
  };

  const startStoryboardGeneration = async () => {
    if (!storyboardGenerationDraft || !model.studio.project_data) {
      return;
    }
    setEntityError("");
    setEntityBusy(true);
    let accepted = false;
    try {
      await flushAllSaves();
      const nextProjectData = structuredClone(model.studio.project_data);
      nextProjectData.story_source = mergeStoryboardGenerationSource(
        nextProjectData.story_source,
        storyboardGenerationDraft,
      );
      await updateProjectData(model.studio, nextProjectData);
      const jobId = await createStoryboard(model.studio, {
        storyText: storyboardGenerationDraft.storyText,
        polishDialogue: storyboardGenerationDraft.polishDialogue,
        sourceType: storyboardGenerationDraft.sourceType,
        expectedPages: storyboardGenerationDraft.expectedPages,
        colorMode: nextProjectData.color_mode,
        readingDirection: nextProjectData.reading_direction,
        preferredLayoutKey: storyboardGenerationDraft.layoutKey,
      });
      accepted = true;
      setStoryboardGenerationDraft(null);
      const job = await waitForTrackedJob(model.studio, jobId);
      const review = storyboardReviewFromJob(job);
      if (!review) {
        throw new ComicApiError("下書き生成結果に編集可能なデータがありません。");
      }
      setStoryboardError("");
      setStoryboardReview({ jobId, ...review });
      dispatch({ type: "message", message: "" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "下書きを生成できませんでした。";
      if (!accepted) {
        setEntityError(message);
      }
      dispatch({
        type: "message",
        message,
      });
    } finally {
      setEntityBusy(false);
    }
  };

  const bindAssetToCharacter = async (characterId: string) => {
    if (!assetDraft) return;
    const character = model.studio.characters.find((item) => item.id === characterId);
    if (!character) return;
    setEntityError("");
    setEntityBusy(true);
    try {
      await setCharacterReference(model.studio, character.id, assetDraft.id);
      const studio = await loadBootstrap(model.studio.urls.bootstrap, model.studio.active_project?.id);
      dispatch({type:"refresh-studio", studio});
      setAssetDraft(null);
      setEntityBusy(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "キャラクター参考画像を設定できませんでした。";
      setEntityError(message);
      dispatch({ type: "message", message });
      setEntityBusy(false);
    }
  };

  const bindAssetToOutfit = async (characterId: string, outfitId: string) => {
    if (!assetDraft) return;
    const character = model.studio.characters.find((item) => item.id === characterId);
    if (!character) return;
    setEntityError("");
    setEntityBusy(true);
    try {
      await updateCharacter(model.studio, {
        ...character,
        profile: {
          ...character.profile,
          outfits: character.profile.outfits.map((outfit) =>
            outfit.id === outfitId
              ? { ...outfit, reference_asset_ids: [...new Set([...outfit.reference_asset_ids, assetDraft.id])] }
              : outfit
          ),
        },
      });
      const studio = await loadBootstrap(model.studio.urls.bootstrap, model.studio.active_project?.id);
      dispatch({type:"refresh-studio", studio});
      setAssetDraft(null);
      setEntityBusy(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "衣装参考図を設定できませんでした。";
      setEntityError(message);
      dispatch({ type: "message", message });
      setEntityBusy(false);
    }
  };

  const bindAssetToScene = async (sceneId: string) => {
    if (!assetDraft || !model.studio.project_data) return;
    setEntityError("");
    setEntityBusy(true);
    const next = structuredClone(model.studio.project_data);
    next.scene_library = next.scene_library.map((scene) =>
      scene.id === sceneId
        ? { ...scene, reference_asset_ids: [...new Set([...scene.reference_asset_ids, assetDraft.id])] }
        : scene
    );
    try {
      await updateProjectData(model.studio, next);
      const studio = await loadBootstrap(model.studio.urls.bootstrap, model.studio.active_project?.id);
      dispatch({type:"refresh-studio", studio});
      setAssetDraft(null);
      setEntityBusy(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "シーン参考画像を設定できませんでした。";
      setEntityError(message);
      dispatch({ type: "message", message });
      setEntityBusy(false);
    }
  };

  const resultsRoot = document.getElementById("comic-workspace-results") ?? document.body;
  const operationFeedbackRoot = document.getElementById("comic-operation-feedback");
  const saveFeedbackRoot = document.getElementById("comic-save-feedback");
  const operationFeedback = (
    <>
      <GenerationStatus jobs={activeJobs} now={jobClock} />
      {model.message && (
        <p className="comic-editor-message comic-operation-message" role="status">
          {model.message}
        </p>
      )}
    </>
  );
  const saveFeedback = (model.saveStatus === "error" || model.saveStatus === "conflict") ? (
    <div role="alert" className="comic-editor-message">
      <p>
        {model.saveStatus === "conflict"
          ? "別の画面で更新されています。未保存の変更をバックアップしてから、サーバー版を開いてください。"
          : "変更を保存できませんでした。この画面を閉じずに「保存を再試行」を押してください。"}
      </p>
      <button type="button" onClick={() => { const link=document.createElement("a"); link.href=URL.createObjectURL(new Blob([JSON.stringify({page_id:model.page.id,editor_state:model.state,panels:model.page.panels},null,2)],{type:"application/json"})); link.download="manga-unsaved.json"; link.click(); window.setTimeout(()=>URL.revokeObjectURL(link.href),30000); }}>未保存内容をバックアップ</button>
      {model.saveStatus === "error" && <button type="button" onClick={() => void flushAllSaves().catch((error:Error)=>dispatch({type:"message",message:error.message}))}>保存を再試行</button>}
      {model.saveStatus === "conflict" && <button type="button" onClick={() => {
        if (!window.confirm("未保存内容をバックアップしましたか？サーバー版に置き換えると現在の未保存変更を破棄します。")) return;
        void loadComicPage(model.studio,model.page.id).then((page)=>dispatch({type:"replace-studio",studio:{...model.studio,active_page:page}})).catch((error:Error)=>dispatch({type:"message",message:error.message}));
      }}>バックアップ後にサーバー版を開く</button>}
    </div>
  ) : null;

  return (
    <>
      {document.getElementById("comic-production-root") && createPortal(
        <ProductionPanel studio={model.studio} pageId={model.page.id} flushSaves={flushAllSaves}
          onUpdate={syncProduction} onReview={(run) => void reviewProduction(run)}
          onOpenPage={(pageId) => window.dispatchEvent(new CustomEvent("comic:page-load-requested", { detail: { pageId } }))}
          onOpenPanel={openProductionPanel} />,
        document.getElementById("comic-production-root")!,
      )}
      {operationFeedbackRoot
        ? createPortal(operationFeedback, operationFeedbackRoot)
        : operationFeedback}
      {saveFeedbackRoot
        ? createPortal(saveFeedback, saveFeedbackRoot)
        : saveFeedback}
      <PageCanvas
        model={model}
        dispatch={dispatch}
        commit={commit}
      />
      <div className="comic-canvas-floating-tools">
        <button
          type="button"
          className="comic-button comic-button--quiet"
          onClick={() => window.dispatchEvent(new CustomEvent("comic:editor-action", { detail: { action: "add-bubble" } }))}
        >
          ＋ 吹き出し
        </button>
      </div>
      {inspector && createPortal(
        <Inspector
          model={model}
          dispatch={dispatch}
          commit={commit}
          reopenedAnalysis={reopenedAnalysis}
          changeLayout={changeLayout}
          removePanel={removePanel}
          queuePanelSave={panelSaveQueue.queue}
          flushPanelSaves={flushAllSaves}
        />,
        inspector,
      )}
      {unplacedPanelsTray && createPortal(
        <UnplacedPanelsTray model={model} commit={commit} addPanel={addPanel} />,
        unplacedPanelsTray,
      )}
      {assetLibrary && createPortal(
        <AssetLibrary studio={model.studio} onSelect={(asset) => {
          setEntityError("");
          setAssetDraft(asset);
        }} />,
        assetLibrary,
      )}
      {createPortal(<>
      {storyboardReview && (
        <StoryboardReview
          value={storyboardReview.value}
          metadata={storyboardReview.metadata}
          layouts={model.studio.layout_catalog}
          busy={storyboardBusy}
          action={storyboardAction}
          errorMessage={storyboardError}
          applied={storyboardReview.applied || Boolean(storyboardReview.draft?.applied_chapter_id)}
          production={Boolean(storyboardReview.production)}
          characterLabels={storyboardReview.draft?.character_labels}
          onChange={(value) => {
            setStoryboardError("");
            const updated = { ...storyboardReview, value };
            storyboardReviewRef.current = updated;
            setStoryboardReview(updated);
          }}
          onContinue={storyboardReview.metadata?.partial ? () => void continueStoryboard() : null}
          onClose={closeStoryboard}
          onApply={() => {
            setStoryboardError("");
            setStoryboardAction("apply");
            if (storyboardReview.production) {
              void flushAllSaves().then(flushStoryboardDraft).then(async () => {
                const review = storyboardReviewRef.current;
                if (!review?.production || !review.draft) return;
                const latest = await loadProduction(modelRef.current.studio, review.production.id);
                const resumed = await changeProduction(modelRef.current.studio, latest, "resume", {
                  confirmed: true, draft_revision: review.draft.revision,
                });
                await syncProduction(resumed);
                closeStoryboard();
              }).catch((reason) => setStoryboardError(reason instanceof Error ? reason.message : "制作を続けられませんでした。"))
                .finally(() => setStoryboardAction(null));
              return;
            }
            void flushAllSaves().then(() => applyStoryboard(model.studio, storyboardReview.jobId, storyboardReview.value))
              .then(async () => {
                await flushAllSaves();
                closeStoryboard();
                const studio = await loadBootstrap(model.studio.urls.bootstrap, model.studio.active_project?.id);
                dispatch({type:"replace-studio", studio});
              })
              .catch((error: unknown) => {
                setStoryboardError(error instanceof Error ? error.message : "下書きを反映できませんでした。");
                dispatch({
                  type: "message",
                  message: error instanceof Error ? error.message : "下書きを反映できませんでした。",
                });
              })
              .finally(() => setStoryboardAction(null));
          }}
        />
      )}
      {continuousPreview && (
        <ContinuousPreview
          pages={continuousPreview}
          onClose={() => {
            setContinuousPreview(null);
            returnToEditorSource();
          }}
        />
      )}
      </>, resultsRoot)}
      {createPortal(<>
      {projectExportOpen && (
        <ProjectExportDialog
          pageCount={model.studio.chapters.reduce((total, chapter) => total + chapter.pages.length, 0)}
          busy={entityBusy}
          errorMessage={entityError}
          onStart={(format, scale) => void startProjectExport(format, scale)}
          onClose={() => {
            setProjectExportOpen(false);
            setEntityError("");
          }}
        />
      )}
      {storyboardGenerationDraft && (
        <StoryboardGenerationDialog
          draft={storyboardGenerationDraft}
          layouts={model.studio.layout_catalog}
          outputLanguage={model.studio.project_data?.output_language ?? "ja"}
          operationCredits={model.studio.operation_credits}
          busy={entityBusy}
          errorMessage={entityError}
          onChange={setStoryboardGenerationDraft}
          onStart={() => void startStoryboardGeneration()}
          onClose={() => {
            setStoryboardGenerationDraft(null);
            setEntityError("");
          }}
        />
      )}
      {projectSettingsDraft && (
        <ProjectSettingsDialog
          draft={projectSettingsDraft}
          busy={entityBusy}
          errorMessage={entityError}
          onChange={setProjectSettingsDraft}
          onSave={() => void saveProjectSettings()}
          onClose={() => {
            setProjectSettingsDraft(null);
            setEntityError("");
          }}
        />
      )}
      {characterDraft && (
        <CharacterDialog
          draft={characterDraft}
          assets={characterAssets}
          operationCredits={model.studio.operation_credits}
          busy={entityBusy}
          generationPhase={characterGenerationPhase}
          errorMessage={entityError}
          onChange={setCharacterDraft}
          onSave={() => void saveCharacterDraft()}
          onGenerate={(sourceAssetIds) => void startCharacterGeneration(sourceAssetIds)}
          onSetPrimary={(assetId) => void setGeneratedCharacterPrimary(assetId)}
          onDelete={() => void removeCharacterDraft()}
          onClose={() => {
            setCharacterDraft(null);
            setCharacterGenerationPhase("idle");
            setEntityError("");
          }}
        />
      )}
      {batchCharacterOpen && batchCharacterRows && (
        <BatchCharacterDialog
          rows={batchCharacterRows}
          characterSheetCredits={
            model.studio.operation_credits.character_sheet_generate
          }
          busy={batchCharacterBusy}
          onChange={setBatchCharacterRows}
          onSubmit={(generateSheets) =>
            void processBatchCharacters(generateSheets)
          }
          onRetry={(index) =>
            void processBatchCharacters(
              batchCharacterRows[index]?.generateRequested ?? false,
              index,
            )
          }
          onClose={() => setBatchCharacterOpen(false)}
        />
      )}
      {sceneDraft && (
        <SceneDialog
          draft={sceneDraft}
          studio={model.studio}
          busy={entityBusy}
          errorMessage={entityError}
          onChange={setSceneDraft}
          onSave={() => void saveSceneDraft()}
          onDelete={() => void removeSceneDraft()}
          onClose={() => {
            setSceneDraft(null);
            setEntityError("");
          }}
        />
      )}
      {assetDraft && (
        <AssetActionDialog
          asset={assetDraft}
          model={model}
          busy={entityBusy}
          errorMessage={entityError}
          onUsePanel={() => {
            const panel = selectedPanel(model);
            if (!panel) return;
            setEntityError("");
            setEntityBusy(true);
            void selectPanelAsset(model.studio, panel.id, assetDraft.id)
              .then((saved) => {
                dispatch({ type: "panel-image", panel: saved });
                setAssetDraft(null);
                window.dispatchEvent(new CustomEvent("comic:asset-changed"));
                setEntityBusy(false);
              })
              .catch((error: unknown) => {
                dispatch({ type: "message", message: error instanceof Error ? error.message : "素材をコマへ設定できませんでした。" });
                setEntityBusy(false);
                setEntityError(error instanceof Error ? error.message : "素材をコマへ設定できませんでした。");
              });
          }}
          onUseCharacter={(characterId) => void bindAssetToCharacter(characterId)}
          onUseOutfit={(characterId, outfitId) => void bindAssetToOutfit(characterId, outfitId)}
          onUseScene={(sceneId) => void bindAssetToScene(sceneId)}
          onCreateVariant={() => {
            const panel = selectedPanel(model);
            if (!panel) return;
            setEntityError("");
            setEntityBusy(true);
            const sourceId = assetDraft.id;
            let accepted = false;
            void flushAllSaves()
              .then(async () => {
                const jobId = await generatePanel(model.studio, panel.id, "similar_version", "", 1, sourceId);
                accepted = true;
                setAssetDraft(null);
                dispatch({ type: "panel-status", panel: { ...panel, generation_status: "queued" } });
                await waitForTrackedJob(model.studio, jobId);
                const [studio, page] = await Promise.all([
                  loadBootstrap(model.studio.urls.bootstrap, model.studio.active_project?.id),
                  loadComicPage(model.studio, model.page.id),
                ]);
                dispatch({ type: "refresh-studio", studio: { ...studio, active_page: page } });
                dispatch({ type: "message", message: "似た画像の候補が完成しました。" });
              })
              .catch((error: unknown) => {
                const message = error instanceof Error ? error.message : "似た画像を作成できませんでした。";
                if (!accepted) {
                  setEntityError(message);
                }
                dispatch({ type: "message", message });
              })
              .finally(() => setEntityBusy(false));
          }}
          onToggleFavorite={() => {
            setEntityError("");
            setEntityBusy(true);
            void updateAssetFavorite(model.studio, assetDraft.id, !assetDraft.is_favorite)
              .then((updated) => {
                setAssetDraft(updated);
                window.dispatchEvent(new CustomEvent("comic:asset-changed"));
              })
              .catch((error: unknown) => {
                const message = error instanceof Error ? error.message : "お気に入り状態を更新できませんでした。";
                setEntityError(message);
                dispatch({ type: "message", message });
              })
              .finally(() => setEntityBusy(false));
          }}
          onDelete={() => {
            if (!window.confirm("この素材をライブラリから削除しますか？")) return;
            setEntityError("");
            setEntityBusy(true);
            void deleteAsset(model.studio, assetDraft.id)
              .then(() => {
                setAssetDraft(null);
                window.dispatchEvent(new CustomEvent("comic:asset-changed"));
              })
              .catch((error: unknown) => {
                const message = error instanceof Error ? error.message : "素材を削除できませんでした。";
                setEntityError(message);
                dispatch({ type: "message", message });
              })
              .finally(() => setEntityBusy(false));
          }}
          onClose={() => {
            setAssetDraft(null);
            setEntityError("");
          }}
        />
      )}
      </>, document.body)}
    </>
  );
}

const bootstrap = readBootstrap();
const rootElement = document.getElementById("comic-editor-root");
if (bootstrap?.active_page && rootElement) {
  document.getElementById("comic-inspector-root")?.replaceChildren();
  document.getElementById("comic-asset-library-root")?.replaceChildren();
  document.getElementById("comic-unplaced-panels-root")?.replaceChildren();
  createRoot(rootElement).render(<Editor bootstrap={bootstrap} />);
}
